/**
 * The English catalogue, and the type every other catalogue is checked against.
 *
 * Appendix S, Localization: "string catalogue with keys, never inline text. Urdu and English at v1.
 * Translators receive context and screenshots. Pluralisation rules per language. No string
 * concatenation to build sentences."
 *
 * So each key is a whole sentence with named `{placeholders}`, and a count-dependent sentence is a
 * {@link PluralMessage} rather than two keys chosen by an `if` in a component. Keys are grouped by
 * the screen that owns them, which is also what a translator needs to be given context.
 *
 * English text here is byte-identical to what the screens rendered before they were converted, so
 * every existing English assertion in `apps/counter` keeps passing.
 */
export const en = {
    // Shell, `DESIGN-TOKENS.md` §16.3.
    'shell.sections': 'Sections',
    'shell.branch': 'Branch',
    'shell.drawerOpen': 'Drawer open',
    'shell.drawerClosed': 'Drawer closed',
    'shell.signOut': 'Sign out',
    'shell.waiting': 'waiting',
    'shell.signedIn': 'Signed in',
    // The mobile bottom bar and its More sheet, `decisions/0031`. The bar's own landmark name,
    // because two navigations in one page with one name is a landmark nobody can tell apart.
    'shell.bar': 'Counter',
    'shell.more': 'More',
    'shell.moreClose': 'Close',
    // §5 ships both densities, so both need a name a shopkeeper recognises rather than a token
    // name. "Roomier" and "Tighter" are what the choice does, not what the design system calls it.
    'shell.density': 'Rows',
    'shell.density.comfortable': 'Roomier',
    'shell.density.compact': 'Tighter',
    /**
     * `LOC-002`'s per-user half, which had no control at all: `staff.language` was read by the
     * session context and written by nothing, so a dispenser in a Lahore shop could not put the
     * counter into Urdu however complete the catalogue got.
     *
     * Each language is named **in itself**, never translated. A person looking for Urdu is looking
     * for the word they would write, and "Urdu" in English is no use to somebody who cannot read
     * the screen they are on. Beside the density chooser, which is where this product already keeps
     * a per-user display preference.
     */
    'shell.language': 'Language',
    'shell.language.en': 'English',
    'shell.language.ur': 'اردو',
    'shell.language.shop': 'As the shop is set',
    'shell.languageQueued': 'Changing the language.',
    /**
     * Said at the point of choosing, because this is the moment somebody finds out.
     *
     * 125 of 1,933 keys have Urdu. Until this chooser existed nobody could select it, so `TY-01a`
     * was a gap only the repository knew about; now a shopkeeper can pick Urdu and get a screen that
     * is mostly English, and a product that does that without saying so reads as broken rather than
     * as unfinished.
     */
    'shell.language.partial': 'Urdu is part written. Anything not translated yet stays in English.',
    // The rail's destinations.
    'nav.queue': 'Work queue',
    'nav.customers': 'Customers',
    'nav.newOrder': 'New order',
    'nav.dispensing': 'Unfinished',
    'nav.orders': 'Orders',
    'nav.cash': 'Cash drawer',
    'nav.reports': 'Reports',
    // The rail's six sections, `decisions/0031`. Named for what a shop does rather than for how the
    // code is organised, because that is the question somebody at a counter is asking.
    'nav.group.counter': 'Counter',
    'nav.group.aftercare': 'Aftercare',
    'nav.group.stock': 'Stock and buying',
    'nav.group.catalogue': 'Catalogue',
    'nav.group.money': 'Money',
    'nav.group.shop': 'Shop',
    // Sign in.
    'signIn.signUp': 'New shop? Set one up',
    'signUp.heading': 'Set up your shop',
    'signUp.means': 'A full-feature trial, no card. Your shop is yours from the first day and nothing is locked.',
    'signUp.shopName': 'Shop name',
    'signUp.ownerName': 'Your name',
    'signUp.email': 'Email address',
    'signUp.password': 'Choose a password',
    'signUp.country': 'Country',
    'signUp.country.PK': 'Pakistan',
    'signUp.country.IN': 'India',
    'signUp.country.BD': 'Bangladesh',
    'signUp.country.LK': 'Sri Lanka',
    'signUp.country.AE': 'United Arab Emirates',
    'signUp.country.SA': 'Saudi Arabia',
    'signUp.country.GB': 'United Kingdom',
    'signUp.country.US': 'United States',
    'signUp.submit': 'Set up my shop',
    'signUp.busy': 'Setting it up',
    'signUp.haveAnAccount': 'Already have a shop? Sign in',
    'signUp.unreachable': 'We cannot reach Lensmira right now. This is not your details, try again shortly.',
    'signUp.doneHeading': 'Your shop is being set up',
    'signUp.doneMeans': 'This takes a moment. Sign in once it is ready.',
    'signUp.doneSignInAs': 'You sign in as',
    'signUp.authenticatorHeading': 'Set up your authenticator first',
    'signUp.authenticatorMeans': 'An owner signs in with a code from an authenticator app as well as a password. On the sign in screen, choose Setting up for the first time, and you will be given the code to scan. Until you do, signing in will say your password is wrong.',
    'signUp.waitlistHeading': 'We are not in your country yet',
    'signUp.waitlistMeans': 'You are on the list. Lensmira handles the tax rules, currency and receipt format of each country separately, so we open one at a time. We will write to you when yours is ready.',
    'signUp.backToSignIn': 'Go to sign in',
    'signIn.heading': 'Sign in',
    'signIn.email': 'Email address',
    'signIn.password': 'Password',
    'signIn.totp': 'Authenticator code',
    'signIn.submit': 'Sign in',
    'signIn.busy': 'Signing in',
    'signIn.totpPrompt': 'Enter the code from your authenticator app.',
    'signIn.unreachable': "We can't reach your account right now. This isn't your password, try again shortly.",
    // Finding a customer.
    'customerSearch.heading': 'Which customer?',
    'customerSearch.label': 'Name or phone number',
    'customerSearch.why.name': 'name',
    'customerSearch.why.sound': 'sounds like',
    'customerSearch.why.phone': 'phone',
    // `NewCustomer`, section 6.2 `CUS-001`, `CUS-003`, `CUS-008`. The form the counter never had:
    // until now the only way a customer row came into existence was the seed or a CSV import, so a
    // shop that signed up and did not migrate a file could not put a walk-in on file at all.
    //
    // Four things are said in these words rather than in the code.
    //
    // Why a number is required, because it is the one field a shopkeeper will argue about. Appendix K
    // ranks exact phone above every name signal and `CUS-003` makes it the first duplicate key, so a
    // record with no number is one nobody finds again. The anonymous sale is the path for a customer
    // who will not give one.
    //
    // What the already-on-file list is for. It is offered before the submit and every row is a
    // button, because detection that cannot be acted on is just a warning: the person picks the
    // existing record instead. `CUS-003`'s merge tool does not exist, so no sentence here points at
    // one.
    //
    // What "not asked" means, which is the third consent state a checkbox cannot hold. A stored no is
    // a refusal the customer gave; silence is not.
    //
    // And `CUS-008`'s warning, above the notes box rather than below it, because a warning read after
    // typing has already failed.
    'newCustomer.heading': 'Put a customer on file',
    'newCustomer.whyPhone': 'A name and one number are enough to start. The number is what finds this customer again and what a recall is sent to, so a record without one is one nobody can reach. Somebody who will not give a number can still be sold to: ring it up as a walk-in instead.',
    'newCustomer.nameLabel': 'Name, as they say it',
    'newCustomer.phoneLabel': 'Mobile number',
    'newCustomer.secondLabel': 'Another number, if they gave one',
    'newCustomer.secondKindLabel': 'What that number is',
    'newCustomer.kindMobile': 'A mobile',
    'newCustomer.kindLandline': 'A landline',
    'newCustomer.kindWhatsapp': 'A WhatsApp number',
    'newCustomer.emailLabel': 'Email',
    'newCustomer.addressLabel': 'Address',
    'newCustomer.dobLabel': 'Date of birth',
    'newCustomer.genderLabel': 'Gender, if you record it',
    'newCustomer.languageLabel': 'Language they read',
    'newCustomer.languageUnset': 'Not said',
    'newCustomer.languageEnglish': 'English',
    'newCustomer.languageUrdu': 'Urdu',
    'newCustomer.channelLabel': 'How they prefer to be contacted',
    'newCustomer.channelUnset': 'Not said',
    'newCustomer.channelSendableNote': 'This records what they prefer. The shop can send email and in-app messages today; WhatsApp and SMS arrive in a later release, and a preference recorded now is used then.',
    'newCustomer.marketingLabel': 'May the shop message them about offers',
    'newCustomer.recallLabel': 'May the shop remind them when a check-up is due',
    'newCustomer.consentUnasked': 'Not asked',
    'newCustomer.consentYes': 'Yes, they agreed',
    'newCustomer.consentNo': 'No, they declined',
    'newCustomer.consentMeaning': 'Leave these as not asked if the question did not come up. A no is recorded as the customer declining, which stops those messages; not asked records nothing either way.',
    'newCustomer.tagsLabel': 'Tags, separated by commas',
    'newCustomer.notesLabel': 'Notes',
    'newCustomer.notesWarning': 'Not for anything clinical. Prescriptions, conditions and eye health belong on the prescription, not in a note.',
    'newCustomer.nameRequired': 'A customer needs a name.',
    'newCustomer.phoneRequired': 'A mobile number is needed, for the reason above.',
    'newCustomer.dobInFuture': 'That date of birth is in the future. Check the year.',
    'newCustomer.alreadyHeading': 'Somebody like this is already on file',
    'newCustomer.alreadyHint': 'Open one of these instead of filing a second record for the same person. Two records for one customer split their prescriptions and their balance, and nothing merges them back.',
    'newCustomer.foundByPhone': 'same number',
    'newCustomer.foundByName': 'similar name',
    'newCustomer.foundBySound': 'sounds the same',
    'newCustomer.bornOn': 'born',
    'newCustomer.submit': 'Put them on file',
    'newCustomer.queueLabel': 'A new customer, {name}',
    'queue.subject.customer': 'The customer',
    'newCustomer.doneHeading': 'On file',
    'newCustomer.done': 'They are on file and can be found by name or by number. A prescription, a sale and a recall can all be attached to them now.',
    'newCustomer.racedHeading': 'Another till filed a match while this was being typed',
    'newCustomer.raced': 'Both records exist and neither was changed. Decide which one the shop carries on with, and open that one from here.',
    'newCustomer.openTheNew': 'Open the new record',
    'app.newCustomer': 'Put a new customer on file',
    // `CustomerErasure`, section 6.2 `CUS-006`, section 15.10, Appendix M `RET-01`, release gate 10.
    //
    // These sentences are the gate. `RET-01` is "computes what can and cannot go, shows the customer
    // exactly what is retained and why, and executes the permitted portion, never all-or-nothing",
    // and the showing happens in words: a shopkeeper reads them out to somebody who has asked for
    // their data to be deleted, so each one has to be true and plain at the same time.
    //
    // `erasure.blockedMeans` says what the shop has to do rather than promising it happens by itself.
    // Section 15.10 says a queued request "executes automatically once the blocking condition
    // clears", and the runner that would do that does not exist yet, so the sentence names the person
    // who finishes it instead of describing a feature that is not there.
    // `Fittings`, section 6.4 `RX-006`, `RX-007`, section 6.8 `DSP-002`, Appendix I. The measurements
    // nothing in the product recorded: the table was read in two places and written in none, so
    // `CUS-002`'s section was permanently empty and a lab order carried whatever was typed onto it.
    //
    // The labels are the words on a dispensing bench, not the column names. "Seg height" is what a
    // dispenser says and what is written on the envelope that goes to the lab.
    //
    // `fittings.means` says the two things that are not obvious: they are taken against a frame, and
    // the newest set is what the next lab order uses.
    'fittings.start': 'Take fitting measurements',
    'fittings.heading': 'Fitting measurements',
    'fittings.means': 'Taken off the customer with the frame on their face. The newest set is what goes to the lab with the next order, so take a fresh set for a different frame rather than correcting these.',
    'fittings.faceHeading': 'Off the face',
    'fittings.frameHeading': 'Off the frame',
    'fittings.noFrameYet': 'No frame chosen, which is fine for a PD. A segment height needs the frame they will wear.',
    'fittings.againstFrame': 'Measured against {frame}.',
    'fittings.pdOd': 'PD right (mm)',
    'fittings.pdOs': 'PD left (mm)',
    'fittings.nearPd': 'Near PD (mm)',
    'fittings.segOd': 'Seg height right (mm)',
    'fittings.segOs': 'Seg height left (mm)',
    'fittings.fittingHeight': 'Fitting height (mm)',
    'fittings.vertex': 'Vertex distance (mm)',
    'fittings.pantoscopic': 'Pantoscopic tilt (degrees)',
    'fittings.wrap': 'Face-form wrap (degrees)',
    'fittings.frameA': 'Frame A, the eye size (mm)',
    'fittings.frameB': 'Frame B (mm)',
    'fittings.frameDbl': 'Frame DBL, the bridge (mm)',
    'fittings.frameEd': 'Frame ED (mm)',
    'fittings.temple': 'Temple length (mm)',
    'fittings.notANumber': '{field} is not a number. Write it in millimetres, as 31.5.',
    'fittings.nothingTaken': 'Nothing has been measured yet. Fill in at least one.',
    'fittings.record': 'Record them',
    'fittings.queueLabel': 'Fitting measurements',
    'queue.subject.fitting': 'The fitting',
    // `REF-001` to `REF-006`: the eye test. No key here describes a reason for a referral, on purpose.
    'queue.subject.refraction': 'The eye test',
    'refraction.heading': 'Eye tests',
    'refraction.none': 'No eye test on file for this customer.',
    'refraction.noneHint': 'A prescription brought in from elsewhere needs no eye test here. Record one when you do the test yourself.',
    'refraction.loadFailed': 'The eye tests could not be loaded. Nothing has been lost, try again.',
    'refraction.start': 'Record an eye test',
    'refraction.formLabel': 'Record an eye test',
    'refraction.queueLabel': 'Eye test',
    'refraction.record': 'Record the eye test',
    'refraction.cancel': 'Cancel',
    'refraction.section.who': 'Who did the test',
    'refraction.section.acuity': 'Visual acuity',
    'refraction.section.readings': 'Readings',
    'refraction.section.near': 'Near addition and dominant eye',
    'refraction.section.outcome': 'Outcome',
    'refraction.tester.inHouseDoctor': 'Doctor or optometrist on staff',
    'refraction.tester.inHouseRefractionist': 'Refractionist on staff',
    'refraction.tester.visitingOptometrist': 'Visiting optometrist',
    'refraction.tester.externalPrescriber': 'Prescriber elsewhere',
    'refraction.tester.customerSuppliedUnknown': 'Unknown, brought in by the customer',
    'refraction.testerHelp.inHouseDoctor': 'You, with a registration number.',
    'refraction.testerHelp.inHouseRefractionist': 'You. No registration needed.',
    'refraction.testerHelp.visitingOptometrist': 'Name and registration number.',
    'refraction.testerHelp.externalPrescriber': 'Name and registration, if legible.',
    'refraction.testerHelp.customerSuppliedUnknown': 'Nobody to name. The record is flagged.',
    'refraction.recordedAsYou': 'The test is recorded under your name.',
    'refraction.unknownFlag': 'This is flagged as having no identifiable prescriber.',
    'refraction.testerName': 'Name',
    'refraction.registration': 'Registration number',
    'refraction.notation': 'Acuity is written as',
    'refraction.notation.metric6': '6/6 (metric)',
    'refraction.notation.imperial20': '20/20',
    'refraction.notation.logmar': 'LogMAR',
    'refraction.notationHint': 'Every value is saved with the notation chosen here, so 6/9 is never ambiguous later.',
    'refraction.va.unaided': 'Unaided',
    'refraction.va.aided': 'With glasses',
    'refraction.va.pinhole': 'Pinhole',
    'refraction.set.objective': 'Auto-refractor',
    'refraction.set.subjective': 'Subjective, final',
    'refraction.set.oldGlasses': 'Old glasses (focimeter)',
    'refraction.stepHint': 'Powers go in steps of 0.25. Leave a row empty if it was not taken.',
    'refraction.nearAdd': 'Near addition (D)',
    'refraction.workingDistance': 'Working distance (cm)',
    'refraction.dominant': 'Dominant eye',
    'refraction.outcome': 'Outcome',
    'refraction.outcome.prescriptionIssued': 'Prescription issued',
    'refraction.outcome.noChange': 'No change',
    'refraction.outcome.referredOut': 'Referred out',
    'refraction.referredTo': 'Referred to',
    'refraction.referredOn': 'Referred on',
    'refraction.noReason': 'Only who and when are kept. The reason is not recorded here.',
    'refraction.referredLine': 'Referred to {destination} on {on}',
    'refraction.nearLine': 'Near addition {add} at {distance} cm',
    'refraction.dominantLine': 'Dominant eye: {eye}',
    'refraction.producedRx': 'A prescription was issued from this test.',
    'refraction.sphereRequired': '{where}: a sphere is needed.',
    'refraction.powerInvalid': '{where}: that is not a power in steps of 0.25 within range.',
    'refraction.nearAddInvalid': 'The near addition is not a power in steps of 0.25.',
    'refraction.workingDistanceRequired': 'A near addition needs the working distance it was found at, in centimetres.',
    'refraction.workingDistanceInvalid': 'The working distance is a whole number of centimetres.',
    'fittings.recorded': 'Recorded. They go to the lab with the next order for this customer.',
    'fittings.warned': 'Recorded, and worth a second look. These are outside what is usual without being impossible:',
    // `CustomerExport`, section 6.2 `CUS-006`, `CUS-006a`, section 15.10 steps 1 and 2. The half of
    // `CUS-006` that had no screen: a shop could erase a customer and could not give them what it
    // holds.
    //
    // Two buttons, because `CUS-006a` describes two different acts. The ordinary export is filtered
    // by what the person running it may see and the file says what it left out. A subject-access
    // request is the customer's own data and is not filtered, and the sentence says the thing that
    // makes that safe: the person operating the tool is not the person receiving the file.
    'customerExport.heading': 'Give the customer their record',
    'customerExport.whatItIs': 'A single file with everything the shop holds on them: prescriptions, purchases, jobs, measurements, recalls and consents. It downloads to this device.',
    'customerExport.forShop': 'Export what I can see',
    'customerExport.forCustomer': 'The customer asked for their own data',
    'customerExport.subjectAccessMeans': 'This one is not filtered by what you can see, because it is their data and you are only operating the tool. Say whose request it is: that sentence is what the shop can show afterwards.',
    'customerExport.reasonLabel': 'Whose request, and when',
    'customerExport.reasonRequired': 'Say whose request it is. An unfiltered export is recorded against your name.',
    'customerExport.subjectAccessConfirm': 'Export everything',
    'customerExport.cancel': 'Leave it',
    'customerExport.queueLabel': 'An export for {name}',
    'queue.subject.export': 'The export',
    'customerExport.done': 'Downloaded. It holds everything the shop has on them.',
    'customerExport.partial': 'Downloaded, and it is not everything. Your role cannot see the following, so the file leaves it out and says so:',
    'customerExport.withheldSection': '{section}',
    // `CustomerPortalLink`, section 6.2 `CUS-007`. The staff half of the customer portal: the shop
    // makes a link and sends it to the customer itself, because WhatsApp and SMS are out of v1 and
    // `MSG-001a` says shop customers largely do not use email. The spec gives no lifetime, so the
    // person making the link says how long, and there is deliberately no default in the field.
    'portalLink.heading': 'Customer portal link',
    'portalLink.whatItIs': 'A private link where the customer can see their purchases and prescriptions. Anyone who has the link can open it until it stops working, so send it only to the customer.',
    'portalLink.lifetimeLabel': 'Works for how many hours',
    'portalLink.lifetimeRequired': 'Enter a whole number of hours, one or more.',
    'portalLink.make': 'Make a link',
    'portalLink.revoke': 'Cancel their links',
    'portalLink.queueLabel': 'A portal link for {name}',
    'portalLink.revokeQueueLabel': 'Cancel the portal links for {name}',
    'queue.subject.portalLink': 'The portal link',
    'portalLink.ready': 'The link is ready. It is shown once: the shop cannot show it again.',
    'portalLink.linkLabel': 'The link to send',
    'portalLink.copy': 'Copy the link',
    'portalLink.copied': 'Copied.',
    'portalLink.copyFailed': 'The browser would not copy it. Select the link and copy it by hand.',
    'portalLink.expires': 'It works until {when}.',
    'portalLink.withPrescriptions': 'It shows their prescriptions as well as their purchases.',
    'portalLink.withoutPrescriptions': 'It shows their purchases only, not their prescriptions.',
    'portalLink.revoked': {
        one: '{count} link cancelled. It no longer opens anything.',
        other: '{count} links cancelled. They no longer open anything.',
    },
    // `Portal`, the customer's own page. Reached from a link, with no sign-in. It speaks to the
    // customer, not to staff, and says nothing about why a link failed beyond that it did not work:
    // an expired link, a cancelled one and a made-up one must read the same.
    'portal.title': 'Your records',
    'portal.hello': 'Hello, {name}.',
    'portal.loading': 'Loading your records.',
    'portal.unusable': 'This link does not work. Ask the shop for a new one.',
    'portal.unavailable': "We can't reach the shop's records right now. Try again shortly.",
    'portal.tooMany': 'Too many attempts. Try again later.',
    'portal.failed': 'Something went wrong. Try again shortly.',
    'portal.orders.heading': 'What you bought',
    'portal.orders.none': 'Nothing bought yet.',
    'portal.orders.noInvoice': 'Order without an invoice number',
    'portal.orders.placed': 'Bought {date} at {branch}',
    'portal.orders.owing': 'You owe {amount}',
    'portal.orders.paid': 'Paid in full',
    'portal.orders.see': 'See the items',
    'portal.orders.back': 'Back to all purchases',
    'portal.order.item': 'Item',
    'portal.rx.heading': 'Your prescriptions',
    'portal.rx.withheld': 'This link does not show prescriptions. Ask the shop if you need a copy of yours.',
    'portal.rx.download': 'Download your prescription',
    'portal.rx.downloadFailed': 'Could not get your prescription just now. Try again shortly.',
    'portal.rx.back': 'Back to your page',
    // Appendix C.3's nine remake states. The work queue showed the raw enum value to a shopkeeper
    // (`cause_assigned`, `credit_expected`), because no screen in the product had needed a word for
    // one: `JobDetail` shows a remake's cause and never its state.
    //
    // Worded as what is being waited for, not as what has happened, because that is what a row on
    // the queue is for. "Costing the remake" rather than "cause assigned".
    'status.remake.raised': 'Raised',
    'status.remake.causeAssigned': 'Cause agreed',
    'status.remake.costed': 'Costed',
    'status.remake.creditExpected': 'Waiting for the lab credit',
    'status.remake.creditReceived': 'Lab credit received',
    'status.remake.newJobCreated': 'New job raised',
    'status.remake.inProgress': 'Being remade',
    'status.remake.resolved': 'Settled',
    'status.remake.rejected': 'Refused by the lab',
    'erasure.start': 'Erase this customer at their request',
    'erasure.heading': 'What an erasure would remove',
    'erasure.whatItIs': 'The customer has asked the shop to delete what it holds on them. Some of it the shop may delete and some of it the law requires it to keep. Read both halves before confirming.',
    'erasure.planFailed': 'The records could not be read, so nothing is offered. Nobody should confirm an erasure against a list that failed to load.',
    'erasure.goesHeading': 'This is removed',
    'erasure.staysHeading': 'This is kept, and why',
    'erasure.nothingStays': 'Nothing has to be kept. Everything the shop holds on them can go.',
    'erasure.blockedHeading': 'What is in the way',
    'erasure.blockedMeans': 'The request is recorded today either way, and the date it was made is what counts. Someone at the shop finishes it once the work is collected or the money is in.',
    'erasure.onFileSince': 'Already asked for on {date}, and still on file.',
    'erasure.confirm': 'Erase what can be erased',
    'erasure.confirmWhileBlocked': 'Record the request',
    // A panel whose only button erases somebody needs a way out. Opening it is how a shopkeeper
    // reads what would happen, and before this key the only thing to press afterwards was the
    // erasure itself.
    'erasure.cancel': 'Leave it without erasing',
    'erasure.queueLabel': 'An erasure for {name}',
    'queue.subject.erasure': 'The erasure',
    'erasure.doneHeading': 'Erased',
    'erasure.done': 'What could go has gone, and the customer can no longer be found by name or number. What the law requires is still on the record, and the audit log says what was removed and by whom.',
    'erasure.queuedHeading': 'Recorded, not finished',
    'erasure.queued': 'Nothing was removed yet, because of the following. The request is on file from today.',
    'erasure.alreadyDone': 'This customer has already been erased.',
    // `FrameChoice`, section 6.9 `INV-001`, section 6.7 `POS-001`. The chooser that a frame line had
    // no way to be: the product field held a UUID, so a frame sale named no stock and the shelf was
    // never relieved.
    //
    // The words choose a **lot**, not a model, and say what is on hand. Two lots of one frame are two
    // different costs (`MR-05`), and a dispenser picking between them needs to know there is one left
    // rather than six, because the last one in the window is the one somebody has already promised.
    //
    // `frameChoice.empty` and `frameChoice.none` are two different sentences on purpose: a shop with
    // no frames on the shelf and a search that matched nothing are not the same situation, and only
    // one of them means somebody should go and receive a delivery.
    'frameChoice.label': 'Frame off the shelf, by brand, model, colour or barcode',
    'frameChoice.loadFailed': 'The shelf could not be read, so no frame is offered. Selling one without naming it leaves the stock figure wrong for good.',
    'frameChoice.empty': 'No frames on the selling shelf at this branch. Receive a delivery, or move stock from the back store.',
    'frameChoice.none': 'Nothing on the shelf matches that.',
    'frameChoice.chosen': 'Selling {frame}, lot {lot}.',
    // `ROLE-002`'s seven roles, said the way a shopkeeper describes the person rather than the way
    // the matrix names the row. `ShopSetup` is the first screen in the product to show one.
    //
    // "Optician" and "dispenser" are two jobs in one shop and the words are not interchangeable: the
    // optician tests eyes and the dispenser fits and sells, which is why Appendix B gives them
    // different rows.
    // `ShopSetup`, section 6.1 `TEN-002a`, section 6.31 `ROLE-002`, `BR-01`, `ROLE-001a`. The screen
    // for the two things a shop does to itself routinely and had no way to do at all: hire somebody
    // and open a branch.
    //
    // Three things are said in words here rather than left to the form's shape.
    //
    // Why the password is on screen while it is typed. It is being set for somebody else and read out
    // across a counter, and a masked field produces a password nobody can repeat and a shop that
    // writes it on paper instead. The sentence tells them to have the person change it.
    //
    // Why the role chooser is short. It offers only the roles this person may assign, and saying so
    // stops a manager hunting for Owner in a list that does not have it.
    //
    // What a timezone is for, because it is the one field whose wrong value is invisible: a branch
    // with the wrong zone books its sales to the wrong day, and the shop finds out at a month end.
    // The feedback form. The notice is the only control on customer details reaching ANUBRIS, so it
    // sits before the box and not in the placeholder, which disappears when somebody starts typing.
    'feedback.title': 'Tell us what you think',
    'feedback.intro': 'Report a fault, suggest a change, ask a question or say what is working. Your message goes to the ANUBRIS team.',
    'feedback.noCustomerDetails': 'Do not include customer details. No names, phone numbers or prescriptions.',
    'feedback.categoryLabel': 'What is this about',
    'feedback.category.bug': 'Something is wrong',
    'feedback.category.idea': 'An idea',
    'feedback.category.question': 'A question',
    'feedback.category.praise': 'Something that works well',
    'feedback.messageLabel': 'Your message',
    'feedback.placeholder': 'What happened, or what would you like to see?',
    'feedback.count': '{used} of {max} characters',
    'feedback.submit': 'Send',
    'feedback.thanks': 'Thank you. Your message has been received.',
    'feedback.empty': 'Write a message before sending.',
    'feedback.tooLong': 'A message can be at most {max} characters. Shorten it and send again.',
    'feedback.queueLabel': 'Sending your feedback',
    'queue.subject.feedback': 'Your message',
    'onboarding.heading': 'Getting your shop ready',
    'onboarding.progress': '{done} of {total} done',
    'onboarding.tick': 'Mark done',
    'onboarding.done': 'Done',
    'onboarding.queueLabel': 'Marking a setup step done',
    'onboarding.item.addBranch': 'Open your branch and set its address and invoice series',
    'onboarding.item.addStaff': 'Add the people who work here and what each may do',
    'onboarding.item.addCatalogue': 'Put your frames and lenses on file',
    'onboarding.item.publishPriceBook': 'Publish a price book, so a sale can be priced',
    'onboarding.item.firstPrescription': 'Record a prescription',
    'onboarding.item.firstSale': 'Take a sale',
    'jobTicket.open': 'Job ticket for the lab',
    'jobTicket.close': 'Close the ticket',
    'jobTicket.failed': 'The ticket could not be built for this job.',
    'jobTicket.copyOrder': 'Copy the order',
    'jobTicket.copyByHand': 'This device cannot copy for you. Select the text below and copy it.',
    'jobTicket.orderText': 'The order, as text',
    'jobTicket.copied': 'Copied. Paste it into a message to your lab.',
    'jobTicket.emailLab': 'Email the lab',
    'jobTicket.print': 'Print',
    'jobTicket.barcodeLabel': 'Barcode for job {number}',
    'jobTicket.lab': 'Lab',
    'jobTicket.noLab': 'No lab chosen yet',
    'jobTicket.promised': 'Promised',
    'jobTicket.noPromisedDate': 'No date agreed',
    // `{count}` needs both forms, which the catalogue test enforces. One frame is reachable: the
    // read reports ambiguity above one, and a later change could lower that.
    'jobTicket.frameAmbiguous': {
        one: 'This order has one frame and the job does not say whether these lenses are for it. Write it in the special instructions.',
        other: 'This order has {count} frames and the job does not say which one these lenses are for. Write it in the special instructions.',
    },
    'jobTicket.frame': 'Frame',
    'jobTicket.ownFrame': "Customer's own frame, handle with care",
    'jobTicket.colour': 'Colour',
    'jobTicket.size': 'Size',
    'jobTicket.mounting': 'Mounting',
    'jobTicket.prescription': 'Prescription',
    'jobTicket.eye': 'Eye',
    'jobTicket.sphere': 'Sph',
    'jobTicket.cylinder': 'Cyl',
    'jobTicket.axis': 'Axis',
    'jobTicket.add': 'Add',
    'jobTicket.pd': 'PD',
    'jobTicket.segHeight': 'Seg ht',
    'jobTicket.lens': 'Lens',
    'jobTicket.treatments': 'Treatments',
    'jobTicket.diameter': 'Diameter',
    'jobTicket.baseCurve': 'Base curve',
    'jobTicket.specialInstructions': 'Special instructions',
    'jobTicket.notSupplied': 'Not supplied with this job',
    'jobTicket.notGiven': 'Not given',
    'shopSetup.clearTotp': 'Clear {name}s authenticator',
    'shopSetup.clearTotpQueued': 'Clearing {name}s authenticator. They set up a new one from the sign-in screen.',
    'shopSetup.queueClearTotpLabel': 'Clearing {name}s authenticator',
    'shopSetup.myTotpHeading': 'Your own authenticator',
    'shopSetup.myTotpMeans': 'An authenticator app shows a six-digit code that changes every half minute. Setting one up means your password alone is not enough to open your account. You can do this whether or not the shop asks for it.',
    'shopSetup.myTotpOn': 'You have an authenticator on this account. You are asked for a code every time you sign in.',
    'shopSetup.myTotpPassword': 'Your password, to confirm it is you',
    'shopSetup.myTotpEnrol': 'Set up an authenticator',
    'shopSetup.myTotpNeedsPassword': 'Type your password, so somebody who walked up to this screen cannot add a second factor to your account.',
    'shopSetup.myTotpSecret': 'Type this into your authenticator app',
    'shopSetup.myTotpLink': 'Or open it in the app',
    'shopSetup.myTotpOnce': 'This is shown once. Nothing can read it back, so if you lose it an owner has to clear it and you start again.',
    'shopSetup.myTotpFailed': 'That password was not accepted, so nothing was changed.',
    'shopSetup.myTotpAlready': 'This account already has an authenticator. An owner has to clear it before a new one can be set up.',
    'shopSetup.myTotpUnreachable': 'We could not reach the server, so nothing was changed.',
    'shopSetup.totpHeading': 'Sign-in security',
    'shopSetup.totpMeans': 'An authenticator app shows a six-digit code that changes every half minute. Asking for one means a stolen password is not enough on its own to open the shop. Anybody here can set one up for themselves from the sign-in screen whether or not you tick this.',
    'shopSetup.totpRequire': 'Owners and managers must use an authenticator app',
    'shopSetup.totpWarning': 'Ticking this stops an owner or manager signing in until they have set one up. Anybody who already has keeps using it either way. If somebody loses their phone, unticking this will not let them in, because an account with an authenticator is always asked for its code: use Clear authenticator on their row in the people list, and they set up a new one. So keep a second owner account, or you will have nobody who can do that for you.',
    'shopSetup.queueTotpOnLabel': 'Requiring an authenticator for owners and managers',
    'shopSetup.queueTotpOffLabel': 'No longer requiring an authenticator',
    // `OPS-207`. The question is the Net Promoter one because the spec names no instrument; a change
    // of instrument is these strings and `SATISFACTION_SCALE`, nothing else.
    'supportAccess.heading': 'Support access',
    'supportAccess.means': 'Lensmira support cannot open your shop records unless you allow it here. You choose what they may see and for how long, and you can withdraw it at any time.',
    'supportAccess.loadFailed': 'Support access could not be loaded. Try again in a moment.',
    'supportAccess.closed': 'Closed. Support cannot see anything in your shop.',
    'supportAccess.open': 'Open until {until}.',
    'supportAccess.mayRead': 'Support may see',
    'supportAccess.withdraw': 'Withdraw access now',
    'supportAccess.grantHeading': 'Open support access',
    'supportAccess.replaceHeading': 'Change support access',
    'supportAccess.replaceMeans': 'A new grant replaces the one that is open now.',
    'supportAccess.scopeLegend': 'What may support see?',
    'supportAccess.scopeNone': 'Choose at least one thing support may see.',
    'supportAccess.sensitive': 'Sensitive',
    'supportAccess.durationLabel': 'For how long, in minutes',
    'supportAccess.durationHint': {
        one: 'At most {count} minute.',
        other: 'At most {count} minutes ({hours} hours).',
    },
    'supportAccess.durationInvalid': 'Enter a whole number of minutes, 1 or more.',
    'supportAccess.durationTooLong': {
        one: 'Choose {count} minute or fewer.',
        other: 'Choose {count} minutes or fewer.',
    },
    'supportAccess.grant': 'Open access',
    'supportAccess.queueGrant': 'Opening support access',
    'supportAccess.queueWithdraw': 'Withdrawing support access',
    'supportAccess.activityHeading': 'When support looked',
    'supportAccess.activityNone': 'Support has not tried to open your shop.',
    'supportAccess.usedBy': '{name} opened support access',
    'supportAccess.refusedTo': '{name} tried and was refused',
    'supportAccess.reasonGiven': 'Reason given: {reason}',
    'supportAccess.historyHeading': 'Earlier access',
    'supportAccess.withdrawnState': 'Withdrawn',
    'supportAccess.expiredState': 'Ran out',
    'supportAccess.span': 'From {from} until {until}',
    'satisfaction.heading': 'A quick question about Lensmira',
    'satisfaction.question': 'How likely are you to recommend Lensmira to another optician?',
    'satisfaction.scaleLow': '0 is not at all likely',
    'satisfaction.scaleHigh': '10 is extremely likely',
    'satisfaction.commentLabel': 'Anything you would like us to know (optional)',
    'satisfaction.commentWarning': 'Please do not type customer names, phone numbers or prescriptions here.',
    'satisfaction.send': 'Send answer',
    'satisfaction.dismiss': 'Not now',
    'satisfaction.thanks': 'Thank you. We will ask again in a few months.',
    'satisfaction.queueLabelAnswer': 'Sending your answer to Lensmira',
    'satisfaction.queueLabelDismiss': 'Putting off the Lensmira question',
    'shopSetup.seriesLabel': 'Invoice number format',
    'shopSetup.saveSeries': 'Save the format',
    'shopSetup.seriesInvalid': 'An invoice format needs {series} and {seq}, as in {series}-{year}-{seq:6}. Without the sequence every invoice in a year reads the same.',
    'shopSetup.queueSeriesLabel': 'Saving the invoice number format',
    'shopSetup.blockHeading': 'Invoice numbers for a till',
    'shopSetup.blockMeans': 'A till draws its invoice numbers from a block reserved for it. The size is how many invoices it can write before it needs another, including while it has no network, so pick a number that covers the longest outage you expect. You choose this once per till; later blocks follow the same size.',
    'shopSetup.blockTerminalLabel': 'Till',
    'shopSetup.blockSeriesLabel': 'Series code',
    'shopSetup.blockYearLabel': 'Fiscal year',
    'shopSetup.blockSizeLabel': 'How many invoices',
    'shopSetup.allocateBlock': 'Reserve the numbers',
    'shopSetup.blockTerminalRequired': 'Say which till these numbers are for.',
    'shopSetup.blockSeriesRequired': 'Say which series these numbers belong to.',
    'shopSetup.blockYearRequired': 'Say which fiscal year this block is for, as a whole year.',
    'shopSetup.blockSizeRequired': 'Say how many invoices this block holds, as a whole number above zero.',
    'shopSetup.queueBlockLabel': 'Reserving invoice numbers for {terminal}',
    'shopSetup.heading': 'Set up the shop',
    // The list and the offboarding, which `staff.offboarded_at` had no writer for: a shop whose
    // dispenser left had an account that still worked.
    //
    // An offboarded person stays in the list with the date, because `ROLE-004`'s question after
    // anything goes wrong is who had which role and when, and a list that drops them makes a
    // shopkeeper think a sale was taken by nobody.
    //
    // `shopSetup.offboardMeans` says the three things offboarding does and the one it does not: their
    // records stay, because every sale is attributed to them.
    'shopSetup.peopleHeading': 'Who works here',
    'shopSetup.peopleLoading': 'Reading the list.',
    'shopSetup.peopleNone': 'Nobody is on file yet.',
    'shopSetup.staffLoadFailed': 'The list of people could not be read.',
    'shopSetup.thatIsYou': 'that is you',
    'shopSetup.offboardedOn': 'left on {date}',
    'shopSetup.offboard': 'Offboard {name}',
    'shopSetup.offboardMeans': '{name} will not be able to sign in, and anybody signed in as them is signed out at once. Their sales, jobs and prescriptions stay exactly as they are, because the record of who did what has to survive them leaving.',
    'shopSetup.offboardReason': 'Why, for the record',
    'shopSetup.offboardConfirm': 'Offboard them',
    'shopSetup.offboardCancel': 'Leave it',
    'shopSetup.queueOffboardLabel': 'Offboarding {name}',
    'shopSetup.offboardQueued': 'Offboarding {name}. They lose access once it has gone through.',
    'shopSetup.personHeading': 'Add somebody who works here',
    'shopSetup.personMeans': 'They can sign in as soon as this is saved, at the branches you tick and nowhere else. Nothing here can be changed from this screen afterwards, so read it back before saving.',
    'shopSetup.nameLabel': 'Name, as the shop calls them',
    'shopSetup.roleLabel': 'What they do',
    'shopSetup.roleUnset': 'Not chosen',
    'shopSetup.roleLimit': 'The list holds the roles you can give. You cannot create somebody who can do more than you can.',
    'shopSetup.loginLabel': 'The email or phone number they sign in with',
    'shopSetup.passwordLabel': 'A first password',
    'shopSetup.passwordMeans': 'On screen on purpose, because you have to read it out to them. Have them change it once they are in.',
    'shopSetup.branchesLegend': 'Where they work',
    'shopSetup.costAt': 'They may also see what stock costs at {branch}',
    'shopSetup.addPerson': 'Add them',
    'shopSetup.nameRequired': 'A name is needed.',
    'shopSetup.roleRequired': 'Choose what they do.',
    'shopSetup.loginRequired': 'An email or phone number is needed, because it is how they sign in.',
    'shopSetup.passwordRequired': 'A first password is needed. An empty one lets anybody sign in as them.',
    'shopSetup.branchRequired': 'Tick at least one branch. Somebody with none can do nothing anywhere.',
    'shopSetup.queuePersonLabel': 'A new person, {name}',
    'shopSetup.personQueued': 'Saving them. They can sign in once it has gone through.',
    'shopSetup.branchHeading': 'Open a branch',
    'shopSetup.branchMeans': 'A branch is what every sale, every shelf and every figure in a report is attached to. You will be able to work there as soon as it is saved; its first till needs an invoice number block before it can sell.',
    'shopSetup.branchNameLabel': 'What the branch is called',
    'shopSetup.branchCodeLabel': 'A short code for it, which goes on its invoice numbers',
    'shopSetup.countryLabel': 'Country, two letters, as in PK',
    'shopSetup.timezoneLabel': 'Timezone, as in Asia/Karachi',
    'shopSetup.timezoneMeans': 'This decides which day a sale belongs to. A wrong one puts the first hours of the morning on yesterday and nobody notices until a month end.',
    'shopSetup.currencyLabel': 'Currency, three letters, as in PKR',
    'shopSetup.openBranch': 'Open it',
    'shopSetup.branchNameRequired': 'A branch needs a name.',
    'shopSetup.branchCodeRequired': 'A branch needs a code, because its invoice numbers carry it.',
    'shopSetup.countryRequired': 'A country is needed.',
    'shopSetup.timezoneRequired': 'A timezone is needed, for the reason above.',
    'shopSetup.currencyRequired': 'A currency is needed.',
    'shopSetup.queueBranchLabel': 'A new branch, {name}',
    'shopSetup.branchQueued': 'Opening it. It will be in the branch list once it has gone through.',
    // `ACC-008`'s period close, which had no screen and no route: `assertPeriodOpen` has taken a
    // `closedPeriods` argument since the ledger was written and nothing ever recorded one.
    //
    // Worded as "month end", which is what a shopkeeper calls it. "Period" is the ledger's word and
    // appears only where the month itself is named.
    'nav.periodClose': 'Month end',
    'periodClose.heading': 'Month end',
    'periodClose.whatItIs': 'Closing a month locks its figures so they cannot change. Anything that arrives for a closed month afterwards is posted into the month you are in now, as an adjustment.',
    'periodClose.alreadyHeading': 'Months already closed',
    'periodClose.loadFailed': 'Could not read which months are closed, so nothing is offered. Whether a month is already closed is not something to find out by trying to close it.',
    'periodClose.closedOn': 'Closed {date}',
    'periodClose.closeHeading': 'Close a month',
    'periodClose.nothingToClose': 'Every month that has ended is already closed.',
    'periodClose.monthLabel': 'Which month',
    'periodClose.monthUnchosen': 'Not chosen',
    'periodClose.reasonLabel': 'A note, if you want one',
    'periodClose.confirmMeans': 'Closing {period} cannot be undone. Nothing in it can be changed afterwards, and a correction to it goes into the month you are in now.',
    'periodClose.confirm': 'Close {period}',
    'periodClose.cancel': 'Leave it open',
    'periodClose.start': 'Close this month',
    'periodClose.queueLabel': 'Closing {period}',
    'queue.subject.periodClose': 'The month end',
    // §15.25's corrections. `COR-001`'s "both sides show the link" is the log below the record;
    // the panel above it is the reassignment.
    //
    // Worded as "correct", not "edit" or "change": nothing here overwrites anything. The original
    // keeps its id, the correction is a linked record, and what the field said before is in it.
    'corrections.heading': 'Corrections',
    'corrections.logHeading': 'Already corrected',
    'corrections.logNone': 'This has never been corrected.',
    'corrections.logLoading': 'Reading the corrections on this record.',
    'corrections.logFailed': 'Could not read the corrections on this record. What it says now is still what it says; what it said before is what could not be read.',
    'corrections.movedFrom': 'was {previous}, now {current}',
    'corrections.movedTo': 'set to {current}',
    'corrections.correctedBy': '{who}, {date}',
    'corrections.makeHeading': 'Correct this record',
    'corrections.offersLoading': 'Checking what can be corrected.',
    'corrections.offersFailed': 'Could not check what can be corrected, so nothing is offered. Whether a month is closed or a pay period settled is not something to find out by being refused.',
    'corrections.nothingOffered': 'Nothing on this record can be corrected.',
    'corrections.whichField': 'What is wrong',
    'corrections.fieldUnchosen': 'Not chosen',
    'corrections.currently': 'It says {current} now.',
    'corrections.currentlyNobody': 'It names nobody now.',
    'corrections.withheldHeading': 'What cannot be corrected here',
    'corrections.chooseCustomer': 'The customer it should be against',
    'corrections.chooseStaff': 'Who it should be against',
    'corrections.chooseBranch': 'The branch it should be at',
    'corrections.searchCustomer': 'Search by name or phone',
    'corrections.searchShort': 'Type at least two letters, or a phone number.',
    'corrections.searchNone': 'No customer found. Check the spelling, or try the phone number.',
    'corrections.noStaff': 'Nobody else works at this branch, so there is nobody to move it to.',
    'corrections.noBranch': 'You hold no other branch to move it to.',
    'corrections.reasonLabel': 'Why',
    'corrections.reasonRequired': 'A correction needs a reason. Without one, the link this creates cannot explain itself later.',
    'corrections.noChange': 'That is what it already says, so there is nothing to correct.',
    'corrections.start': 'Correct it',
    'corrections.confirmMeans': 'This moves {record} from {previous} to {next}. The original is kept and a correction record is made alongside it, so both sides show what happened.',
    'corrections.confirmMeansEmpty': 'This sets {record} to {next}. The original is kept and a correction record is made alongside it, so both sides show what happened.',
    'corrections.confirm': 'Correct it',
    'corrections.cancel': 'Leave it as it is',
    'corrections.queueLabel': 'Correcting {record}',
    // What to call a record with no name of its own. An un-invoiced order's number arrives as the
    // literal string "(not yet invoiced)", and "This moves (not yet invoiced) from..." is not a
    // sentence anybody can read.
    'corrections.queueBusy': 'Something on this till is still saving. A correction takes the version of the record it was shown, and whatever is saving may change it, so wait until everything has gone through.',
    /*
     * What a sale with no invoice number yet is called.
     *
     * It used to be the literal string `(not yet invoiced)`, substituted in four places across the
     * server and the counter, so it was English in the Urdu interface on every order heading and in
     * every "Open order" button's accessible name, and it leaked into any sentence that
     * interpolated the number.
     *
     * The wording is an assumption, recorded as one in `later_todo.md` 51: the words are the ones
     * that were there, without the parentheses, because the parentheses were a developer marking a
     * placeholder and a label a shopkeeper reads should not carry that.
     */
    /*
     * Refusals, in the shop's own language where the catalogue has them (`LOC-001`).
     *
     * The words are the server's, because that is where the reason is known and because a refusal
     * with no entry here still has to read as something. What this adds is a place a translator can
     * reach: `apps/counter/src/refusals.ts` prefers these and falls back to the wire.
     *
     * Only the ones whose sentence needs no parameter. A refusal that names a month or a job number
     * needs those as fields rather than baked into a string, which is `later_todo.md` 48.
     */
    'refusal.saleCarriesAPrescription': 'This sale was made against a prescription, which belongs to the customer it names. Moving the sale to somebody else would leave it carrying another person’s eyes. Void it and ring it up for the right customer.',
    'refusal.saleHasAJob': 'A job has been raised on this sale and holds a frozen copy of that customer’s eyes. Moving the sale would leave the job made for somebody else.',
    'refusal.commissionPeriodClosed': 'The pay period this sale falls in has been worked out, so moving who sold it would move pay that has already been settled. Correct it through the pay run instead.',
    'refusal.cashSessionCounted': 'The till this payment was taken at has been counted, and the Z-report says who took it. Moving it now would disagree with a figure somebody has signed.',
    'refusal.correctionNoChange': 'That is what it already says, so there is nothing to correct.',
    'refusal.correctionReasonRequired': 'A correction needs a reason. Without one, the link this creates cannot explain itself later.',
    'refusal.idempotencyKeyRequired': 'That did not go out properly. Try it again, and tell somebody if it keeps happening.',
    'order.notInvoiced': 'Not invoiced yet',
    'corrections.recordSale': 'this sale',
    'corrections.recordJob': 'this job',
    'corrections.recordPayment': 'this payment',
    'queue.subject.correction': 'The correction',
    'correctionType.wrongCustomer': 'The customer',
    'correctionType.wrongStaff': 'Who it is credited to',
    'correctionType.wrongBranch': 'The branch',
    'correctionType.wrongJob': 'The job',
    'correctionType.duplicateEntry': 'Entered twice',
    // `later_todo.md` 32's remainder: changing what somebody may do, and giving them a new
    // password. Hiring and offboarding existed; everything between them needed a psql prompt.
    'shopSetup.change': 'Change what {name} can do',
    'shopSetup.changeRole': 'Their role',
    'shopSetup.changeBranches': 'Where they work',
    'shopSetup.changeCost': 'Where they can see cost price',
    'shopSetup.changeMeans': 'Changing this signs them out of every till straight away, because a signed-in terminal keeps working under the role it signed in with.',
    'shopSetup.changeConfirm': 'Save the change',
    'shopSetup.changeCancel': 'Leave it as it is',
    'shopSetup.changeNeedsBranch': 'Leave them at least one branch. Somebody with none can act nowhere, which is a locked-out account rather than somebody who has left.',
    'shopSetup.queueChangeLabel': 'Changing what {name} can do',
    'shopSetup.changeQueued': 'Saving the change for {name}. They will be signed out of every till.',
    'shopSetup.reset': 'Give {name} a new password',
    'shopSetup.newPassword': 'The new password',
    'shopSetup.resetMeans': 'This replaces their password and signs them out everywhere, including the till in front of them. Tell {name} the new one yourself, because nothing here will show it again.',
    'shopSetup.resetConfirm': 'Set the password',
    'shopSetup.resetCancel': 'Leave it alone',
    'shopSetup.newPasswordRequired': 'Type a new password.',
    'shopSetup.queueResetLabel': 'A new password for {name}',
    'shopSetup.resetQueued': 'Setting a new password for {name}. They will be signed out everywhere.',
    'queue.subject.setup': 'The change',
    'nav.setup': 'Setup',
    'role.owner': 'Owner, who can do everything including open a branch',
    'role.manager': 'Manager, who runs a branch and can hire',
    'role.dispenser': 'Dispenser, who fits and sells',
    'role.cashier': 'Cashier, who takes money at the till',
    'role.optician': 'Optician, who tests eyes and writes prescriptions',
    'role.accountant': 'Accountant, who sees the books and not the prescriptions',
    'role.readOnly': 'Read only, who can look and change nothing',
    // `RES-001`: the number is available, not on-hand, because that is what the sale will accept.
    // The held count is said separately where there is one, so a dispenser sees that the difference
    // is a promise to somebody and not a miscount.
    'frameChoice.onHand': {
        one: 'the last one',
        other: '{count} to sell',
    },
    'frameChoice.held': {
        one: 'one held',
        other: '{count} held',
    },
    'customerSearch.hint.short': 'Type at least two letters, or a phone number.',
    'customerSearch.hint.searching': 'Searching',
    'customerSearch.hint.none': 'No customer found. Check the spelling, or try the phone number.',
    'customerSearch.hint.found': {
        one: '{count} found. Arrow keys to choose, Enter to pick.',
        other: '{count} found. Arrow keys to choose, Enter to pick.',
    },
    // `JobDetail`'s verbs for Appendix C.2's forward edges. Separate from `status.job.*`, which names
    // the state: "Sent to lab" is where the job is, "Mark sent to lab" is what the dispenser does.
    'action.job.draft': 'Move to draft',
    'action.job.ordered': 'Mark ordered from lab',
    'action.job.sent': 'Mark sent to lab',
    'action.job.chasing': 'Start chasing the lab',
    'action.job.acknowledged': 'Mark acknowledged by lab',
    'action.job.inProduction': 'Mark in production',
    'action.job.shipped': 'Mark shipped from lab',
    'action.job.received': 'Mark received at branch',
    'action.job.qc': 'Start quality check',
    'action.job.qcFailed': 'Mark quality check failed',
    'action.job.remakeRaised': 'Raise a remake',
    'action.job.ready': 'Mark ready for collection',
    'action.job.collected': 'Collect job',
    'action.job.onHold': 'Put on hold',
    'action.job.cancelled': 'Cancel job',
    /**
     * `JOB-003`: a job comes off hold back to the state it was held from, so the button names it.
     * A whole sentence with a hole rather than "Resume, back to " plus a label (Appendix S).
     */
    'action.job.resumeTo': 'Resume, back to {status}',
    /**
     * What the write queue calls a job action, so a blocked write in the pending list says which job
     * it was and what was being done to it (`OFF-005`). A message rather than a template literal,
     * because this string reaches a shopkeeper's screen like any other.
     */
    'jobDetail.writeLabel': '{action} (job {number})',
    // Appendix E's enum values in a shopkeeper's words, read by
    // `apps/counter/src/status-labels.ts`. Four screens held their own copy of these tables and
    // three of them disagreed about the job states; this is the one copy.
    'status.rxType.distance': 'Distance',
    'status.rxType.near': 'Near',
    'status.rxType.bifocal': 'Bifocal',
    'status.rxType.progressive': 'Progressive',
    'status.rxType.occupational': 'Occupational',
    'status.rxType.safety': 'Safety',
    'status.rxType.contactLens': 'Contact lens',
    'status.rx.draft': 'Draft',
    'status.rx.active': 'Active',
    'status.rx.superseded': 'Superseded',
    'status.rx.expired': 'Expired',
    'status.rx.discarded': 'Discarded',
    'status.order.draft': 'Draft',
    'status.order.quoted': 'Quoted',
    'status.order.confirmed': 'Confirmed',
    'status.order.partiallyPaid': 'Partially paid',
    'status.order.paid': 'Paid',
    'status.order.fulfilled': 'Fulfilled',
    'status.order.closed': 'Closed',
    'status.order.expired': 'Expired',
    'status.order.cancelled': 'Cancelled',
    'status.order.refunded': 'Refunded',
    'status.order.onHold': 'On hold',
    'status.job.draft': 'Draft',
    'status.job.ordered': 'Ordered',
    'status.job.sent': 'Sent to lab',
    'status.job.chasing': 'Chasing lab',
    'status.job.acknowledged': 'Acknowledged',
    'status.job.inProduction': 'In production',
    'status.job.shipped': 'Shipped from lab',
    'status.job.received': 'Received at branch',
    'status.job.qc': 'Quality check',
    'status.job.qcFailed': 'QC failed',
    'status.job.remakeRaised': 'Remake raised',
    'status.job.ready': 'Ready for collection',
    'status.job.collected': 'Collected',
    'status.job.onHold': 'On hold',
    'status.job.cancelled': 'Cancelled',
    'status.cashSession.open': 'Open',
    'status.cashSession.counting': 'Counting',
    'status.cashSession.variancePending': 'Variance pending approval',
    'status.cashSession.approved': 'Approved, closing',
    'status.cashSession.closed': 'Closed',
    // The write queue's own sentences, `OFF-005`. Rendered by five screens through
    // `apps/counter/src/queue/status-line.ts`, which is one copy of what used to be five.
    'queue.online': 'Online',
    'queue.offline': 'Offline',
    'queue.synced': '{connection}, everything synced',
    'queue.busy': '{connection}, {detail}',
    'queue.waitingToSync': {
        one: '{count} waiting to sync',
        other: '{count} waiting to sync',
    },
    /**
     * The plural form fixes a grammar bug the five copies all shared: they read "1 need attention",
     * which `apps/counter/test/blocked-writes.test.tsx` quotes in its own docstring. The catalogue
     * test is what found it, by refusing a `{count}` message with no plural form.
     */
    'queue.needAttention': {
        one: '{count} needs attention',
        other: '{count} need attention',
    },
    'queue.statusLine': '{queueState}. {writeState}',
    'queue.write.saved': '{subject} saved.',
    'queue.write.blocked': '{subject} was NOT saved: {reason}. It needs attention.',
    'queue.write.pending': '{subject} is on this device and will save when it reaches the server.',
    'queue.write.refusedNoReason': 'refused by the server',
    // The nouns the five screens put in `{subject}`. Noun phrases rather than sentences, because
    // the sentence is the message above and this is the hole in it.
    'queue.subject.prescription': 'Prescription',
    'queue.subject.payment': 'Payment',
    'queue.subject.order': 'The order',
    'queue.subject.confirmation': 'The confirmation',
    'queue.subject.openingFloat': 'The opening float',
    'queue.subject.count': 'The count',
    'queue.subject.payOut': 'The pay-out',
    'queue.subject.payIn': 'The pay-in',
    'queue.subject.dispense': 'The dispense',
    'queue.subject.quote': 'The quote',
    // `JobDetail` labels its write with what the action was, so its three are their own sentences.
    'jobDetail.write.saved': '{label}: saved.',
    'jobDetail.write.blocked': '{label}: this was NOT saved, {reason}. It needs attention.',
    'jobDetail.write.pending': '{label}: on this device, will save when it reaches the server.',
    // The blocked-writes banner, `OFF-005`.
    'blocked.heading': {
        one: '{count} thing has not saved and is holding up everything after it',
        other: '{count} things have not saved and are holding up everything after them',
    },
    'blocked.noReason': 'The server refused it and gave no reason.',
    'blocked.retry': 'Try again',
    'blocked.discard': 'Discard',
    'blocked.confirmDiscard': 'Discard "{label}"? It has not been saved anywhere and cannot be recovered.',
    // The counter shell's own states, in `App.tsx`.
    'app.settingsLoadFailed': 'Could not load this shop’s settings.',
    'app.drawerLoadFailed': 'Could not load the drawer.',
    'app.expensesLoadFailed': 'Could not load what this branch has spent.',
    'expenses.heading': 'Expenses',
    'app.noBranch': 'You are not assigned to a branch, so there is nothing you can do here yet. Ask the owner to give you access to a branch.',
    'app.back': 'Back',
    'app.backToSearch': 'Back to search',
    'app.backToOrders': 'Back to orders',
    'app.capturePrescription': 'Capture prescription',
    'app.takePayment': 'Take payment',
    'app.returnLine': 'Return a line',
    'app.customerOpenFailed': 'That customer record could not be opened.',
    'app.jobOpenFailed': 'Could not open that job.',
    'app.orderOpenFailed': 'Could not open that order.',
    'app.quoteOpenFailed': 'Could not open that quote.',
    'app.receiptOpenFailed': 'Could not build a receipt for that order.',
    'app.printReceipt': 'Print the receipt',
    'app.noTerminal': 'No terminal is configured for this branch, so there is no drawer to open.',
    'app.cannotOrder': 'This branch cannot raise an order yet.',
    'app.cannotOrder.noTerminal': 'No terminal has an open invoice number block.',
    'app.cannotOrder.noSeries': 'No invoice series is configured for the branch.',
    // `PRC-013`.
    'app.cannotOrder.noRounding': 'No rounding rule is configured for the shop.',
    // Words and sentences that three or more screens share. Collapsed here because they are the same
    // sentence, so a translator renders each once. Screen headings that match a rail destination
    // (`nav.*`) and the shell's `app.takePayment` / `app.returnLine` reuse those keys instead.
    'common.open': 'Open',
    'common.total': 'Total',
    'common.outstanding': 'Outstanding',
    'common.overdue': 'Overdue',
    'common.promised': 'Promised {date}',
    'common.reason': 'Reason',
    'common.quantity': 'Quantity',
    'common.amount': 'Amount',
    'common.lines': 'Lines',
    'common.notRecorded': 'Not recorded',
    'common.orderNoLines': 'This order has no lines.',
    // Tender types, shared by `TakePayment` (the method select) and `OrderDetail` (the payment list).
    'tender.cash': 'Cash',
    'tender.card': 'Card',
    'tender.bankTransfer': 'Bank transfer',
    'tender.wallet': 'Mobile wallet',
    'tender.storeCredit': 'Store credit',
    'tender.giftCard': 'Gift card',
    'tender.panel': 'Panel',
    'tender.voucher': 'Voucher',
    'tender.cod': 'Cash on delivery',
    // `RX-001`'s power grid headers, read by `CustomerDetail` and `JobDetail`. The row headers are
    // per screen because the two screens spell the eye abbreviation differently ("OD" and "od").
    'rx.col.eye': 'Eye',
    'rx.col.sphere': 'Sphere',
    'rx.col.cylinder': 'Cylinder',
    'rx.col.axis': 'Axis',
    'rx.col.add': 'Add',
    // `RX-003`: "displayed on demand, never destructive". The record says which notation it is in
    // rather than leaving a reader to work it out from the signs, and says so when the two differ.
    'rx.notation.minus': 'Minus-cylinder',
    'rx.notation.plus': 'Plus-cylinder',
    'rx.notation.showMinus': 'Show in minus-cylinder',
    'rx.notation.showPlus': 'Show in plus-cylinder',
    'rx.notation.enteredInPlus': 'Entered in plus-cylinder.',
    // `RES-001`, `RES-002`: a frame held for a named customer, with an end. `decisions/0032`.
    // "What happened to the frame I was promised" is the question this panel answers, so an ended
    // hold says which way it ended rather than vanishing from the list.
    'holds.heading': 'Frames on hold',
    'holds.loadFailed': 'The holds could not be read.',
    'holds.none': 'No frame has been held for this customer.',
    'holds.unknownFrame': 'The frame is no longer on file.',
    'holds.quantity': {
        one: 'One held',
        other: '{count} held',
    },
    'holds.until': 'Until',
    'holds.state.held': 'Held',
    'holds.state.expired': 'Expired',
    'holds.state.released': 'Released',
    'holds.state.releasedFor': 'Released: {reason}',
    'holds.release': 'Release the hold',
    'holds.releaseConfirm': 'Release it',
    'holds.reason': 'Why',
    'holds.cancel': 'Keep the hold',
    'holds.newHeading': 'Hold a frame',
    'holds.pickAFrame': 'Pick a frame to hold.',
    'holds.quantityLabel': 'How many',
    'holds.untilLabel': 'Hold until',
    'holds.hold': 'Hold it',
    // `DESIGN-TOKENS.md` §8 `ST-02`: the six states every data surface has to have. The two empty
    // ones are different sentences on purpose, because they are different situations: "none yet" is
    // an invitation and "none match that" is a suggestion, and one sentence serving both is wrong
    // half the time. "Not allowed" is its own, because a surface that cannot say it says "there is
    // nothing here", which is a different and false statement.
    'surface.loading': 'Reading {what}.',
    'surface.empty': 'No {what} on file yet.',
    'surface.filtered': 'Nothing matches that.',
    'surface.partial': 'Some of this is not shown: {missing}.',
    'surface.denied': 'You are not allowed to see {what} here.',
    'surface.retry': 'Try again',
    // §16.5: "offline indicator with pending count, always visible when not clean". Three states and
    // not one urgency: offline with writes waiting is normal in a Pakistani shop, waiting while
    // online is a slow connection, and a blocked write is the one somebody has to act on.
    'queueFlag.waiting': {
        one: 'One not saved yet',
        other: '{count} not saved yet',
    },
    'queueFlag.offline': {
        one: 'Offline, one not saved yet',
        other: 'Offline, {count} not saved yet',
    },
    'queueFlag.offlineClean': 'Offline, everything saved',
    'queueFlag.blocked': {
        one: 'One needs attention',
        other: '{count} need attention',
    },
    // §16.5: "form error summary at the top listing every failed field as a link". A link and not a
    // sentence, because the summary is a way to reach the field rather than a second place to read
    // the same words.
    'formErrors.item': '{message}',
    'formErrors.heading': {
        one: 'One thing needs fixing',
        other: '{count} things need fixing',
    },
    // §16.5: "sync conflict resolver showing both versions side by side with an explicit choice".
    // Retry on a version conflict was a button that could not work: the queued body still carries the
    // `expectedVersion` that already lost the race, so it re-sent the same stale number.
    'conflict.means': 'Somebody else changed this record while you were working on it. Choose which to keep.',
    'conflict.noDifference': 'The two are the same. Keeping either keeps this record.',
    'conflict.field': 'What',
    'conflict.mine': 'Your change',
    'conflict.theirs': 'On file now',
    'conflict.empty': 'Nothing',
    'conflict.keepMine': 'Keep my change',
    'conflict.keepTheirs': 'Keep what is on file',
    // Where a line stands for returns, `POS-005`. `ReturnLine` and `OrderDetail` both show it.
    'returns.position.returnable': 'Returnable',
    'returns.position.madeToSpecification': 'Made to prescription',
    'returns.position.policyDependent': 'Ask the shop',
    // `NewOrder`, `SAL-001`, `SAL-012`, `POS-007`, `POS-008a`. Its sale type labels differ in
    // punctuation from `OrderDetail`'s ("Lenses only (own frame)"), so the two screens keep two sets
    // rather than one of them changing on screen.
    'newOrder.saleType.label': 'Sale type',
    'newOrder.saleType.choose': 'Choose a sale type',
    'newOrder.saleType.completeSpectacles': 'Complete spectacles',
    'newOrder.saleType.lensesOnlyOwnFrame': 'Lenses only (own frame)',
    'newOrder.saleType.frameOnly': 'Frame only',
    'newOrder.saleType.singleLensReplacement': 'Single lens replacement',
    'newOrder.saleType.planoSunglasses': 'Plano sunglasses',
    'newOrder.saleType.prescriptionSunglasses': 'Prescription sunglasses',
    'newOrder.saleType.readyReaders': 'Ready readers',
    'newOrder.saleType.contactLenses': 'Contact lenses',
    'newOrder.saleType.clSolutions': 'Contact lens solutions',
    'newOrder.saleType.repairOrAdjustment': 'Repair or adjustment',
    'newOrder.saleType.spareParts': 'Spare parts',
    'newOrder.saleType.eyeTestOnly': 'Eye test only',
    'newOrder.taxTreatment.choose': 'Choose a tax treatment',
    'newOrder.taxTreatment.inclusive': 'Inclusive (price already includes tax)',
    'newOrder.taxTreatment.exclusive': 'Exclusive (tax added on top)',
    'newOrder.taxTreatment.exempt': 'Exempt from tax',
    'newOrder.taxTreatment.zeroRated': 'Zero-rated',
    'newOrder.customerLine': 'Customer: {customerName}.',
    'newOrder.noCustomer': 'No customer attached to this sale.',
    'newOrder.prescriptionAttached': 'A prescription is attached.',
    'newOrder.noPrescription': 'No prescription attached yet.',
    'newOrder.prescriptionKind.contactLens': 'contact lens',
    'newOrder.prescriptionKind.spectacle': 'spectacle',
    'newOrder.prescription.notNeeded': '{saleType} does not need a prescription.',
    'newOrder.prescription.needed': '{saleType} needs a {kind} prescription.',
    'newOrder.prescription.neededNoneAttached': '{saleType} needs a {kind} prescription, and none is attached to this sale yet.',
    // `POS-007`.
    'newOrder.layaway': 'Layaway: paid over time, held before manufacture',
    'newOrder.lines.addAnother': 'Add another line',
    'newOrder.createButton': 'Create order',
    'newOrder.createHint': 'This saves a draft (Appendix C.1). It does not confirm the sale or take an invoice number: that is a separate step, below, once the order exists.',
    'newOrder.queue.createLabel': 'New order for {customerName}',
    'newOrder.queue.confirmLabel': 'Confirm order {orderId}',
    'newOrder.created.heading': 'Order created',
    'newOrder.created.totals': 'Subtotal {subtotal}, tax {tax}, total {total}.',
    'newOrder.created.orderDiscount': 'Order discount {discount}.',
    'newOrder.created.lineReport': '{position}. {description}: {gross} ({net} net + {tax} tax)',
    'newOrder.confirm.heading': 'Confirm the order',
    'newOrder.confirm.hint': "The order above is still a draft. Confirming issues the invoice number from this terminal's {seriesCode} block for {fiscalYear}, and cannot be taken back by creating the order again.",
    'newOrder.confirm.button': 'Confirm order',
    'newOrder.confirmed.heading': 'Confirmed',
    'newOrder.confirmed.invoice': 'Invoice {invoiceNumber} issued (sequence {sequence}).',
    // `{n}` is the line's position, counted from 1. The same label names the line in the editor, in
    // an error and in a screen reader's form-field list.
    'newOrder.line.label': 'Line {n}',
    'newOrder.line.remove': 'Remove line {n}',
    'newOrder.line.saleType': 'Line {n} sale type',
    // `SAL-012`.
    'newOrder.line.saleTypeSameAsOrder': 'Same as the order',
    'newOrder.line.category': 'Line {n} category',
    'newOrder.line.description': 'Line {n} description',
    'newOrder.line.quantity': 'Line {n} quantity',
    'newOrder.line.productId': 'Line {n} product ID (optional)',
    'newOrder.line.priceLegend': 'Line {n} price',
    'newOrder.line.priceFixed': 'Line {n}: fixed price',
    'newOrder.line.priceLens': 'Line {n}: price as a lens (Appendix V, the price book decides)',
    'newOrder.line.unitPrice': 'Line {n} unit price ({currency})',
    // `PRC-007`, `DSP-003`, `DSP-004`. The eye replaced the sphere, cylinder and add fields: the
    // server reads the powers from the prescription, so there is nothing for a dispenser to retype.
    'newOrder.line.eye': 'Line {n}: which eye',
    'newOrder.line.eyeUnset': 'Choose an eye',
    'newOrder.line.eyeRight': 'Right (OD)',
    'newOrder.line.eyeLeft': 'Left (OS)',
    'newOrder.line.pairNumber': 'Line {n}: pair number',
    'newOrder.line.fittingHeight': 'Line {n}: fitting height in mm',
    'newOrder.error.lineEyeRequired': 'Line {n} is priced as a lens, so it needs an eye. The powers come from the prescription.',
    'newOrder.error.linePairInvalid': 'Line {n}: a pair number is a whole number from 1.',
    'newOrder.error.lineFittingHeightInvalid': 'Line {n}: a fitting height is a number of millimetres above zero.',
    'newOrder.line.diameter': 'Line {n} diameter mm (optional)',
    'newOrder.line.treatments': 'Line {n} treatments (optional, comma-separated IDs)',
    'newOrder.line.taxPercent': 'Line {n} tax percent',
    'newOrder.line.taxTreatment': 'Line {n} tax treatment',
    'newOrder.line.taxCode': 'Line {n} tax code',
    // `{legend}` is the discount control's own name, "Order discount" or "Line 2 discount", which
    // is also what its validation errors call it.
    'newOrder.discount.orderLegend': 'Order discount',
    'newOrder.discount.lineLegend': 'Line {n} discount',
    'newOrder.discount.noDiscount': '{legend}: no discount',
    'newOrder.discount.percent': '{legend}: percent',
    'newOrder.discount.amount': '{legend}: amount',
    'newOrder.discount.percentField': '{legend} percent',
    'newOrder.discount.amountField': '{legend} amount ({currency})',
    'newOrder.discount.limitNote': 'The per-role discount limit is a shop setting with no default, so it is not checked here. A discount above what a role may apply is refused when it is saved, by staff member, with a reason.',
    // Validation, in the order `buildLine` and `buildOrder` meet them. `{subject}` is a discount
    // legend above. `linePrefixed` carries a message written in `packages/domain`, which is English
    // until that package grows message keys of its own.
    'newOrder.error.discountPercentMissing': '{subject}: enter a percent, or choose no discount.',
    'newOrder.error.discountPercentInvalid': '{subject}: not a valid percentage',
    'newOrder.error.discountAmountInvalid': '{subject}: not a valid amount',
    'newOrder.error.discountPrefixed': '{subject}: {message}',
    'newOrder.error.lineCategoryRequired': 'Line {n}: category is required.',
    'newOrder.error.lineDescriptionRequired': 'Line {n}: description is required.',
    'newOrder.error.lineQuantityInvalid': 'Line {n}: quantity must be a whole number of at least 1.',
    'newOrder.error.linePrefixed': 'Line {n}: {message}',
    'newOrder.error.lineDiameterInvalid': 'Line {n}: diameter must be a positive number of millimetres.',
    'newOrder.error.lineUnitPriceInvalid': 'Line {n}: not a valid price',
    'newOrder.error.lineTaxPercentRequired': 'Line {n}: tax percent is required.',
    'newOrder.error.lineTaxPercentInvalid': 'Line {n}: tax percent is not valid',
    'newOrder.error.lineTaxTreatmentRequired': 'Line {n}: choose a tax treatment.',
    'newOrder.error.lineTaxCodeRequired': 'Line {n}: tax code is required.',
    'newOrder.error.saleTypeRequired': 'Choose a sale type before creating the order.',
    'newOrder.error.noLines': 'An order needs at least one line.',
    'newOrder.error.createdReadBack': 'The order was created, but its details could not be read back.',
    'newOrder.error.confirmedReadBack': 'The order was confirmed, but its invoice number could not be read back.',
    // `CustomerDetail`, `CUS-*`, `RX-002`, `RX-006`. Version cards link to each other, so the
    // supersede sentences carry a `{link}` hole: the screen fills it with a sentinel and splits the
    // result around it to put an anchor there, since a message is text and an anchor is not.
    'customerDetail.balance.heading': 'Udhaar balance',
    'customerDetail.balance.none': 'No balance owing',
    'customerDetail.balance.owes': 'Owes {amount}',
    'customerDetail.balance.credit': 'Credit of {amount}',
    'customerDetail.rx.heading': 'Prescription history',
    'customerDetail.rx.keptNote': 'Every version is kept. A correction creates a new version rather than editing the old one.',
    'customerDetail.rx.withheld': 'Your role does not include prescription access, so these are not shown. This is not "none on file".',
    'customerDetail.rx.none': 'No prescriptions on file.',
    'customerDetail.rx.noneInForce': 'No {typeLabel} prescription is currently in force. The most recent is version {version} ({status}).',
    'customerDetail.rx.versionHeading': 'Version {version}',
    'customerDetail.rx.tableCaption': 'Powers for {typeLabel} version {version}',
    'customerDetail.rx.row.right': 'Right (OD)',
    'customerDetail.rx.row.left': 'Left (OS)',
    'customerDetail.rx.issued': 'Issued {issued}',
    'customerDetail.rx.issuedExpires': 'Issued {issued}, expires {expires}',
    'customerDetail.rx.issuedBy': 'Issued {issued}, prescribed by {prescriber}',
    'customerDetail.rx.issuedExpiresBy': 'Issued {issued}, expires {expires}, prescribed by {prescriber}',
    'customerDetail.rx.firstVersion': 'This is the first version on file.',
    'customerDetail.rx.supersedesHiddenReason': 'Supersedes an earlier version not shown here: {reason}',
    'customerDetail.rx.supersedesHidden': 'Supersedes an earlier version not shown here.',
    'customerDetail.rx.supersedesLinkReason': 'Supersedes {link}: {reason}',
    'customerDetail.rx.supersedesLink': 'Supersedes {link}.',
    'customerDetail.rx.versionRef': 'version {version}',
    'customerDetail.rx.notSuperseded': 'Nothing supersedes this version.',
    'customerDetail.rx.supersededHidden': 'Superseded by a later version not shown here.',
    'customerDetail.rx.supersededByLink': 'Superseded by {link}.',
    'customerDetail.rx.compare': 'Compare with version {version}',
    'customerDetail.rx.compareHeading': 'Version {current} against version {previous}',
    'customerDetail.rx.compareFieldColumn': 'Field',
    'customerDetail.rx.compareUnchanged': 'No change: {value}',
    'customerDetail.rx.compareAdded': 'Added: {after}',
    'customerDetail.rx.compareRemoved': 'Removed (was {before})',
    'customerDetail.rx.compareChanged': 'Changed: {before} to {after} ({delta})',
    'customerDetail.rx.compareChangedNoDelta': 'Changed: {before} to {after}',
    'customerDetail.rx.compareNotCompared': 'Visual acuity and notes are not compared. Check them on the version itself.',
    'customerDetail.rx.compareBalanceYes': 'Balance lens',
    'customerDetail.rx.compareBalanceNo': 'Corrected',
    'customerDetail.rx.compareField.intermediateAdd': 'Intermediate add',
    'customerDetail.rx.compareField.prismHorizontal': 'Prism, horizontal',
    'customerDetail.rx.compareField.prismVertical': 'Prism, vertical',
    'customerDetail.rx.compareField.prismOblique': 'Prism, oblique',
    'customerDetail.rx.compareField.backVertexDistance': 'Back vertex distance',
    'customerDetail.rx.compareField.balanceLens': 'Balance lens',
    'customerDetail.rx.compareUnreadable': 'This comparison cannot be shown because a value on file is not a 0.25 step.',
    'customerDetail.orders.heading': 'Purchase and job history',
    'customerDetail.orders.none': 'No orders yet.',
    'customerDetail.fitting.heading': 'Fitting measurements',
    'customerDetail.fitting.keptNote': 'Kept per frame, separate from the prescription, because they belong to the frame rather than the eyes.',
    'customerDetail.fitting.none': 'No fitting measurements on file.',
    'customerDetail.fitting.monocularPd': 'Monocular PD (OD / OS)',
    'customerDetail.fitting.binocularPd': 'Binocular PD',
    'customerDetail.fitting.nearPd': 'Near PD',
    'customerDetail.fitting.segmentHeight': 'Segment height (OD / OS)',
    // One pair of millimetre values and one single value, used by every measurement above.
    'customerDetail.fitting.pairMm': '{od} / {os} mm',
    'customerDetail.fitting.valueMm': '{value} mm',
    'customerDetail.fitting.measuredOn': 'Measured {recordedOn}',
    'customerDetail.recall.heading': 'Recall schedule',
    'customerDetail.recall.notBuilt': 'Recalls arrive with M5. This is not "none scheduled": nothing can schedule one yet.',
    'customerDetail.recall.none': 'No recalls scheduled.',
    'customerDetail.recall.due': 'Due {dueOn}',
    'customerDetail.consent.heading': 'Consent records',
    'customerDetail.consent.none': 'No consent recorded.',
    'customerDetail.consent.granted': 'Granted',
    'customerDetail.consent.declined': 'Declined',
    // `JobDetail`, `JOB-003`, `JOB-005a`, `RX-005`. `statusPrefix` sits beside a badge element, so it
    // cannot take the status as a hole.
    'jobDetail.heading': 'Job {jobNumber}',
    'jobDetail.statusPrefix': 'Status:',
    'jobDetail.rx.heading': 'Prescription this job was made from',
    'jobDetail.rx.tableCaption': 'Frozen prescription snapshot for job {jobNumber}',
    'jobDetail.rx.col.pd': 'PD (mm)',
    'jobDetail.rx.col.segmentHeight': 'Segment height (mm)',
    'jobDetail.rx.row.right': 'Right (od)',
    'jobDetail.rx.row.left': 'Left (os)',
    'jobDetail.rx.frozenNote': 'Frozen at order time. A later correction to the prescription does not change what this job was made from.',
    'jobDetail.qcLatest.heading': 'Latest quality check',
    'jobDetail.qc.result': 'Result',
    'jobDetail.qc.passed': 'Passed',
    'jobDetail.qc.failed': 'Failed',
    'jobDetail.qc.heading': 'Record the quality check',
    'jobDetail.qc.measuredAgainst': 'Measured against ordered.',
    'jobDetail.qc.eyeCaption.right': 'Right eye (od)',
    'jobDetail.qc.eyeCaption.left': 'Left eye (os)',
    'jobDetail.qc.col.field': 'Field',
    'jobDetail.qc.col.ordered': 'Ordered',
    'jobDetail.qc.col.measured': 'Measured',
    // The hidden label on each measured input. `{side}` and `{field}` are the lowercase words
    // below, which the old code took from English identifiers.
    'jobDetail.qc.measuredEye': 'Measured {side} eye {field}',
    'jobDetail.qc.side.right': 'right',
    'jobDetail.qc.side.left': 'left',
    'jobDetail.qc.field.sphere': 'sphere',
    'jobDetail.qc.field.cylinder': 'cylinder',
    'jobDetail.qc.field.axis': 'axis',
    'jobDetail.qc.field.add': 'add',
    'jobDetail.qc.monocularPd': 'Monocular PD (mm)',
    'jobDetail.qc.frameCondition': 'Frame condition',
    'jobDetail.qc.reasonFailed': 'Reason it failed',
    'jobDetail.qc.errorNoResult': 'Say whether it passed or failed before saving.',
    'jobDetail.qc.errorNoReason': 'A failed check needs a reason. The remake it raises is assigned its cause from it.',
    'jobDetail.qc.save': 'Save quality check',
    'jobDetail.qc.writeLabel': 'Quality check (job {jobNumber})',
    'jobDetail.actions.heading': 'Actions',
    'jobDetail.deadEnd.remakeRaised': 'A remake has been raised for this job. Track its progress from the remakes screen.',
    'jobDetail.deadEnd.collected': 'This job has been collected. There is nothing further to do.',
    'jobDetail.deadEnd.cancelled': 'This job is cancelled.',
    'jobDetail.onHoldNoResume': 'This job is on hold with no record of where to resume it. That has to be set explicitly before it can move again.',
    'jobDetail.collect.heading': 'Collect',
    /**
     * `POS-005`: a void unwinds the sale and leaves the job alone, so a finished pair can be
     * sitting on the shelf against a sale that no longer exists. The dispenser is told what
     * changed and what to do, because the job itself still looks ready to them.
     */
    'jobDetail.collect.orderNotCollectable': 'These are made and ready, but the sale behind them has been voided, so there is nothing to collect against. Ring the glasses up again on a new sale, then collect them on that.',
    'jobDetail.collect.cashLabel': 'Cash received now',
    'jobDetail.collect.invalid': 'Not a valid amount.',
    'jobDetail.collect.preview': '= {amount}. Leave empty if the balance is fully on udhaar.',
    'jobDetail.collect.submit': 'Collect job',
    'jobDetail.collect.writeLabel': 'Collect job {jobNumber}',
    // `CashSession`, `POS-004`, Appendix C.7. The screen title is `nav.cash`.
    'cashSession.terminal': 'Terminal {terminalId}',
    'cashSession.terminalOpenedWith': 'Terminal {terminalId}, opened with {openingFloat}',
    'cashSession.openHeading': 'Open the drawer',
    'cashSession.openingFloatLabel': 'Opening float',
    'cashSession.openButton': 'Open drawer',
    'cashSession.floatNegative': 'A drawer cannot open with less than nothing in it.',
    'cashSession.queue.openLabel': 'Open drawer, terminal {terminalId}',
    'cashSession.movementHeading': 'Pay-ins and pay-outs',
    'cashSession.directionLegend': 'Direction',
    'cashSession.directionIn': 'Pay-in (money into the drawer)',
    'cashSession.directionOut': 'Pay-out (money out of the drawer)',
    'cashSession.recordButton': 'Record',
    'cashSession.amountRequired': 'Enter an amount.',
    'cashSession.movementRefused': 'That movement was not accepted.',
    'cashSession.queue.payInLabel': 'Pay-in for terminal {terminalId}',
    'cashSession.queue.payOutLabel': 'Pay-out for terminal {terminalId}',
    'cashSession.movementsNone': 'No pay-ins or pay-outs recorded yet.',
    'cashSession.movementsRecorded': {
        one: '{count} pay-in or pay-out recorded. The amounts appear with the Z-report, after the count.',
        other: '{count} pay-ins or pay-outs recorded. The amounts appear with the Z-report, after the count.',
    },
    'cashSession.varianceWaiting': 'This session has a variance and is waiting for a manager who did not count it to approve, before it can close (Appendix C.7).',
    // Heading and submit button are the same words.
    'cashSession.countAndClose': 'Count and close',
    'cashSession.blindCountHint': 'Count the cash in the drawer without looking at anything else on this screen. What it should hold appears only after you submit your count.',
    'cashSession.countedLabel': 'Amount counted',
    'cashSession.countRequired': 'Enter what you actually counted. It cannot be less than nothing.',
    'cashSession.queue.countLabel': 'Count and close, terminal {terminalId}',
    'cashSession.resultUnreadable': 'The count was accepted, but its result could not be read.',
    'cashSession.resultHeading': 'Count result',
    'cashSession.resultCounted': 'Counted {counted} against an expected {expected}.',
    'cashSession.resultBalanced': 'The drawer balanced. This session is closed.',
    // Short and over are two sentences, not one with a word swapped, so each translates alone.
    'cashSession.resultShort': 'Short by {amount}. This session needs a manager who did not count it to approve the variance before it closes (Appendix C.7).',
    'cashSession.resultOver': 'Over by {amount}. This session needs a manager who did not count it to approve the variance before it closes (Appendix C.7).',
    'cashSession.zReportTotals': 'Opening float {openingFloat}, cash taken {cashTaken}, pay-ins {payIns}, pay-outs {payOuts}.',
    'cashSession.zReportMovement': '{amount}: {reason}',
    // `ReturnLine`, `POS-005`. The title is `app.returnLine`.
    'returnLine.policyRefused': 'The server refused the returns policy (HTTP {status}).',
    'returnLine.policyUnreachable': 'Could not reach the server to check the returns policy.',
    'returnLine.linesAriaLabel': 'Lines on this order',
    'returnLine.linesLeft': '{remaining} of {sold} left to return',
    'returnLine.restockedLabel': 'Goods went back on the shelf',
    'returnLine.returnButton': 'Return',
    'returnLine.goodwillButton': 'Return anyway as a goodwill exception',
    'returnLine.overrideHeading': 'Not returnable as of right',
    'returnLine.overrideFallback': 'This return needs a goodwill override.',
    'returnLine.goodwillConfirm': '{reason}\n\nReturn it anyway as a goodwill exception? This needs the goodwill override permission and is recorded against you.',
    'returnLine.noReason': 'The server refused it and gave no reason.',
    'returnLine.queuedOnDevice': 'On this device and will be sent when it reaches the server.',
    'returnLine.returned': 'Returned.',
    'returnLine.returnedNoCash': 'Returned. No cash goes back: nothing was paid at the counter, so the balance on the order comes down instead.',
    'returnLine.returnedRefund': 'Returned. {amount} goes back.',
    'returnLine.notReturned': 'NOT returned: {message}',
    'returnLine.notReturnedCode': 'NOT returned: {message} ({code})',
    'returnLine.queue.returnLabel': 'Return {quantity} from order {orderId}',
    // `OrderDetail`, `POS-001`, `POS-005`, `PRC-*`. Its sale type labels are the plain-comma ones.
    'orderDetail.heading': 'Order {number}',
    /*
     * And the same sentences for an order `TAX-002` has not numbered yet.
     *
     * Substituting a label into `{number}` gave "Order Not invoiced yet", which is the defect the
     * label was meant to end rather than a smaller version of it: the placeholder it replaced read
     * as a sentence only because of its parentheses, and dropping those made it worse. A sentence
     * with nothing to name has to be a different sentence.
     */
    'orderDetail.headingNoNumber': 'Order, not invoiced yet',
    'orderDetail.linesCaption': 'Lines on order {number}',
    'orderDetail.linesCaptionNoNumber': 'Lines on this order',
    'orderDetail.col.description': 'Description',
    'orderDetail.col.linePrice': 'Line price',
    'orderDetail.col.discount': 'Discount',
    'orderDetail.col.tax': 'Tax',
    'orderDetail.saleType.completeSpectacles': 'Complete spectacles',
    'orderDetail.saleType.lensesOnlyOwnFrame': 'Lenses only, own frame',
    'orderDetail.saleType.frameOnly': 'Frame only',
    'orderDetail.saleType.singleLensReplacement': 'Single lens replacement',
    'orderDetail.saleType.planoSunglasses': 'Plano sunglasses',
    'orderDetail.saleType.prescriptionSunglasses': 'Prescription sunglasses',
    'orderDetail.saleType.readyReaders': 'Ready readers',
    'orderDetail.saleType.contactLenses': 'Contact lenses',
    'orderDetail.saleType.clSolutions': 'Contact lens solutions',
    'orderDetail.saleType.repairOrAdjustment': 'Repair or adjustment',
    'orderDetail.saleType.spareParts': 'Spare parts',
    'orderDetail.saleType.eyeTestOnly': 'Eye test only',
    // The sale type and the returns position under a line, joined by the screen's separator. The
    // separator stays in the message so the join is the translator's, not a template literal's.
    'orderDetail.lineMeta': '{saleType} · {returnsPosition}',
    'orderDetail.priceWhy': 'Why "{description}" costs what it does',
    // Read by a screen reader before the figure they name; the screen supplies the space.
    'orderDetail.priceContributedSr': 'Contributed',
    'orderDetail.priceRunningSr': 'Running total',
    'orderDetail.paymentsHeading': 'Payments',
    'orderDetail.noPayments': 'No payments have been taken yet.',
    'orderDetail.refundBadge': 'Refund or reversal',
    'orderDetail.takenAt': 'Taken {takenAt}',
    /**
     * `COR-001`'s fourth supported combination, `payment:wrong_staff`, which the server has
     * supported since it was written and nothing could reach.
     *
     * The row says who took the money because a shopkeeper cannot ask for an attribution to be
     * corrected without seeing the one that is wrong, and `BR-08` makes that attribution a thing
     * somebody is accountable for rather than a detail.
     */
    'orderDetail.takenBy': 'Taken by {name}',
    'orderDetail.takenByUnknown': 'Nobody is recorded as taking this',
    'orderDetail.correctTaker': 'Correct who took this',
    'orderDetail.correctTakerFor': 'Correct who took the {method} payment of {amount}',
    'orderDetail.refundedInFull': 'Refunded in full. Invoiced {total}, {refunded} given back. There is nothing left to collect.',
    'orderDetail.partlyRefundedOutstanding': 'Partly refunded: {refunded} of {total} given back. Outstanding: {outstanding}',
    'orderDetail.partlyRefundedSettled': 'Partly refunded: {refunded} of {total} given back. The rest was settled.',
    'orderDetail.paidInFull': 'Nothing outstanding. Paid in full.',
    'orderDetail.outstanding': 'Outstanding: {outstanding}',
    'orderDetail.creditBalance': 'Credit balance: {credit}',
    // `OrderList`. The title is its own, "Orders and jobs", unlike the rail's "Orders".
    'orderList.heading': 'Orders and jobs',
    'orderList.loadFailed': 'Could not load orders and jobs: {message}',
    'orderList.loadFailedNoDetail': 'Could not load orders and jobs.',
    // What this screen calls itself inside a surface sentence, as its own key: lowercasing a
    // translated label is not a safe operation in every script.
    'orderList.what': 'the orders',
    'orderList.empty': 'Nothing due. New orders and jobs will appear here.',
    // Whole accessible names for the two Open buttons, so no sentence is a visible word plus a
    // hidden fragment. The visible text stays `common.open`.
    'orderList.openOrder': 'Open order {number}',
    // Named by the customer, which is also what tells six un-invoiced rows apart.
    'orderList.openOrderNoNumber': 'Open the order for {customer}',
    'orderList.openJob': 'Open job on order {number}, {status}',
    // The job number is what names this one; the order's is a nicety it can do without.
    'orderList.openJobNoNumber': 'Open the job for {customer}, {status}',
    'orderList.quote': 'Quote',
    'orderList.openQuote': 'Open quote {number} to convert it or record that it has run out',
    'orderList.openQuoteNoNumber': 'Open the quote for {customer} to convert it or record that it has run out',
    'orderList.refunded': 'Refunded',
    // `TakePayment`, `POS-002`, `POS-004`, `BR-08`. The title and the button are `app.takePayment`.
    // Each "Rounded to" sentence is its own message, because the second half depends on the first.
    'takePayment.cashSessionRequired': 'Cash has to go into an open drawer. Open a cash session for this terminal first.',
    'takePayment.amountDue': 'Amount due: {amount}',
    'takePayment.tenderMethod': 'Tender {number} method',
    'takePayment.tenderAmount': 'Tender {number} amount ({currency})',
    'takePayment.removeTender': 'Remove tender {number}',
    'takePayment.addTender': 'Add another tender',
    'takePayment.stillOutstanding': 'Still outstanding: {amount}',
    'takePayment.changeDue': 'Change due: {amount}',
    'takePayment.fullyTendered': 'Fully tendered.',
    'takePayment.roundedStillOutstanding': 'Rounded to {rounded}. Still outstanding: {amount}',
    'takePayment.roundedChangeDue': 'Rounded to {rounded}. Change due: {amount}',
    'takePayment.roundedFullyTendered': 'Rounded to {rounded}. Fully tendered.',
    'takePayment.amountUnparseable': 'That is not an amount {currency} can be counted in. Use numbers only, with at most two decimal places.',
    'takePayment.submitBusy': 'Taking payment',
    'takePayment.partialAllowed': 'A partial payment is allowed: the balance stays owing rather than being refused.',
    'takePayment.queueLabel': 'Payment for order {orderId}',
    // `RxCapture`, `RX-001`. The type select goes through `rxTypeLabel`. The paste parser's own
    // assumption notes are English from `packages/domain` and have no key here.
    'rxAction.issue': 'Issue prescription',
    'rxAction.issueHint': 'Issuing locks the values. A later change is a correction, kept as a new version.',
    'rxAction.correct': 'Correct',
    'rxAction.correctLabel': 'Correction to version {version}',
    'rxAction.heading': 'Correct version {version}',
    'rxAction.reason': 'Reason for the correction',
    'rxAction.sphereRequired': 'Both eyes need a sphere.',
    'rxAction.submit': 'Save correction',
    'rxAction.cancel': 'Cancel',
    'queue.subject.rxIssue': 'The issue',
    // Contact lens prescriptions, `RX-001`, `RX-002`, §5.2.
    'customerDetail.cl.heading': 'Contact lens prescriptions',
    'customerDetail.cl.none': 'No contact lens prescriptions on file.',
    'customerDetail.cl.tableCaption': 'Contact lens values for version {version}',
    'customerDetail.cl.substitution.allowed': 'Brand substitution allowed.',
    'customerDetail.cl.substitution.notAllowed': 'Brand-bound: no substitution.',
    'customerDetail.cl.substitution.unset': 'Brand substitution not decided.',
    'clRx.col.power': 'Power',
    'clRx.col.baseCurve': 'Base curve (mm)',
    'clRx.col.diameter': 'Diameter (mm)',
    'clRx.col.brand': 'Brand',
    'clRx.col.modality': 'Modality',
    'clRx.modality.daily': 'Daily',
    'clRx.modality.twoWeekly': 'Two-weekly',
    'clRx.modality.monthly': 'Monthly',
    'clRx.modality.quarterly': 'Quarterly',
    'clRx.modality.annual': 'Annual',
    'clRxAction.fieldsRequired': 'Both eyes need a power, base curve, diameter, brand and modality.',
    'clRxAction.powerInvalid': 'Power must step by 0.25 and stay within 30.00 either way.',
    'clRxAction.cylinderInvalid': 'Cylinder must step by 0.25 and stay within 10.00 either way.',
    'clRxAction.addInvalid': 'Add must step by 0.25, from 0.25 to 4.00.',
    'clRxAction.mmInvalid': 'Base curve must be {bcMin} to {bcMax} mm and diameter {diaMin} to {diaMax} mm.',
    'rxCapture.heading': 'Prescription for {name}',
    'rxCapture.typeLabel': 'Prescription type',
    'rxCapture.pasteRejected': 'Not filled in, because the sign was not written: {cells}. Type these yourself.',
    'rxCapture.save': 'Save prescription',
    'rxCapture.keyboardHint': 'Tab moves between cells, arrows step by 0.25, space toggles the sign, Ctrl+D copies od to os.',
    // `WorkQueue`, `JOB-004`. The title is `nav.queue`; a job's chip goes through `jobStatusLabel`.
    'workQueue.kind.job': 'Job',
    'workQueue.kind.order': 'Order',
    'workQueue.kind.remake': 'Remake',
    'workQueue.kind.repair': 'Repair',
    'workQueue.waitingToday': 'Waiting today',
    'workQueue.waitingDays': {
        one: 'Waiting {count} day',
        other: 'Waiting {count} days',
    },
    'workQueue.loadFailedNoDetail': 'Could not load the work queue.',
    // What the work queue calls itself inside a surface sentence. Its own key rather than the
    // rail's label lowercased, because lowercasing a translated word is not a safe operation.
    'workQueue.what': 'what is waiting',
    'workQueue.empty': 'Nothing is waiting on anyone. Everything raised has been dealt with.',
    'workQueue.thingsWaiting': {
        one: '{count} thing is waiting',
        other: '{count} things are waiting',
    },
    'workQueue.pastPromised': 'Past its promised date',
    'workQueue.noPromisedDate': 'No promised date set',
    // `Quote`, `DSP-001`, `PRC-011`, Appendix V.5. A quote is an order in `quoted`, so it is found on
    // the order list and this screen is only the detail. "Run out" rather than "expired" for a lapsed
    // one: nothing has written that status yet, and saying it had would misdescribe the record.
    'quote.heading': 'Quote',
    'quote.headingFor': 'Quote for {name}',
    'quote.issuedExpires': '{saleType}, quoted on {issued} and good until {expires}.',
    'quote.linesCaption': 'What was quoted, at the prices it was quoted at',
    'quote.pair': 'Pair {pair}',
    'quote.subtotal': 'Before discount and tax',
    'quote.orderDiscount': 'Discount on the whole quote',
    'quote.lapsed': 'This quote has run out. Record that, or raise a new one: the prices in it were held until the date above and no longer are.',
    'quote.expired': "This quote has been marked as run out. Raise a new one to sell at today's prices.",
    'quote.converted': 'This quote has become an order. Take payment against the order, not here.',
    'quote.convert': 'Turn this into an order',
    'quote.recordExpiry': 'Record that it has run out',
    'quote.openOrder': 'Open the order it became',
    'quote.queue.convertLabel': 'Turn a quote into an order',
    'quote.queue.expireLabel': 'Record a quote as run out',
    // `Dispensing`, `DSP-005`. The list is per branch, not per person: a draft only its author can
    // see is a draft that gets started again from nothing. The sale type select goes through
    // `saleTypeLabel`, which is the order form's wording.
    'dispensing.heading': 'Unfinished dispenses',
    'dispensing.whatThisIs': 'A dispense saved half-done, so whoever is on the floor can carry on with it. Anyone at this branch can pick one up.',
    'dispensing.loadFailed': 'Could not load what is unfinished.',
    'dispensing.saleTypeLabel': 'Sale type',
    'dispensing.saleTypeNone': 'Not chosen yet',
    'dispensing.start': 'Start one for {name}',
    'dispensing.startWithoutCustomer': 'Start one with no customer yet',
    'dispensing.openHeading': 'Waiting at this branch',
    'dispensing.savedBy': 'Left by {staff} at {when}',
    'dispensing.noSaleType': 'Sale type not chosen yet',
    'dispensing.resume': 'Carry on',
    'dispensing.resumedHeading': 'Carrying on',
    'dispensing.resumedVersion': 'Version {version}, as this screen read it. If another terminal saves first, your save stops and shows you theirs rather than writing over it.',
    'dispensing.save': 'Save it',
    'dispensing.queue.startLabel': 'Start a dispense',
    'dispensing.queue.saveLabel': 'Save a dispense',
    // `Receipt`, `POS-011`, `PK-024`, Appendix L. The slip itself, 58mm. The `absent` list above it
    // is Appendix L's own English field names and is deliberately not translated: it is a reference
    // to a document for whoever is fixing it, not a sentence for a shopkeeper's customer.
    //
    // Short lines, because 58mm is about 32 monospaced characters. "Balance due" rather than "Amount
    // outstanding" for that reason, not for tone.
    'receipt.heading': 'Receipt',
    'receipt.print': 'Print receipt',
    'receipt.slipLabel': 'The receipt as it will print',
    'receipt.incomplete': {
        one: 'One thing a receipt must carry is missing. It will print without it.',
        other: '{count} things a receipt must carry are missing. It will print without them.',
    },
    'receipt.taxRegistration': 'Tax reg {registration}',
    'receipt.invoice': 'Invoice {series} {number}',
    'receipt.when': 'Sold {when}',
    'receipt.servedBy': 'Served by {name}',
    'receipt.linesCaption': 'What was sold, with quantity, unit price and tax',
    'receipt.qtyOnly': 'Qty {quantity}',
    'receipt.qtyAt': 'Qty {quantity} at {unit}',
    'receipt.lineTax': 'Tax {tax}',
    'receipt.subtotal': 'Before discount and tax',
    'receipt.discount': 'Discount',
    'receipt.tax': 'Tax',
    'receipt.total': 'Total',
    'receipt.refunded': 'Refunded',
    'receipt.balanceDue': 'Balance due',
    'receipt.paidInFull': 'Paid in full. Thank you.',
    'receipt.notSettled': 'Not paid in full. Please keep this slip.',
    'receipt.promised': 'Ready by {date}',
    'status.rimType.fullRim': 'Full rim',
    'status.rimType.semiRimless': 'Semi-rimless (nylon cord)',
    'status.rimType.rimless': 'Rimless (drilled)',
    'status.frameMaterial.acetate': 'Acetate',
    'status.frameMaterial.metal': 'Metal',
    'status.frameMaterial.titanium': 'Titanium',
    'status.frameMaterial.tr90': 'TR90',
    'status.frameMaterial.rimlessMount': 'Rimless mount',
    'frameCatalogue.heading': 'Add a frame',
    'frameCatalogue.means': 'A frame on file can be priced, sold and sent to a lab. Enter the model once, then add each colour and size as its own line.',
    'frameCatalogue.notYours': 'Only an owner or a manager can change the catalogue. Ask one of them to add this frame.',
    'frameCatalogue.modelHeading': 'The model',
    'frameCatalogue.brand': 'Brand',
    'frameCatalogue.model': 'Model name',
    'frameCatalogue.collection': 'Collection',
    'frameCatalogue.modelCode': 'Model code',
    'frameCatalogue.material': 'Material',
    'frameCatalogue.rimType': 'Rim type',
    'frameCatalogue.fitSegment': 'Who it fits',
    'frameCatalogue.notSaid': 'Not said',
    'frameCatalogue.variantHeading': 'This colour and size',
    'frameCatalogue.variantMeans': 'Stock, price and the barcode belong to the colour and size, not to the model, so a frame is not on file until it has one.',
    'frameCatalogue.colourCode': 'Colour code',
    'frameCatalogue.colourName': 'Colour name',
    'frameCatalogue.eyeSize': 'Eye size (mm)',
    'frameCatalogue.bridge': 'Bridge (mm)',
    'frameCatalogue.temple': 'Temple length (mm)',
    'frameCatalogue.barcode': 'Barcode',
    'frameCatalogue.udi': 'Device identifier (UDI)',
    'frameCatalogue.mounting': 'Drill, notch or groove data',
    'frameCatalogue.mountingMeans': 'A lab cutting a lens for a rimless or semi-rimless frame cannot see the hardware, so it needs this. Without it the job cannot be sent.',
    'frameCatalogue.mountingRequired': 'A rimless or semi-rimless frame needs its drill, notch or groove data before it can be sent to a lab.',
    'frameCatalogue.brandAndModelRequired': 'A frame needs a brand and a model name.',
    'frameCatalogue.colourRequired': 'A frame needs a colour code, because stock and price belong to the colour.',
    'frameCatalogue.save': 'Put this frame on file',
    'frameCatalogue.saved': '{brand} {model} is on file.',
    'frameCatalogue.queueLabel': 'Adding {brand} {model}',
    'frameCatalogue.queueSubject': 'the frame',
    // `StockLocations`, §6.30 `BR-04`, `BR-10`, §6.10 `REF-010`. Nothing in the product could create
    // a stock location, so every tenant on the server held zero and could not receive stock at all.
    'stockLocations.heading': 'Where stock is kept',
    'stockLocations.means': 'Stock is received into a location and sold from one. A shop needs at least one before it can take any delivery.',
    'stockLocations.notYours': 'Only an owner can add a place to keep stock. Ask them to add it.',
    'stockLocations.existingHeading': 'Places at this branch',
    'stockLocations.noneYet': 'No places yet, so there is nowhere to receive a delivery. Add the shop floor first.',
    'stockLocations.newHeading': 'Add a place',
    'stockLocations.name': 'Name',
    'stockLocations.code': 'Short code',
    'stockLocations.kindLabel': 'What it is',
    'stockLocations.kind.selling': 'Shop floor',
    'stockLocations.kind.backStore': 'Back store',
    'stockLocations.kind.warehouse': 'Central warehouse',
    'stockLocations.kind.trial': 'Trial case',
    'stockLocations.effect.selling': 'Stock here can be sold at the counter.',
    'stockLocations.effect.backStore': 'Stock here is held but not sold. Move it to the shop floor to sell it.',
    'stockLocations.effect.warehouse': 'Supplies the branches and issues no customer bills. Nothing is sold from here.',
    'stockLocations.effect.trial': 'For trial frames and diagnostic lenses. Stock here is used up and never sold, and it is left out of the dead stock report.',
    'stockLocations.codeTaken': '{code} is already used at this branch. Pick another short code.',
    'stockLocations.nameAndCodeRequired': 'A place needs a name and a short code.',
    'stockLocations.save': 'Add this place',
    'stockLocations.saved': '{name} is on file. Stock can be received into it now.',
    'stockLocations.queueLabel': 'Adding {name}',
    'stockLocations.queueSubject': 'the place',
    // The trial expiry watch on the stock screen. §12 case 118, §6.10 `REF-010`, `INV-009`. A
    // diagnostic contact lens expiring in a drawer had nothing in the product that could surface it.
    'trialExpiry.heading': 'Trial stock going out of date',
    'trialExpiry.means': 'Trial lenses and diagnostic lenses are never sold, so nothing at the till will ever stop an expired one being used on a customer. This is the only place it shows.',
    'trialExpiry.windowLabel': 'Show',
    'trialExpiry.windowExpiredOnly': 'Already out of date',
    'trialExpiry.windowDays': 'Also the next {days} days',
    'trialExpiry.expiredHeading': 'Take these out today',
    'trialExpiry.noneExpired': 'Nothing in a trial case is out of date.',
    'trialExpiry.soonHeading': 'Due within {days} days',
    'trialExpiry.noneSoon': 'Nothing is due in that time.',
    'trialExpiry.expiredOn': 'went out of date {day}, {quantity} left',
    'trialExpiry.expiresOn': 'due {day}, {quantity} left',
    'trialExpiry.undated': {
        one: '{count} more trial item has no date on it, a trial frame most likely.',
        other: '{count} more trial items have no date on them, trial frames most likely.',
    },
    'nav.frameCatalogue': 'Catalogue',
    'rxCopy.print': 'Print',
    'rxCopy.heading': 'Prescription',
    'rxCopy.customer': 'Name',
    'rxCopy.dateOfBirth': 'Date of birth',
    'rxCopy.type': 'Type',
    'rxCopy.issued': 'Issued',
    'rxCopy.tested': 'Sight test',
    'rxCopy.expires': 'Expires',
    'rxCopy.noExpiry': 'No expiry recorded',
    'rxCopy.expiredOn': 'Expired on {date}',
    'rxCopy.version': 'Version',
    'rxCopy.eye': 'Eye',
    'rxCopy.sphere': 'Sph',
    'rxCopy.cylinder': 'Cyl',
    'rxCopy.axis': 'Axis',
    'rxCopy.add': 'Add',
    'rxCopy.pd': 'PD',
    'rxCopy.segHeight': 'Seg ht',
    'rxCopy.prescriber': 'Prescriber',
    'rxCopy.prescriberName': 'Name',
    'rxCopy.registration': 'Registration',
    'rxCopy.prescriberPractice': 'Practice',
    'rxCopy.source': 'Source',
    'rxCopy.legend': 'What the abbreviations mean',
    'rxCopy.notSupplied': 'Not recorded on this prescription',
    'rxCopy.notGiven': 'Not given',
    'rxCopy.open': 'Prescription copy for the customer',
    'rxCopy.close': 'Close the copy',
    'rxCopy.failed': 'The prescription copy could not be built.',
    'status.rxSource.inHouse': 'Tested here',
    'status.rxSource.customerSupplied': 'Brought in by the customer',
    'status.rxSource.transcribed': 'Copied from a written prescription',
    'status.rxSource.imported': 'Imported from previous records',
    'status.rxSource.ocrAssisted': 'Read from a photograph and checked',
    'receipt.fbrNumber': 'FBR invoice {number}',
    'receipt.policy': 'Returns policy {version}',
    // `DSP-001`: issuing a quote from the order screen, on the same lines. Three of these say what
    // the server refuses and why, because a dispenser who fills in a discount and loses it silently
    // is the outcome they exist to prevent.
    'newOrder.quote.heading': 'Quote this instead',
    'newOrder.quote.expiresLabel': 'Good until',
    'newOrder.quote.hint': 'A quote holds these prices until the date you set. No date is assumed, because nothing in the price book says how long a quote should last.',
    'newOrder.quote.issueButton': 'Raise a quote',
    'newOrder.quote.queueLabel': 'Quote for {customerName}',
    'newOrder.quote.customerRequired': 'A quote is raised for a named customer, so somebody can come back with it. Attach a customer first.',
    'newOrder.quote.expiryRequired': 'A quote needs the date it is good until.',
    'newOrder.quote.noDiscounts': 'A quote carries no discount. Clear the discounts above, or sell this as an order instead.',
    // `Repairs`, `REP-001` to `REP-004`, section 6.27. "Free" rather than a blank or a zero amount:
    // `REP-004` makes an adjustment given away the main source of goodwill and says an unrecorded
    // one is an invisible cost, so the word is the point.
    'repairs.heading': 'Repairs on the bench',
    'repairs.loadFailed': 'Could not load what is in for repair.',
    'repairs.stageLabel': 'Show',
    'repairs.stageOpen': 'Still in the shop',
    'repairs.stageReceived': 'Taken in, not priced',
    'repairs.stageClassified': 'Priced, not finished',
    'repairs.stageCompleted': 'Finished',
    'repairs.promised': 'Promised {date}',
    'repairs.notOurs': 'Not sold here',
    'repairs.free': 'Free',
    'repairs.charge': 'Charge {amount}',
    'repairs.shopCost': 'Cost us {amount}',
    'repairs.parts': 'Parts: {parts}',
    'repairs.openCustomer': 'Open the customer',
    'repairs.chargeLabel': 'What to charge ({currency})',
    'repairs.chargeRequired': 'Say what to charge. Nothing is not the same answer as zero, and a free adjustment is recorded as free.',
    'repairs.chargeBad': 'That is not an amount.',
    'repairs.classify': 'Price it',
    'repairs.outcomeLabel': 'What was done',
    'repairs.outcomeRequired': 'Say what was done. A blank records that something happened and not what.',
    'repairs.complete': 'Finish it',
    'repairs.queueClassifyLabel': 'A repair price',
    'repairs.queueCompleteLabel': 'A finished repair',
    'queue.subject.repair': 'The repair',
    'nav.repairs': 'Repairs',
    // `Recalls`, `APT-004` to `APT-006`, section 6.24. The queue a shopkeeper works through.
    // "Ran out" rather than "overdue" on a lapsed contact lens entry: `APT-006` calls it a safety
    // issue, and a customer still wearing lenses they have run out of is not merely late.
    'recalls.heading': 'Customers to bring back',
    'recalls.loadFailed': 'Could not load who is due.',
    'recalls.statusLabel': 'Show',
    'recalls.statusOpen': 'Still to do',
    'recalls.statusSent': 'Contacted',
    'recalls.statusWithheld': 'Not contacted, and why',
    'recalls.statusResponded': 'Answered',
    'recalls.statusConverted': 'Bought',
    'recalls.statusExpired': 'Too old to ask about',
    'recalls.due': 'Due {date}',
    // `{number}`, not `{count}`: this is which ask it is, an ordinal, and the catalogue requires a
    // plural form wherever `{count}` appears. "Ask number 2" has no plural to give, and naming an
    // ordinal `count` is what asked the rule a question it could not answer.
    'recalls.nudge': 'Ask number {number}',
    'recalls.lapsed': 'Their supply has run out.',
    'recalls.lapsedOn': 'Their supply ran out on {date}.',
    'recalls.withheldBecause': 'Not contacted: {reason}',
    // `RECALL_WITHHELD_REASONS`, which reached the screen as the code: a shopkeeper read
    // "Not contacted: no_consented_channel". Worded as what the shop can do about it, because that
    // is what the row is on this screen for.
    'recallWithheld.suppressed': 'they asked not to be contacted',
    'recallWithheld.noConsentedChannel': 'no email or in-app consent on file',
    'recalls.openCustomer': 'Open the customer',
    'recalls.channelLabel': 'How you contacted them about the {kind}',
    'recalls.channelUnset': 'Choose how',
    'recalls.channelEmail': 'Email',
    'recalls.channelInApp': 'In the app',
    'recalls.channelByHand': 'Phone call or in person',
    'recalls.channelRequired': 'Say how you contacted them. The recall report counts each way separately, so a phone call recorded as an email is a number nobody can use.',
    'recalls.markSent': 'Record that you contacted them',
    'recalls.markResponded': 'They answered',
    'recalls.markConverted': 'They bought',
    'recalls.queueSentLabel': 'A recall contact',
    'recalls.queueOutcomeLabel': 'A recall outcome',
    'queue.subject.recall': 'The recall',
    'recallKind.sightTest': 'Sight test',
    'recallKind.contactLens': 'Contact lens resupply',
    'recallKind.frameRefresh': 'New frame',
    'recallKind.warrantyExpiry': 'Warranty ending',
    'recallStatus.due': 'Due',
    'recallStatus.withheld': 'Not contacted',
    'recallStatus.sent': 'Contacted',
    'recallStatus.responded': 'Answered',
    'recallStatus.converted': 'Bought',
    'recallStatus.expired': 'Too old',
    'nav.recalls': 'Bring back',
    // `Notifications`, `MSG-005a`, section 6.26. The kind is the category and the server's summary
    // is the sentence, which names the till, the job or the amount.
    'notifications.heading': 'Needs attention',
    'notifications.tabsLabel': 'Which notifications to show',
    'notifications.tabUnread': 'To do',
    'notifications.tabRead': 'Seen',
    // "Cleared", not "Done with": the action beside each row is "Done with it", and two controls
    // whose accessible names differ by one word is a collision a person has to read twice. Found by
    // a test that clicked the wrong one, which is what a shopkeeper would have done.
    'notifications.tabDismissed': 'Cleared',
    'notifications.loadFailed': 'Could not load what needs attention.',
    'notifications.noneUnread': 'Nothing needs attention. Everything raised has been dealt with.',
    'notifications.branchWide': 'For the whole branch',
    'notifications.open': 'Open it',
    'notifications.markRead': 'Mark as seen',
    'notifications.dismiss': 'Done with it',
    'notifications.queueLabel': 'A notification',
    'queue.subject.notification': 'The notification',
    'notificationKind.jobOverdue': 'Job past its date',
    'notificationKind.stockOut': 'Out of stock',
    'notificationKind.tillVariance': 'Till does not balance',
    'notificationKind.disputeOpened': 'Dispute opened',
    'notificationKind.subscriptionPastDue': 'Subscription past due',
    'notificationKind.labQueryUnanswered': 'Lab is waiting on an answer',
    'notificationKind.aiQuotaExhausted': 'Mira has run out for this month',
    'notificationKind.reservationExpired': 'Reservation expired',
    'nav.notifications': 'Attention',
    // `Reports`, `RPT-01` to `RPT-11`, Appendix Z. One screen renders all of them from the listing's
    // own columns, so these are the words around a report rather than in one. A parameter's key and
    // the withheld field names are Appendix Z's and the server's, and are deliberately not
    // translated: they are field names, not sentences.
    'reports.heading': 'Reports',
    'reports.loadFailed': 'Could not load the list of reports.',
    'reports.runFailed': 'Could not run that report.',
    'reports.operational': 'Day to day',
    'reports.financial': 'Money',
    'reports.notBuiltYet': 'Not built yet',
    'reports.run': 'Run it',
    'reports.parametersRequired': {
        one: 'One field above still needs a value.',
        other: '{count} fields above still need a value.',
    },
    // `RPT-09` makes "what the figures are as of" part of the report, and this line was printing it
    // as the server's own field names and values: a shopkeeper read
    // `branchId: 01a148b3-e5e8-7680-8f7a-f01bc1ae42dc . asOf: 2026-10-17T21:07:29.137Z`.
    //
    // The branch is dropped rather than worded, because the shell already says which branch you are
    // in and a report run for it does not need to repeat the id. A key this build has no word for
    // still prints as itself, so a new report's own parameter is never silently hidden.
    'reports.asOf': 'Figures as at {when}',
    'reports.from': 'From {when}',
    'reports.to': 'To {when}',
    'reports.period': 'Period {period}',
    'reports.noRows': 'Nothing to report for what you asked.',
    'reports.noValue': 'None',
    'reports.rowCount': {
        one: 'One row',
        other: '{count} rows',
    },
    // `PriceImpact`, `PRC-012`. Figures are the server's: each lens line is priced under the book that
    // was in force on its sale date and again under the draft, before discounts and tax. Nothing here
    // is saved, and publishing does not depend on having looked.
    'priceImpact.heading': 'Preview a price change',
    'priceImpact.intro': 'See what a draft price book would have done to lens sales over the last {days} days. Each lens line is priced again under the book that applied on the day it was sold and under the draft, before discounts and tax. Nothing is saved or published.',
    'priceImpact.noRounding': 'This shop has no rounding rule set, so a preview cannot be priced. Set one in the shop settings first.',
    'priceImpact.versionLabel': 'Draft version id',
    'priceImpact.preview': 'Preview',
    'priceImpact.loadFailed': 'Could not preview that version.',
    'priceImpact.truncated': {
        one: 'Only the most recent line was read. These figures cover that line only.',
        other: 'Only the most recent {count} lines were read. These figures cover those lines only.',
    },
    'priceImpact.nothing': 'No lens sales in the last {days} days could be compared.',
    'priceImpact.compared': {
        one: 'One lens line compared.',
        other: '{count} lens lines compared.',
    },
    'priceImpact.totalsCaption': 'Effect of the draft, by lens',
    'priceImpact.product': 'Lens',
    'priceImpact.lines': 'Lines',
    'priceImpact.before': 'Before',
    'priceImpact.after': 'With the draft',
    'priceImpact.change': 'Change',
    'priceImpact.smallest': 'Smallest line change',
    'priceImpact.largest': 'Largest line change',
    'priceImpact.total': 'All lenses',
    'priceImpact.charged': 'Actually charged at the time, which can include manual changes:',
    'priceImpact.errorsHeading': 'Lines that could not be priced',
    'priceImpact.side.baseline': 'The old book',
    'priceImpact.side.candidate': 'The draft',
    'priceImpact.problem': {
        one: '{side}, one line: {message}',
        other: '{side}, {count} lines: {message}',
    },
    'priceImpact.skippedHeading': 'Lines left out',
    'priceImpact.skipped': {
        one: 'One line: {message}',
        other: '{count} lines: {message}',
    },
    // `LensChoice`, `DSS-001`, section 6.6. The list is the server's: it ranked, priced and gated
    // cost and margin, so these words say what it did rather than describing a decision made here.
    // `lensChoice.withheld` is followed by the server's own field names, which are not translated.
    'lensChoice.loadFailed': 'Could not load the lenses for this prescription.',
    'lensChoice.rankedByPrice': 'Cheapest first.',
    'lensChoice.rankedByMargin': 'Best margin first.',
    'lensChoice.withheld': 'Not shown to you at this branch:',
    'lensChoice.noneMakeable': 'No lens in the catalogue can be made for this prescription. Open the list below to see which rule stopped each one.',
    'lensChoice.choose': 'Choose {name}',
    'lensChoice.chosen': '{name}, chosen',
    'lensChoice.cost': 'Costs {amount}',
    'lensChoice.costUnquoted': 'No connected supplier has quoted this one',
    'lensChoice.unpricedHeading': 'Makeable, with no price in the book',
    // `DSP-006`: two or three options side by side, so the conversation is about trade-offs rather
    // than a single number. The total excludes order discount and tax per `decisions/0018`.
    'lensChoice.compare': {
        one: 'Compare (pick one more)',
        other: 'Compare these {count}',
    },
    'lensChoice.compareSelect': 'Compare {name}',
    'lensChoice.compareLimit': 'Three at a time. Untick one to swap it for another.',
    'lensChoice.compareHeading': 'Side by side',
    'lensChoice.compareExcludes': 'Before any discount on the whole order, and before tax. The bill will show those.',
    'lensChoice.compareRow': 'What',
    'lensChoice.compareLensPrice': 'Lenses, the pair',
    'lensChoice.compareOtherLines': 'Rest of the order',
    'lensChoice.compareTotal': 'Total',
    'lensChoice.compareDifference': 'Difference',
    'lensChoice.compareCheapest': 'Cheapest of these',
    'lensChoice.compareCost': 'Costs you',
    'lensChoice.compareMargin': 'Margin',
    'lensChoice.compareWarnings': 'Worth saying',
    'lensChoice.compareNoWarnings': 'Nothing',
    'lensChoice.compareRowChoose': 'Put on the order',
    // Appendix E's lens vocabularies, as choosers rather than boxes: `CAT-010`. The lesson `half_rim`
    // taught is that a second spelling typed by hand defeats every guard comparing against the first.
    'status.lensDesign.singleVision': 'Single vision',
    'status.lensDesign.bifocal': 'Bifocal',
    'status.lensDesign.trifocal': 'Trifocal',
    'status.lensDesign.progressive': 'Progressive',
    'status.lensDesign.occupational': 'Occupational',
    'status.lensDesign.degressive': 'Degressive',
    'status.lensMaterial.cr39': 'CR-39',
    'status.lensMaterial.trivex': 'Trivex',
    'status.lensMaterial.polycarbonate': 'Polycarbonate',
    'status.lensMaterial.highIndexPlastic': 'High index plastic',
    'status.lensMaterial.glass': 'Glass',
    'status.lensSurfacing.stock': 'Stock',
    'status.lensSurfacing.conventionalSurfaced': 'Surfaced to order',
    'status.lensSurfacing.freeformDigital': 'Freeform digital',
    // `CAT-012`'s eleven kinds.
    'status.treatment.hardCoat': 'Hard coat',
    'status.treatment.arCoating': 'Anti-reflective coating',
    'status.treatment.mirrorCoat': 'Mirror coat',
    'status.treatment.photochromic': 'Photochromic',
    'status.treatment.polarised': 'Polarised',
    'status.treatment.tintSolid': 'Solid tint',
    'status.treatment.tintGradient': 'Gradient tint',
    'status.treatment.uv': 'UV',
    'status.treatment.blueLightFilter': 'Blue light filter',
    'status.treatment.hydrophobic': 'Water repellent',
    'status.treatment.antiFog': 'Anti-fog',
    'status.treatment.edgePolish': 'Edge polish',
    // `LensCatalogue`, §6.4 `CAT-010`, `CAT-011`, `CAT-031`. Nothing could put a lens on file, so a
    // shop could sell frames and nothing else.
    'lensCatalogue.heading': 'Add a lens',
    'lensCatalogue.means': 'A lens on file can be priced, quoted and sent to a lab. Without one the lens chooser has nothing to offer and only a frame can be sold.',
    'lensCatalogue.notYours': 'Only an owner or a manager can change the catalogue. Ask one of them to add this lens.',
    'lensCatalogue.brand': 'Brand',
    'lensCatalogue.name': 'Lens name',
    'lensCatalogue.manufacturer': 'Manufacturer',
    'lensCatalogue.udi': 'Device identifier',
    'lensCatalogue.design': 'Design',
    'lensCatalogue.material': 'Material',
    'lensCatalogue.index': 'Index',
    'lensCatalogue.surfacing': 'How it is made',
    'lensCatalogue.corridor': 'Corridor lengths, in mm',
    'lensCatalogue.minFitHeight': 'Smallest fitting height, in mm',
    'lensCatalogue.diameters': 'Blank sizes, in mm',
    'lensCatalogue.rangeHeading': 'What it can make',
    'lensCatalogue.rangeMeans': 'The powers this lens is available in. A price book cannot cover a lens that does not say, so this is required.',
    'lensCatalogue.sphereMin': 'Lowest sphere',
    'lensCatalogue.sphereMax': 'Highest sphere',
    'lensCatalogue.cylinderMax': 'Highest cylinder, unsigned',
    'lensCatalogue.addMin': 'Lowest add',
    'lensCatalogue.addMax': 'Highest add',
    'lensCatalogue.brandAndNameRequired': 'A lens needs a brand and a name.',
    'lensCatalogue.indexRequired': 'A lens needs its index, as in 1.60.',
    'lensCatalogue.rangeRequired': 'Fill in the lowest and highest sphere and the highest cylinder. A price book cannot cover a lens with no range.',
    'lensCatalogue.save': 'Add this lens',
    'lensCatalogue.saved': '{brand} {name} is on file.',
    'lensCatalogue.queueLabel': 'Adding {brand} {name}',
    'lensCatalogue.queueSubject': 'the lens',
    // `TreatmentCatalogue`, §6.4 `CAT-012`. Zero rows everywhere meant every lens went out uncoated
    // as far as Lensmira knew, with nothing to price and nothing for the lab ticket to name.
    'treatmentCatalogue.heading': 'Add a coating or tint',
    'treatmentCatalogue.means': 'Coatings and tints are priced separately and stack. Until one is on file there is nothing to add to a lens, so an AR coat cannot be charged for.',
    'treatmentCatalogue.notYours': 'Only an owner or a manager can change the catalogue. Ask one of them to add this.',
    'treatmentCatalogue.kind': 'What it is',
    'treatmentCatalogue.brand': 'Brand',
    'treatmentCatalogue.grade': 'Grade',
    'treatmentCatalogue.colour': 'Colour',
    'treatmentCatalogue.density': 'Density, as a percentage',
    'treatmentCatalogue.colourRequired': 'A tint needs its colour, so a dispenser can tell one from another.',
    'treatmentCatalogue.save': 'Add this',
    'treatmentCatalogue.saved': '{kind} is on file.',
    'treatmentCatalogue.queueLabel': 'Adding {kind}',
    'treatmentCatalogue.queueSubject': 'the coating',
    // `PriceBooks`, §6.5 `PRC-010`, `PRC-009`. Nothing could create a book, so NO_PRICE_BOOK_IN_FORCE
    // was the answer to every lens path and the message named a route a shop could not reach.
    'priceBooks.heading': 'Lens prices',
    'priceBooks.means': 'A price book says what each lens costs a customer. Until one is published no lens can be quoted or sold, only a frame.',
    'priceBooks.notYours': 'Only an owner or a manager can change lens prices. Ask one of them.',
    'priceBooks.noneYet': 'No price book yet, so no lens can be sold. Add one below, put a price in it, then publish it.',
    'priceBooks.noVersions': 'nothing in it yet',
    'priceBooks.versionLine': 'v{n}, {status}, {rules} priced',
    'priceBooks.newVersion': 'Start a new version',
    'priceBooks.publish': 'Publish v{n}',
    'priceBooks.newBookHeading': 'Add a price book',
    'priceBooks.name': 'Name',
    'priceBooks.code': 'Short code',
    'priceBooks.currency': 'Currency',
    'priceBooks.addBook': 'Add this book',
    'priceBooks.nameAndCodeRequired': 'A price book needs a name and a short code.',
    'priceBooks.newRuleHeading': 'Price a lens',
    'priceBooks.newRuleMeans': 'One price per lens, for the pair. Leave the power range empty to price it at every power, or fill it in to charge more for stronger lenses.',
    'priceBooks.lens': 'Lens',
    'priceBooks.pickALens': 'Pick a lens',
    'priceBooks.amount': 'Price for the pair',
    'priceBooks.bandMin': 'From this power',
    'priceBooks.bandMax': 'To this power',
    'priceBooks.label': 'What the bill should say',
    'priceBooks.addRule': 'Add to {code} v{n}',
    'priceBooks.ruleFieldsRequired': 'Pick a lens, give a price, and say what the bill should call it.',
    'priceBooks.amountInvalid': 'A price is a number, as in 2500.',
    'priceBooks.saved': 'Saved.',
    'priceBooks.queueBook': 'Adding the book {name}',
    'priceBooks.queueVersion': 'Starting a new version of {code}',
    'priceBooks.queueRule': 'Pricing {label} in {currency}',
    'priceBooks.queuePublish': 'Publishing v{n}',
    'priceBooks.queueSubject': 'the price book',
    // `LensTreatments`, §6.4 `CAT-013`. `treatment_compatibility` had no writer anywhere and the read
    // is an allow-list, so a priced coating could never be sold because no lens admitted it.
    'lensTreatments.heading': 'Which coatings go on which lens',
    'lensTreatments.means': 'A coating can only be sold on a lens that admits it. Until you say so here, nothing can be added to a lens, however it is priced.',
    'lensTreatments.notYours': 'Only an owner or a manager can change the catalogue. Ask one of them.',
    'lensTreatments.lens': 'Lens',
    'lensTreatments.pickALens': 'Pick a lens',
    'lensTreatments.treatment': 'Coating or tint',
    'lensTreatments.pickATreatment': 'Pick one',
    'lensTreatments.canItGoOn': 'Can it go on this lens',
    'lensTreatments.yes': 'Yes',
    'lensTreatments.no': 'No',
    'lensTreatments.yesMeans': 'Price this coating in the price book too, or the next publish will be blocked.',
    'lensTreatments.reason': 'Why not',
    'lensTreatments.reasonRequired': 'Say why it cannot go on. A dispenser told only "not available" rings the lab.',
    'lensTreatments.pickBoth': 'Pick a lens and a coating.',
    'lensTreatments.noneYet': 'Nothing recorded for this lens yet, so no coating can be put on it.',
    'lensTreatments.save': 'Record this',
    'lensTreatments.change': 'Change this',
    'lensTreatments.saved': 'Recorded.',
    'lensTreatments.queueLabel': 'Recording which coatings go on this lens',
    'lensTreatments.queueChange': 'Changing which coatings go on this lens',
    'lensTreatments.queueSubject': 'the coating rule',
    // `FrameImages`, §6.4 `CAT-004`. The catalogue had no image column and the product had no object
    // storage at all, so a shop searching the system had to walk to the display wall instead.
    'frameImages.heading': 'Pictures of this frame',
    'frameImages.means': 'A picture is what lets somebody find this frame without walking to the wall. Take one from the front and one from the side.',
    'frameImages.frame': 'Which frame',
    'frameImages.pickAFrame': 'Pick a frame',
    'frameImages.noneYet': 'No pictures yet.',
    'frameImages.loading': 'Loading',
    'frameImages.angle': 'Which angle',
    'frameImages.anglePlaceholder': 'front, side, folded',
    'frameImages.choose': 'Add a picture',
    'frameImages.uploading': 'Sending the picture. Stay on this screen until it finishes.',
    'frameImages.saved': 'Picture added.',
    'frameImages.uploadFailed': 'The picture did not go. Try it again.',
    'frameImages.removeFailed': 'The picture is still there. Try again.',
    'frameImages.remove': 'Remove',
    'frameImages.positionOnly': 'Picture {n}',
    'frameImages.alt': 'This frame, {angle}',
    'frameImages.altNoAngle': 'This frame, picture {n}',
    'lensChoice.whyNot': {
        one: 'Why one other lens is not here',
        other: 'Why {count} other lenses are not here',
    },
    'newOrder.line.noPrescriptionForLens': 'No prescription is attached, so there is no lens to choose and no powers to price one by. Attach one first.',
    // Mira, §6.32. `AI-022` is the surface, `AI-001` is the acceptance screen.
    'mira.launch': 'Ask Mira',
    'mira.shortcut': 'Ctrl+K',
    'mira.region': 'Mira',
    'mira.heading': 'Ask Mira',
    'mira.close': 'Close',
    'mira.closeHint': 'Escape closes this and puts you back where you were.',
    'mira.context.customer': 'Working on customer {name}.',
    'mira.context.order': 'Working on order {number}.',
    'mira.context.orderNoNumber': 'Working on an order that has no invoice number yet.',
    'mira.context.job': 'Working on job {number}.',
    'mira.context.section': 'Working in {section}.',
    'mira.noNames': "Mira is never sent a customer's name or contact details. Leave them, and any prescription values, out of your question.",
    'mira.task.label': 'What do you need?',
    'mira.task.help': 'Help using Lensmira',
    'mira.task.reportQuery': 'A question about the shop',
    'mira.task.messageDraft': 'Draft a message',
    'mira.task.rxOcr': 'Read a prescription slip',
    'mira.task.catalogueCapture': "Read a frame's markings",
    'mira.task.priceSheetParse': 'Read a supplier price list',
    'mira.input.label': 'Your question',
    'mira.occasion.label': 'What is the message about?',
    'mira.occasion.orderReady': 'Their order is ready',
    'mira.occasion.balanceDue': 'They owe a balance',
    'mira.occasion.recall': 'They are due a check-up',
    'mira.occasion.apology': 'An apology',
    'mira.channel.label': 'Where will you send it?',
    'mira.channel.whatsapp': 'WhatsApp',
    'mira.channel.sms': 'SMS',
    'mira.channel.email': 'Email',
    'mira.image.heading': 'The photograph',
    'mira.image.privacy': 'Only the part you draw a box around is sent. Keep the name and phone number outside the box.',
    'mira.image.choose': 'Choose a photograph',
    'mira.image.cropHint': 'Drag a box over the part you want read. Everything outside the box stays on this device.',
    'mira.image.surface': 'The photograph. Drag a box over the part to send.',
    'mira.image.edgeLeft': 'Left edge of the box, percent across',
    'mira.image.edgeTop': 'Top edge of the box, percent down',
    'mira.image.edgeRight': 'Right edge of the box, percent across',
    'mira.image.edgeBottom': 'Bottom edge of the box, percent down',
    'mira.image.use': 'Use this part of the photograph',
    'mira.image.useBlocked': 'Drag a box over the part to send first.',
    'mira.image.keepsTooMuch': 'That box still keeps almost the whole photograph. Draw it around the values only, leaving the name and telephone number outside it.',
    'mira.image.discard': 'Discard this photograph',
    'mira.image.preparing': 'Preparing the photograph.',
    'mira.image.failed': 'That photograph could not be prepared on this device. Try another, or type the values in.',
    'mira.image.ready': {
        one: '{count} photograph is ready to send.',
        other: '{count} photographs are ready to send.',
    },
    'mira.image.item': 'Photograph {position}',
    'mira.image.remove': 'Remove',
    'mira.image.full': 'That is as many photographs as one request carries. Remove one to add another.',
    'mira.image.needed': 'Choose a photograph and draw a box on it before asking.',
    'mira.ask': 'Ask',
    'mira.asking': 'Asking Mira.',
    'mira.cancelAsk': 'Cancel the request',
    'mira.cancelled': 'The request was cancelled before it was sent. No credit was spent.',
    'mira.askLabel': 'Question for Mira',
    'mira.answerLabel': 'Your decision on what Mira proposed',
    'mira.quota.loading': 'Checking what is left this month.',
    'mira.quota.unknown': 'The balance for this month could not be loaded.',
    'mira.quota.available': {
        one: '{count} request left this month.',
        other: '{count} requests left this month.',
    },
    'mira.quota.exhausted': 'No requests are left this month for this.',
    'mira.quota.unquoted': "This is not part of the shop's plan.",
    'mira.offline': 'Mira needs a connection and is not available while this device is offline. Nothing was queued.',
    'mira.manual': 'Everything Mira does can be done by hand. The sections on the left still work.',
    'mira.reason.degraded': 'Mira has no requests left this month, so there is no answer. Do this by hand.',
    'mira.reason.unavailable': 'Mira has no answer for this right now. Do this by hand.',
    'mira.reason.notAllowed': "This shop's data settings do not allow this to be sent to an AI provider. Do this by hand.",
    'mira.reason.unquoted': "This is not part of the shop's plan, so there is no answer. Do this by hand.",
    'mira.reason.refused': 'The request was refused: {reason}.',
    'mira.reason.unreadable': "Mira's answer could not be read. Do this by hand.",
    'mira.reason.noRoom': 'This device has no room left to store the request, so nothing was sent. Make space in the browser, or do this by hand.',
    'mira.read.heading': 'What Mira says about this reading',
    'mira.read.hint': 'These are for you to read. Nothing here is saved anywhere.',
    'mira.read.readable': 'Could it be read at all',
    'mira.read.script': 'Script',
    'mira.read.writing': 'Printed or handwritten',
    'mira.read.notes': "Mira's note",
    'mira.read.legibilityOd': 'Right eye (OD), how clearly it read',
    'mira.read.legibilityOs': 'Left eye (OS), how clearly it read',
    'mira.read.rawText': 'Every marking Mira read off the frame',
    'mira.read.unparsedRow': 'A line Mira could not place',
    'mira.read.warning': 'Something to look at',
    'mira.proposal.heading': 'Mira proposed this',
    'mira.proposal.notSaved': 'Nothing is saved or changed until you decide each value below and confirm.',
    'mira.proposal.source': 'From {model}, version {version}.',
    'mira.proposal.confidence': 'Mira is {percent}% sure.',
    'mira.field.mira': "Mira's value",
    'mira.field.accept': 'Accept',
    'mira.field.edit': 'Edit',
    'mira.field.reject': 'Reject',
    'mira.field.editLabel': 'Your value',
    'mira.field.answer': "Mira's answer",
    'mira.field.undecided': 'Not decided yet.',
    'mira.field.rejected': 'Rejected. This will be left empty.',
    'mira.field.invalid.dioptre': 'Not a power in steps of 0.25. Edit it or reject it.',
    'mira.field.invalid.axis': 'An axis is a whole number from 1 to 180. Edit it or reject it.',
    'mira.field.invalid.number': 'Enter a number, or reject this one.',
    'mira.field.invalid.empty': 'Enter a value, or reject this one.',
    'mira.rx.od.sphere': 'Right eye (OD), sphere',
    'mira.rx.od.cylinder': 'Right eye (OD), cylinder',
    'mira.rx.od.axis': 'Right eye (OD), axis',
    'mira.rx.od.add': 'Right eye (OD), add',
    'mira.rx.os.sphere': 'Left eye (OS), sphere',
    'mira.rx.os.cylinder': 'Left eye (OS), cylinder',
    'mira.rx.os.axis': 'Left eye (OS), axis',
    'mira.rx.os.add': 'Left eye (OS), add',
    'mira.rejectAll': 'Reject all',
    'mira.confirm': 'Confirm my decisions',
    'mira.confirm.blocked': {
        one: '{count} value still needs your decision.',
        other: '{count} values still need your decision.',
    },
    'mira.confirm.ready': 'Every value has a decision.',
    'mira.recording': 'Recording your decisions.',
    'mira.done.accepted': 'Recorded: you accepted what Mira proposed.',
    'mira.done.edited': 'Recorded: you changed some of what Mira proposed.',
    'mira.done.rejected': 'Recorded: you rejected what Mira proposed.',
    'mira.done.notSaved': 'No prescription, price or stock figure has been changed. Enter the values yourself on the ordinary screen.',
    'mira.done.blocked': 'Your decision was NOT recorded: {reason}. It needs attention.',
    'mira.done.takeToRx': 'Open prescription entry with these values',
    'mira.done.otherContext': 'Something else is open now, so these values cannot be carried across. Open the customer they were read for and ask again.',
    'mira.again': 'New request',
    // `Purchasing`, `SUP-001` to `SUP-005` and `INV-006`, sections 6.28 and 6.29. A shopkeeper's
    // words for a supplier order: "book in a delivery" rather than "goods receipt", and "they
    // confirmed it" rather than "acknowledge". The tolerance hint says out loud that the number is
    // asked for every delivery because the product has nowhere to keep it, which is the one thing
    // about this screen a person cannot work out from looking at it.
    'purchasing.heading': 'Orders to suppliers',
    'purchasing.loadFailed': 'Could not load what is on order.',
    'purchasing.statusLabel': 'Show',
    'purchasing.statusOpen': 'Still open',
    'purchasing.expected': 'Due {date}',
    'purchasing.noDatePromised': 'No date promised',
    'purchasing.overdue': 'Past its date',
    'purchasing.backorder': {
        one: 'One piece still owed',
        other: '{count} pieces still owed',
    },
    'purchasing.send': 'Send it to the supplier',
    'purchasing.acknowledge': 'They confirmed it',
    'purchasing.cancel': 'Cancel it',
    'purchasing.bookIn': 'Book in a delivery',
    'purchasing.receiptCaption': 'What arrived against {number}',
    'purchasing.col.item': 'Item',
    'purchasing.col.ordered': 'Ordered',
    'purchasing.col.alreadyIn': 'In already',
    'purchasing.col.arrived': 'Arrived now',
    'purchasing.arrivedFor': 'How many of {description} arrived',
    'purchasing.locationLabel': 'Where it goes',
    'purchasing.locationUnset': 'Choose where',
    'purchasing.locationWarehouse': '{name} (central warehouse)',
    'purchasing.locationNotSelling': '{name} (not sold from)',
    'purchasing.locationRequired': 'Say where the delivery goes. Stock booked into the wrong place is stock the counter cannot find.',
    'purchasing.noLocations': 'This branch has no stock location, so there is nowhere to book a delivery into. Add one before receiving goods.',
    // Said instead of the sentence above when the shelf read itself failed. The two used to share
    // one sentence, which sent a shopkeeper off to add a stock location the branch already has.
    'purchasing.locationsFailed': "Could not read this branch's stock locations, so there is nowhere to book the delivery into yet. Try the screen again in a moment.",
    'purchasing.toleranceLabel': 'Over-delivery allowed, in basis points',
    'purchasing.toleranceHint': 'One hundred basis points is one per cent of what was ordered. Lensmira cannot yet keep this as your shop policy, so it is asked every delivery and the number you type is the one this delivery is judged by.',
    'purchasing.toleranceRequired': 'Say how much over-delivery to allow. There is no number to assume on your behalf.',
    'purchasing.toleranceInvalid': 'Over-delivery allowed is a whole number of basis points, zero or more.',
    'purchasing.nothingReceived': 'Say how many arrived on at least one line.',
    'purchasing.quantityInvalid': 'How many arrived is a whole number, zero or more.',
    'purchasing.recordReceipt': 'Record the delivery',
    'purchasing.receiptsSoFar': 'Deliveries so far: {numbers}',
    'purchasing.invoices': 'Supplier invoices: {detail}',
    'purchasing.queueSendLabel': 'A purchase order sent',
    'purchasing.queueAckLabel': 'A supplier confirmation',
    'purchasing.queueCancelLabel': 'A cancelled purchase order',
    'purchasing.queueReceiveLabel': 'A delivery against {number}',
    'queue.subject.purchaseOrder': 'The purchase order',
    'nav.purchasing': 'Suppliers',
    'purchaseOrderStatus.draft': 'Not sent yet',
    'purchaseOrderStatus.sent': 'Sent',
    'purchaseOrderStatus.acknowledged': 'Confirmed by the supplier',
    'purchaseOrderStatus.partiallyReceived': 'Part delivered',
    'purchaseOrderStatus.received': 'Delivered',
    'purchaseOrderStatus.invoiceMatched': 'Invoice agrees',
    'purchaseOrderStatus.closed': 'Closed',
    'purchaseOrderStatus.cancelled': 'Cancelled',
    'purchaseOrderStatus.disputed': 'In dispute',
    'purchaseOrderStatus.resolved': 'Dispute settled',
    // `Outbox`, `MSG-005` and `OPS-142`, section 6.17. The failure is the point of the screen, so
    // the words for the eight states keep four distinctions apart: sent is not delivered, a bounce is
    // the address and a failure is the attempt, consent saying no is not the reason going away. The
    // buttons are verbs because a button that says "Failed" is a state, not an act.
    'outbox.heading': 'Messages to customers',
    'outbox.loadFailed': 'Could not load the messages.',
    'outbox.statusLabel': 'Show',
    'outbox.statusOpen': 'Still to deal with',
    'outbox.scheduled': 'To go {when}',
    'outbox.attempts': {
        one: 'Tried once',
        other: 'Tried {count} times',
    },
    'outbox.carriedRx': 'This one carried prescription values.',
    'outbox.failedBecause': 'It did not go: {reason}',
    'outbox.notSentBecause': 'Not sent: {reason}',
    'outbox.openCustomer': 'Open the customer',
    'outbox.reasonLabel': 'What went wrong',
    'outbox.reasonRequired': 'Say what went wrong. A failure with no reason tells the next person nothing, and somebody is waiting for this message.',
    'outbox.queueLabel': 'A message update',
    'queue.subject.message': 'The message',
    'outbox.move.queued': 'Put it back in the queue',
    'outbox.move.sending': 'I am sending it now',
    'outbox.move.sent': 'It went',
    'outbox.move.delivered': 'They got it',
    'outbox.move.bounced': 'The address is wrong',
    'outbox.move.failed': 'It did not go',
    'outbox.move.suppressed': 'Do not send it',
    'outbox.move.cancelled': 'No longer needed',
    'nav.outbox': 'Messages',
    'messageKind.orderConfirmation': 'Order confirmed',
    'messageKind.depositReceipt': 'Deposit receipt',
    'messageKind.jobStatusChange': 'Job update',
    'messageKind.delayNotification': 'Running late',
    'messageKind.readyForCollection': 'Ready to collect',
    'messageKind.uncollectedReminder': 'Still with us',
    'messageKind.recall': 'Time to come back',
    'messageKind.appointmentReminder': 'Appointment reminder',
    'messageKind.warrantyExpiry': 'Warranty ending',
    'messageKind.storefrontOrderUpdate': 'Online order update',
    'messageChannel.email': 'Email',
    'messageChannel.inApp': 'In the app',
    'messageStatus.queued': 'Waiting to go',
    'messageStatus.sending': 'Going now',
    'messageStatus.sent': 'Sent',
    'messageStatus.delivered': 'Arrived',
    'messageStatus.bounced': 'Address is wrong',
    'messageStatus.failed': 'Did not go',
    'messageStatus.suppressed': 'Not sent',
    'messageStatus.cancelled': 'No longer needed',
    'suppressionReason.suppressed': 'they asked not to be contacted',
    'suppressionReason.noConsent': 'they have not agreed to this way of being contacted',
    'suppressionReason.rxOptInWithdrawn': 'the shop stopped allowing prescription values in messages',
    'suppressionReason.customerErased': 'they asked to be erased',
    // `SendMessage`, `MSG-002` to `MSG-004`, section 6.17. There is no free-text box: what can be
    // said is what the shop has written, so these words are about choosing and filling a template
    // rather than composing anything. The placeholder names in the form are the template's own and
    // are deliberately not translated.
    'sendMessage.heading': 'Send them a message',
    'sendMessage.loadFailed': 'Could not load the messages the shop can send.',
    'sendMessage.noTemplates': 'The shop has not written any messages yet, so there is nothing to send. An owner writes them in the shop settings.',
    'sendMessage.whatToSay': 'What to tell them',
    'sendMessage.chooseOne': 'Choose a message',
    'sendMessage.option': '{kind}, by {channel}',
    'sendMessage.locales': 'The shop has this one written in more than one language ({locales}). Theirs is used when there is one.',
    'sendMessage.rxWarning': 'This message carries prescription values, which you are typing in yourself. Check them against the prescription before sending.',
    'sendMessage.valuesMissing': 'Fill in {names}, or the message goes out with the gaps still in it.',
    'sendMessage.send': 'Send it',
    'sendMessage.queueLabel': 'A message to a customer',
    // `MessageTemplates`, `MSG-002` and `MSG-003`, section 6.17. The owner's screen, and the only
    // way into the table: a shop with no templates can send nothing at all. Saving and turning on
    // are separate words because they are separate decisions.
    'templates.heading': 'What the shop says',
    'templates.loadFailed': 'Could not load the wording.',
    'templates.none': 'The shop has written nothing yet, so no message can be sent. Write one below and turn it on.',
    'templates.placeholders': 'You can use: {names}',
    'templates.on': 'Being sent',
    'templates.off': 'Not being sent',
    'templates.subjectFor': 'Subject line for {kind} in {locale}',
    'templates.bodyFor': 'What it says, for {kind} in {locale}',
    'templates.bodyRequired': 'Write what it says. A template with no words is not wording.',
    'templates.unknownPlaceholder': '{names} is not something Lensmira can fill in, so this message could never be sent. Use one of the names above.',
    'templates.rxWarning': 'This wording includes prescription values. It can only be sent if the shop has allowed that, and whoever sends it types the values in themselves.',
    'templates.save': 'Save the wording',
    'templates.turnOn': 'Start sending it',
    'templates.turnOff': 'Stop sending it',
    'templates.newHeading': 'Write another',
    'templates.newKind': 'What it is for',
    'templates.chooseKind': 'Choose what it is for',
    'templates.newChannel': 'How it goes out',
    'templates.newLocale': 'Which language',
    'templates.localeEn': 'English',
    'templates.localeUr': 'Urdu',
    'templates.newSubject': 'Subject line',
    'templates.newBody': 'What it says',
    'templates.saveNew': 'Save it',
    'templates.queueSaveLabel': 'Message wording',
    'templates.queueOnLabel': 'A message turned on',
    'templates.queueOffLabel': 'A message turned off',
    'queue.subject.template': 'The wording',
    'nav.templates': 'Wording',
    // `Promotions` and `condition-builder`, `PRM-001` to `PRM-007` and Appendix X, section 6.25. A
    // promotion is a rule, so the screen builds one: the effect words carry the arithmetic (a
    // percentage off against a price set), and the condition rows are Appendix X's own field names,
    // which are deliberately not translated because they are field names and not sentences.
    'promotions.heading': 'Offers',
    'promotions.loadFailed': 'Could not load the offers.',
    'promotions.window': 'From {from} to {to}',
    'promotions.windowInclusive': 'Both days count, so an offer ending Sunday applies all Sunday.',
    'promotions.notWithOthers': 'Not combined with other offers',
    'promotions.needsCoupon': 'Needs a coupon',
    'promotions.floor': 'Stops at a margin of {amount}',
    'promotions.withdrawnBecause': 'Stopped: {reason}',
    'promotions.publish': 'Start it',
    'promotions.withdraw': 'Stop it',
    'promotions.revise': 'Change it',
    'promotions.reasonLabel': 'Why you are stopping it',
    'promotions.reasonRequired': 'Say why you are stopping it. This is what explains a discount a customer got last week and cannot get today.',
    'promotions.newHeading': 'A new offer',
    'promotions.reviseHeading': 'A new version of this offer',
    'promotions.conditionKept': 'This offer-s conditions were written in a shape this screen cannot show, so they are carried over to the new version exactly as they are.',
    'promotions.labelLabel': 'What to call it',
    'promotions.labelRequired': 'Give the offer a name. It is what the till shows.',
    'promotions.kindLabel': 'What it does',
    'promotions.percentLabel': 'How many per cent',
    'promotions.amountLabel': 'How much ({currency})',
    'promotions.appliesToLabel': 'Off what',
    'promotions.productIdLabel': 'Free product id',
    'promotions.categoryLabel': 'Free category',
    'promotions.oneOfTwo': 'Fill in one of these two, not both.',
    'promotions.treatmentIdLabel': 'Free treatment id',
    'promotions.fromTierLabel': 'Upgrade from',
    'promotions.toTierLabel': 'Upgrade to',
    'promotions.startsOnLabel': 'First day',
    'promotions.endsOnLabel': 'Last day',
    'promotions.windowRequired': 'Give the offer a first and a last day. An offer with no end is a discount nobody decided to stop.',
    'promotions.priorityLabel': 'Which goes first',
    'promotions.priorityWhole': 'Which goes first is a whole number. Higher goes first.',
    'promotions.stackableLabel': 'Can combine with other offers',
    'promotions.requiresCouponLabel': 'Only with a coupon',
    'promotions.floorLabel': 'Do not go below this margin ({currency})',
    'promotions.floorMeans': 'Left blank there is no floor, which is not the same as zero. The figure is what the line must still make after the offer, before tax.',
    'promotions.saveNew': 'Save the offer',
    'promotions.saveRevision': 'Save the new version',
    'promotions.cancelRevision': 'Leave it as it is',
    'promotions.cannotSave': 'Could not save the offer.',
    'promotions.queueNewLabel': 'A new offer',
    'promotions.queueReviseLabel': 'A new version of an offer',
    'promotions.queuePublishLabel': 'An offer started',
    'promotions.queueWithdrawLabel': 'An offer stopped',
    'queue.subject.promotion': 'The offer',
    'nav.promotions': 'Offers',
    'promotions.effect.percentOff': '{percent} per cent off',
    'promotions.effect.amountOff': '{amount} off',
    'promotions.effect.fixedPrice': 'Price set to {amount}',
    'promotions.effect.freeItem': '{what} free',
    'promotions.effect.freeTreatment': '{what} free',
    'promotions.effect.upgrade': '{from} upgraded to {to}',
    'promotionEffect.percentOff': 'Take a percentage off',
    'promotionEffect.amountOff': 'Take an amount off',
    'promotionEffect.fixedPrice': 'Set the price',
    'promotionEffect.freeItem': 'Give an item free',
    'promotionEffect.freeTreatment': 'Give a treatment free',
    'promotionEffect.upgrade': 'Upgrade to a better tier',
    'appliesTo.line': 'Each line it matches',
    'appliesTo.order': 'The whole sale, once',
    'appliesTo.category': 'Each line in the category',
    'promotionStatus.draft': 'Not started',
    'promotionStatus.published': 'Running',
    'promotionStatus.withdrawn': 'Stopped',
    // The condition builder. Appendix X.1 is "not a scripting language, a data structure", so this
    // asks for rows rather than offering a box to write one in.
    'condition.legend': 'When it applies',
    'condition.allMustMatch': 'Every line below has to be true. Leave them all out and it always applies.',
    'condition.field': 'What to look at, line {number}',
    'condition.anySale': 'Any sale',
    'condition.operator': 'How to compare, line {number}',
    'condition.value': 'Value, line {number}',
    'condition.valueList': 'Values separated by commas, line {number}',
    'condition.upper': 'Up to, line {number}',
    'condition.moneyIn': 'The amount is in {currency}, as you would write it on a receipt.',
    'condition.chooseYesNo': 'Choose',
    'condition.yes': 'Yes',
    'condition.no': 'No',
    'condition.add': 'Add a line',
    'condition.remove': 'Remove this line',
    // `ApplyOffers`, `PRM-007` and section 15.16, section 6.25. The suppressed list is the point:
    // X.3 step 5 says the till shows every applied rule and every suppressed rule with its reason,
    // because "it did not apply" is not an answer to a customer asking why.
    'applyOffers.heading': 'Offers on this sale',
    'applyOffers.couponLabel': 'Coupon code',
    'applyOffers.couponOptional': 'Leave it blank if they have no coupon. An offer that needs one will say so below.',
    'applyOffers.apply': 'Check the offers',
    'applyOffers.noRounding': 'This shop has not set a rounding rule, so prices cannot be worked out again. An owner sets it in the shop settings.',
    'applyOffers.queueLabel': 'Offers on a sale',
    'queue.subject.offerApply': 'The offers',
    'applyOffers.tookOff': 'Came off: {amount}',
    'applyOffers.noneApplied': 'No offer matched this sale.',
    'applyOffers.applied': '{label} applied.',
    'applyOffers.appliedOverridden': '{label} applied, with a manager allowing it below the floor.',
    'applyOffers.suppressed': '{label} did not apply: {reason} {explanation}',
    'applyOffers.couponTaken': 'Coupon {code} accepted.',
    'applyOffers.couponRefused': 'Coupon {code} was not accepted: {reason}',
    'applyOffers.couponUnknown': 'there is no such coupon',
    'suppression.conditionFalse': 'this sale does not qualify.',
    'suppression.nonStackable': 'another offer on the same line does not combine.',
    'suppression.marginFloor': 'it would take the line below the margin the shop set.',
    'suppression.disabled': 'the offer names a product or treatment that no longer exists.',
    // `IssueCoupon`, `PRM-005`, section 6.25. The code is the shop's own, because the spec gives no
    // format and nothing can generate one without inventing a rule a shop would be stuck with. The
    // two caps are hidden on a single-use coupon, which is the only kind the server refuses them on.
    'coupon.issueFor': 'Issue a coupon for {label}',
    'coupon.issue': 'Issue it',
    'coupon.issued': 'Coupon {code} issued. Write it down: nothing in Lensmira lists coupons yet.',
    'coupon.codeLabel': 'The code customers will quote',
    'coupon.codeIsYours': 'You choose the code, and it is matched exactly, so EID10 and eid10 are two different coupons.',
    'coupon.codeRequired': 'Give the coupon a code. It is the only way a customer can present it.',
    'coupon.kindLabel': 'How many times it can be used',
    'coupon.startsOnLabel': 'First day it works',
    'coupon.expiresOnLabel': 'Last day it works',
    'coupon.expiryRequired': 'Give the coupon a last day. One with no end is a discount nobody decided to stop.',
    'coupon.singleUseMeans': 'A single-use coupon is redeemed once, so there is no limit to set.',
    'coupon.maxRedemptionsLabel': 'Total redemptions allowed',
    'coupon.perCustomerLabel': 'Redemptions allowed per customer',
    'coupon.capsOptional': 'Leave either blank for no limit.',
    'coupon.capWhole': 'A limit is a whole number of redemptions.',
    'coupon.campaignLabel': 'Campaign (optional)',
    'coupon.queueLabel': 'A coupon',
    'queue.subject.coupon': 'The coupon',
    'couponKind.singleUse': 'Once only',
    'couponKind.multiUse': 'As many times as you allow',
    // Added with the review fixes: four sentences a screen needs in order to refuse rather than
    // queue a write the server will certainly reject, since a refused write stops the whole queue.
    'applyOffers.alreadyTaken': 'The offers have been checked on this sale. Checking again would be refused, because a sale takes its offers once.',
    'promotions.cannotReviseFloor': 'This offer has a lowest margin set and you are not allowed to see it, so changing the offer would remove it. Ask someone who can see cost and margin at this branch.',
    'promotions.floorWithheld': 'Has a lowest margin you cannot see',
    'promotions.windowPassed': 'Its last day has passed, so it can no longer be started.',
    'promotions.everyBranchLabel': 'Runs at every branch',
    'promotions.everyBranchMeans': 'Left off, the offer runs only at this branch. Turned on, it runs at every branch of the shop that uses the same currency.',
    // `Stock`, `INV-001` to `INV-009`, section 6.10. Read-only: `stock_view` is the one stock row
    // every role holds, and receiving, adjusting and counting take rows that fewer do. A negative
    // quantity is printed as it is, because section 12 case 47 is the shelf and the record
    // disagreeing and that is the one number a screen must not tidy away.
    'stock.heading': 'What is on the shelf',
    'stock.locationLabel': 'Which shelf',
    'stock.chooseLocation': 'Choose a shelf',
    'stock.locationWarehouse': '{name} (central warehouse)',
    'stock.locationNotSelling': '{name} (not sold from)',
    'stock.noLocations': 'This branch has no shelves set up, so there is nothing to show.',
    'stock.loadFailed': 'Could not load the stock.',
    // What this screen calls itself inside a surface sentence, as its own key.
    'stock.what': 'the shelf',
    'stock.noneHere': 'Nothing is on this shelf.',
    'stock.onHand': {
        one: 'One on hand',
        other: '{count} on hand',
    },
    'stock.expires': 'Expires {date}',
    'stock.tray': 'Tray {tray}',
    'stock.worth': 'Worth {amount}',
    'stock.unknownItem': 'Item not in the catalogue',
    'stock.history': 'Show its history',
    'stock.loadingHistory': 'Loading the history.',
    'stock.movementsFailed': 'Could not load the history.',
    'stock.noHistory': 'Nothing has moved yet.',
    'stock.reverses': 'Reverses an earlier movement',
    'ownership.owned': 'Bought by the shop',
    'ownership.consignment': 'On consignment',
    'ownership.onLoan': 'On loan',
    'movementKind.receipt': 'Came in',
    'movementKind.sale': 'Sold',
    'movementKind.transferOut': 'Sent to another branch',
    'movementKind.transferIn': 'Came from another branch',
    'movementKind.adjustment': 'Adjusted by hand',
    'movementKind.reversal': 'Reversal',
    'stockFlag.negative': 'The record says less than nothing is here. Count it.',
    'nav.stock': 'Shelf',
    // `VoidSale`, section 6.7 `POS-005`, section 17 `OPS-105`, `POS-007`. A panel on an order,
    // beside the offers one. Three decisions are encoded in these words rather than in the code:
    //
    // A void and a refund are named as two operations, with what each is for, because they have two
    // permissions and two journals and the person deciding has to pick. The panel never offers the
    // refund, and on a collected sale it points at the returns desk.
    //
    // What a void does is said in three sentences, one per thing it touches: the money, the shelf
    // and the lab. The third is the one the product does not do, so the words are the only place it
    // can be said. Nothing in `voidOrder` cancels a job.
    //
    // Every refusal the service can give has a sentence here, because a refused write stops the
    // whole queue (`OFF-001a`). `voidSale.feeInvalid` carries no figure and `voidSale.policyUnusable`
    // carries the configured value, because the first is a typing slip and the second is a settings
    // correction nobody can make without knowing what is in there.
    'voidSale.heading': 'Void this sale',
    'voidSale.whatItDoesMoney': 'Voiding gives back everything paid on this sale, less anything the shop keeps. The payments stay on the record and a matching entry is written against each one, so the drawer reads as taken and then given back.',
    'voidSale.whatItDoesGoods': 'Anything taken off the shelf for this sale goes back on it. Lenses made to this prescription were never on the shelf, so nothing comes back for them.',
    'voidSale.whatItDoesJobs': 'A job already with the lab is not stopped by this. Cancel the job as well, or the lab carries on making the glasses and nobody can collect them.',
    'voidSale.notARefund': 'A void is for a sale that never happened: a deposit on glasses nobody collected. A refund is for a sale that did, where the customer had the glasses and brought them back.',
    'voidSale.reasonLabel': 'Why you are voiding it',
    'voidSale.reasonRequired': 'Say why you are voiding this sale. It is what the override report shows somebody who was not there.',
    'voidSale.feeLegend': 'What the shop keeps',
    'voidSale.keepNothing': 'Keep nothing, give it all back',
    'voidSale.keepAmount': 'Keep an amount I decide',
    'voidSale.keepPolicy': 'Keep the shop policy share, {percent} per cent of what was paid',
    'voidSale.feeLabel': 'How much to keep ({currency})',
    'voidSale.feeInvalid': 'That is not an amount of money. Write it as you would on a receipt.',
    'voidSale.feeNegative': 'The shop cannot keep less than nothing. Choose keep nothing instead.',
    'voidSale.noPolicy': 'The shop has configured no cancellation policy, so there is no share to apply. Decide an amount here instead, or let it all go back.',
    'voidSale.policyUnusable': 'The shop policy is set to {percent}, which is not a percentage between 0 and 100. Decide an amount here, and have the setting corrected.',
    'voidSale.needsDrawer': 'Cash has to come out of an open drawer. Open a cash session for this terminal first.',
    'voidSale.needsTerminal': 'This till has no terminal, and a void is recorded against one. Sign in at a terminal first.',
    'voidSale.submit': 'Void the sale',
    'voidSale.queueLabel': 'A void on {number}',
    'voidSale.queueLabelNoNumber': 'A void on a sale with no invoice number yet',
    'queue.subject.void': 'The void',
    'voidSale.alreadyVoid': 'This sale has already been voided.',
    'voidSale.wasCollected': 'The customer has had these, so the sale became revenue and voiding it is refused. Giving money back on a collected sale is a refund, which is a different operation with its own permission. Return the line instead.',
    'voidSale.gaveBack': 'Given back: {amount}',
    'voidSale.kept': 'Kept by the shop: {amount}',
    'voidSale.keptNothing': 'The shop kept nothing.',
    'voidSale.done': 'The sale is void. Nothing was deleted: the payments, the journals and the invoice are all still on the record.',
    // `CommissionSchemes`, `COM-001` to `COM-004` and `COM-007`, section 6.24. The owner's screen for
    // how staff are paid, and the configuration half only: `COM-005`'s behaviour metrics have no
    // source anywhere in the product, so a period close would be a manager typing every reading
    // (`later_todo.md` 22) and there is no honest screen for it yet.
    //
    // The words encode four decisions. **Nothing has a default**, because the spec names no rounding
    // mode, no earning trigger, no basis, no discount treatment for a rate and no remake treatment,
    // so every "Choose ..." option is a blank that refuses rather than a value this screen picked,
    // and each one has a sentence saying why it is being asked. **A rate is a percentage to the
    // shopkeeper** and thousandths of a percent on the wire, so no copy here mentions thousandths.
    // **Paying on margin is not offered**, and the sentence says it is a missing permission rather
    // than a product decision. And **the status words are about force, not about drafting**: "In
    // force" is what a shopkeeper asks about a pay rule, where a promotion is "Running".
    'commission.heading': 'How staff are paid',
    'commission.loadFailed': 'Could not load the pay rules.',
    'commission.otherBranch': 'Set up at {branch}',
    'commission.anotherBranch': 'another branch',
    'commission.roundsBy': 'Rounding: {mode}',
    'commission.earnedWhen': 'Earned: {when}',
    'commission.creditOrder': 'Credited in order: {roles}',
    'commission.noRules': 'No kind of line has a rule, so this pays nothing.',
    'commission.rulesCaption': 'What {label} pays',
    'commission.col.lineKind': 'Kind of line',
    'commission.col.pays': 'Pays on',
    'commission.col.figure': 'Figure',
    'commission.figureFlat': '{amount} a line',
    // Two sentences, not "{rate} per cent of {base}": `{base}` is a word out of `status-labels.ts`
    // and it has to read as a dropdown option too, so dropping it mid-sentence gave "2.5 per cent of
    // What the customer was charged". Appendix S bans building a sentence from parts, and a hole
    // filled by a label is the same mistake with the seam hidden.
    'commission.figureRetail': '{rate} per cent. Paid on: {base}',
    'commission.figureMargin': '{rate} per cent of the margin',
    'commission.lensTierPair': '{tier}: {amount}',
    'commission.tierSteps': {
        one: 'A higher rate above one threshold',
        other: 'A higher rate above {count} thresholds',
    },
    'commission.tierScopeFact': 'Threshold measured for: {scope}',
    'commission.tierMeasureFact': 'Threshold measured on: {measure}',
    'commission.remakeNoReduce': 'No remake reduces pay.',
    'commission.remakeReduces': 'A remake reduces pay. Causes: {causes}. Taken back: {deduction}',
    'commission.metricsCarried': {
        one: 'One behaviour bonus, which this screen carries over and does not change',
        other: '{count} behaviour bonuses, which this screen carries over and does not change',
    },
    'commission.withdrawnBecause': 'Replaced: {reason}',
    'commission.marginLocked': 'This pays on margin, which this screen cannot write, so it cannot be changed or put in force here.',
    'commission.publish': 'Put it in force',
    'commission.publishReplaces': 'Version {number} is in force. Putting this one in force would replace it.',
    'commission.revise': 'Change it',
    'commission.noWithdrawal': 'A draft cannot be retired: nothing in Lensmira withdraws one, so it stays on this list until a version supersedes it.',
    'commission.cannotReviseUnknown': 'This version uses {what}, which this screen cannot show, so changing it here would drop it. Ask whoever set it up.',
    'commission.newHeading': 'A new set of pay rules',
    'commission.reviseHeading': 'A new version of these pay rules',
    'commission.labelLabel': 'What to call it',
    'commission.labelRequired': 'Give the pay rules a name. It is what a commission statement names.',
    'commission.roundingLabel': 'Rounding',
    'commission.chooseRounding': 'Choose how it rounds',
    'commission.roundingRequired': 'Choose how it rounds. A percentage of a price leaves a fraction of a paisa and somebody has to decide which way it goes.',
    'commission.roundingMeans': 'The spec names no rule for that fraction, so every scheme carries its own and there is nothing to fall back on.',
    'commission.earnsOnLabel': 'When it is earned',
    'commission.chooseEarnsOn': 'Choose when it is earned',
    'commission.earnsOnRequired': 'Choose when it is earned.',
    'commission.earnsOnMeans': 'On a layaway the sale and the last payment can be months apart, so this is not the same question twice.',
    'commission.attributionLegend': 'Who is credited, in order',
    'commission.attributionMeans': 'The first role on this list that the sale recorded is the one credited.',
    'commission.attributionRow': '{position}. {role}',
    'commission.attributionRemove': 'Take {role} out of the order',
    'commission.attributionChoose': 'Who to credit next',
    'commission.chooseRole': 'Choose a role',
    'commission.attributionAdd': 'Add them to the order',
    'commission.attributionUseSpec': 'Use the order the spec gives',
    'commission.attributionRequired': 'Name at least one role to credit. A scheme that credits nobody pays nobody.',
    'commission.attributionDuplicate': 'Each role is credited once, so it appears once in the order.',
    'commission.rulesLegend': 'What each kind of line pays',
    'commission.rulesMeans': "The kind has to match the sale line's own category letter for letter. A kind that matches nothing earns nothing, and nothing reports it.",
    'commission.marginNotOffered': 'Paying on margin is not offered here. It needs permission to see cost at this branch, Lensmira cannot tell this screen whether you hold it, and a refused write stops every till.',
    'commission.noRulesYet': 'No kind of line has a rule yet, so these rules would pay nothing.',
    'commission.ruleAdd': 'Add a kind of line',
    'commission.ruleRemove': 'Remove rule {number}',
    'commission.ruleKindLabel': 'Kind of line, rule {number}',
    'commission.ruleKindRequired': 'Give every rule a kind of line. A rule with no kind matches nothing.',
    'commission.ruleKindDuplicate': 'Two rules name the kind {kind}. One kind has one rule, or the second quietly replaces the first.',
    'commission.ruleBasisLabel': 'What it pays on, rule {number}',
    'commission.chooseBasis': 'Choose what it pays on',
    'commission.ruleBasisRequired': 'Choose what rule {number} pays on.',
    'commission.ruleAmountLabel': 'Amount a line ({currency}), rule {number}',
    'commission.ruleRateLabel': 'Rate per cent, rule {number}',
    'commission.ruleBaseLabel': 'Which price, rule {number}',
    'commission.chooseBase': 'Choose which price',
    'commission.ruleBaseRequired': 'Choose which price rule {number} pays on.',
    'commission.ruleBaseMeans': "The spec says only a percentage of retail, so whether a line discount comes off the pay as well is the shop's decision.",
    'commission.lensTierAdd': 'Add a lens tier to rule {number}',
    'commission.lensTierNameLabel': 'Lens tier, rule {number} tier {tierNumber}',
    'commission.lensTierAmountLabel': 'Amount for that tier ({currency}), rule {number} tier {tierNumber}',
    'commission.lensTierMeans': 'A lens tier is whatever this shop calls one: the spec does not define them, so the name has to match what a sale line carries.',
    'commission.lensTierRequired': 'Rule {number} pays per lens tier and names none, so it would pay nothing.',
    'commission.lensTierNameRequired': 'Name every lens tier.',
    'commission.lensTierDuplicate': 'Two tiers named {tier} in one rule. One name, one amount.',
    'commission.amountRequired': 'Fill in every amount.',
    'commission.amountNegative': 'An amount is never negative. Write what is paid, as a positive figure.',
    'commission.amountTooBig': 'That amount is larger than anything the server will take.',
    'commission.rateRange': 'A rate is between 0 and 999.999 per cent, with at most three decimal places.',
    'commission.tierLegend': 'Thresholds and a higher rate',
    'commission.tierOnLabel': 'Pay a higher rate above a threshold',
    'commission.tierMeans': 'Once a threshold is cleared the higher rate applies to every qualifying line in the whole period, not only to the part above it. It is the only way the spec defines.',
    'commission.tierScopeLabel': 'Whose total is measured',
    'commission.chooseTierScope': 'Choose whose total',
    'commission.tierScopeRequired': 'Choose whose total the threshold is measured against.',
    'commission.tierMeasureLabel': 'What is measured',
    'commission.chooseTierMeasure': 'Choose what is measured',
    'commission.tierMeasureRequired': 'Choose what the threshold measures.',
    'commission.tierStepAdd': 'Add a threshold',
    'commission.tierStepRemove': 'Remove threshold {number}',
    'commission.tierThresholdLabel': 'Threshold ({currency}), step {number}',
    'commission.tierThresholdRequired': 'Give every threshold a figure.',
    'commission.tierInclusiveLabel': 'At the threshold itself, step {number}',
    'commission.chooseInclusive': 'Choose',
    'commission.inclusiveAtOrAbove': 'At the threshold or above it',
    'commission.inclusiveAbove': 'Only above the threshold',
    'commission.tierInclusiveMeans': 'The spec says above and nothing more, so each step says whether the figure itself counts. A box that started unticked would be an answer nobody gave.',
    'commission.tierInclusiveRequired': 'Say whether the threshold itself counts, on step {number}.',
    'commission.tierRateLabel': 'Rate above it, per cent, step {number}',
    'commission.tierStepsRequired': 'A higher rate needs at least one threshold.',
    'commission.tierRising': 'Thresholds go up, each one higher than the one before it.',
    'commission.remakeLegend': 'What a remake does to pay',
    'commission.remakeMeans': "A remake the shop caused is always on the statement. Whether it reduces pay is the shop's choice, so there is no default.",
    'commission.remakeCausesLegend': 'Causes that reduce pay',
    'commission.remakeDeductionLabel': 'How much it takes back',
    'commission.chooseDeduction': 'Choose how much',
    'commission.remakeDeductionRequired': 'Choose how much a remake takes back.',
    'commission.remakeAmountLabel': 'Amount taken back ({currency})',
    'commission.remakeAmountRequired': 'Fill in the amount taken back.',
    'commission.remakeCausesEmpty': 'No cause reduces pay, so nothing would ever be taken back. Tick the causes, or set it to take nothing back.',
    'commission.saveNew': 'Save the pay rules',
    'commission.saveRevision': 'Save the new version',
    'commission.cancelRevision': 'Leave them as they are',
    'commission.cannotSave': 'Could not save the pay rules.',
    'commission.queueNewLabel': 'A new set of pay rules',
    'commission.queueReviseLabel': 'A new version of the pay rules',
    'commission.queuePublishLabel': 'Pay rules put in force',
    // Singular, unlike every other word for this screen, because `queue.write.blocked` is
    // "{subject} was NOT saved" and "The pay rules was NOT saved" is not a sentence. The subject is
    // the only place the plural breaks agreement, so it is the only place that says scheme.
    'queue.subject.commissionScheme': 'The pay scheme',
    'nav.commission': 'Pay rules',
    // The enum vocabulary, which `status-labels.ts` owns the tables for. Each word has to read both
    // as a choice in a dropdown and inside a sentence on a list row, which is why none of them is a
    // mid-sentence fragment and why the row sentences name a field and then its word.
    'commissionStatus.draft': 'Not in force',
    'commissionStatus.published': 'In force',
    'commissionStatus.withdrawn': 'Replaced',
    'commissionRounding.halfUp': 'Half a paisa rounds up',
    'commissionRounding.down': 'Fractions are dropped',
    'commissionEarnsOn.sale': 'At the sale',
    'commissionEarnsOn.fullPayment': 'When it is paid in full',
    'commissionBasis.flat': 'A fixed amount a line',
    'commissionBasis.retail': 'A percentage of the price',
    'commissionBasis.margin': 'A percentage of the margin',
    'commissionBasis.lensTier': 'A fixed amount per lens tier',
    'commissionBase.list': 'The price before any discount',
    'commissionBase.net': 'What the customer was charged',
    'commissionTierScope.person': 'Each person on their own',
    'commissionTierScope.branch': 'The branch as a whole',
    'commissionTierMeasure.retail': 'What was sold',
    'commissionTierMeasure.margin': 'The margin made',
    'commissionDeduction.none': 'Nothing',
    'commissionDeduction.fixed': 'A fixed amount',
    'commissionDeduction.lineCommission': 'Whatever was earned on that line',
    'attributionRole.dispenseCreator': 'Who started the sale',
    'attributionRole.paymentTaker': 'Who took the payment',
    'attributionRole.fitter': 'Who fitted',
    'attributionRole.handover': 'Who handed over',
    // Appendix E's ten remake causes. `shop_rx_error` and `shop_measurement_error` are the two
    // `COM-007` names, and they are worded as the shop's own mistake rather than softened, because
    // that is what the statement has to say to the person whose pay it reduces.
    'remakeCause.labError': 'The lab got it wrong',
    'remakeCause.labBreakage': 'The lab broke it',
    'remakeCause.shopRxError': 'The shop typed the prescription wrong',
    'remakeCause.shopMeasurementError': 'The shop measured wrong',
    'remakeCause.prescriberChange': 'The prescriber changed it',
    'remakeCause.nonAdapt': 'The customer could not adapt',
    'remakeCause.frameWarranty': 'A frame warranty claim',
    'remakeCause.coatingDefect': 'A faulty coating',
    'remakeCause.customerDamage': 'The customer damaged it',
    'remakeCause.insurerChange': 'The insurer changed it',
    // `RaisePurchaseOrder`, `SUP-001` and `SUP-002`, section 6.29 and section 15.6 step 2. The
    // accountant's half of purchasing, in a shopkeeper's words: "raise an order" rather than
    // "purchase order", "date promised" rather than "expected delivery". Three of these sentences
    // encode decisions the screen cannot hide. The supplier is an id because the product has no
    // supplier read at all. The currency is asked for because there is no supplier read either, and
    // every cost on the form is parsed at that currency's decimal places. And the catalogue item is
    // explained rather than labelled, because leaving it blank is a delivery that gets signed for at
    // the dock and never reaches a shelf.
    'raiseOrder.heading': 'Raise an order with a supplier',
    'raiseOrder.supplierLabel': 'Supplier',
    // `later_todo.md` 53: the form asked for the supplier's UUID and said so, because there was no
    // supplier read. There is one now, so these replace the hint that apologised for it.
    // §6.19's supplier list, which nothing in the product could add to or correct until now.
    'nav.suppliersSetup': 'Supplier list',
    'suppliers.heading': 'Your suppliers',
    'suppliers.loadFailed': 'Could not read your suppliers.',
    'suppliers.none': 'No supplier is set up yet. Add the first one below.',
    'suppliers.name': 'Their name',
    'suppliers.code': 'A short code',
    'suppliers.country': 'Country, two letters',
    'suppliers.currency': 'The currency they bill in',
    'suppliers.channel': 'How orders reach them',
    'suppliers.channelNone': 'Not set',
    'suppliers.channelMeans': 'An order to a supplier with no way of reaching them cannot be sent. Lensmira records which way you used; it does not send it for you yet.',
    'suppliers.leadTime': 'Usual days to deliver',
    'suppliers.phone': 'Phone',
    'suppliers.email': 'Email',
    'suppliers.noChannelWarning': 'No way to send an order set',
    'suppliers.stopped': 'no longer used',
    'suppliers.addOne': 'Add a supplier',
    'suppliers.add': 'Add them',
    'suppliers.save': 'Save the change',
    'suppliers.cancel': 'Leave it as it is',
    'suppliers.change': 'Change {name}',
    'suppliers.stop': 'Stop using {name}',
    'suppliers.resume': 'Start using {name} again',
    'suppliers.nameRequired': 'Give them a name.',
    'suppliers.codeRequired': 'Give them a short code. It is how an order says who it is for, and two suppliers cannot share one.',
    'suppliers.countryRequired': 'Say which country they are in, as two letters.',
    'suppliers.currencyRequired': 'Say which currency they bill in.',
    'suppliers.currencyShape': 'A currency is three letters, like PKR or USD.',
    'suppliers.leadTimeShape': 'Days to deliver is a whole number, and cannot be negative.',
    'suppliers.queueAddLabel': 'Adding {name}',
    'suppliers.queueChangeLabel': 'A change to {name}',
    'suppliers.queueStopLabel': 'Stopping {name}',
    'suppliers.queueResumeLabel': 'Starting {name} again',
    'queue.subject.supplier': 'The supplier',
    // Appendix E's `supplier_channel`. `whatsapp_image` keeps its word because a row imported or
    // seeded can hold it and `ARC-020` says a value you have never heard of still renders.
    'supplierChannel.api': 'Straight into their system',
    'supplierChannel.portal': 'Typed into their website',
    'supplierChannel.emailStructured': 'Email',
    'supplierChannel.whatsappImage': 'WhatsApp (not available in this version)',
    'raiseOrder.supplierUnchosen': 'Not chosen',
    'raiseOrder.suppliersFailed': 'Could not read your suppliers, so there is nothing to choose from. Try again in a moment.',
    'raiseOrder.noSuppliers': 'No supplier is set up yet. Add one under Suppliers before raising an order.',
    'raiseOrder.currencyLabel': 'Currency of this order',
    'raiseOrder.currencyHint': 'Every cost below is read in this currency. Your supplier bills in theirs, which Lensmira cannot yet show you, so a line taking the list price in another currency comes back refused.',
    'raiseOrder.expectedLabel': 'Date promised',
    'raiseOrder.expectedHint': 'This is the date Lensmira compares against to call an order late, so an order with no date promised is an order nobody can chase.',
    'raiseOrder.skuLabel': 'Supplier code, line {position}',
    'raiseOrder.descriptionLabel': 'What it is, line {position}',
    'raiseOrder.quantityLabel': 'How many, line {position}',
    'raiseOrder.fromPriceListLabel': 'Use the supplier price list, line {position}',
    'raiseOrder.priceListMeans': 'The price in force on the day is taken from this supplier list, so no cost is typed on this line.',
    'raiseOrder.costLabel': 'Cost each ({currency}), line {position}',
    'raiseOrder.ownershipLabel': 'Who owns it, line {position}',
    'raiseOrder.productIdLabel': 'Catalogue item, line {position}',
    'raiseOrder.productIdMeans': 'Left blank, the delivery can be signed for and never reach a shelf: nothing says which frame or lens arrived, so no stock is booked in for it.',
    'raiseOrder.addLine': 'Add another line',
    'raiseOrder.save': 'Raise the order',
    'raiseOrder.draftMeans': 'This writes a draft. Sending it to the supplier is a separate step on the suppliers screen.',
    'raiseOrder.raised': 'Raised as {number}.',
    'raiseOrder.raisedUnnumbered': 'The order was raised. Its number is on the suppliers screen.',
    'raiseOrder.supplierRequired': 'Say which supplier this order goes to.',
    'raiseOrder.currencyRequired': 'Say which currency this order is in. There is no currency to assume on your behalf.',
    'raiseOrder.linesRequired': 'Put at least one line on the order.',
    'raiseOrder.skuAndDescriptionRequired': 'Every line needs a supplier code and a description of what it is.',
    'raiseOrder.quantityWhole': 'How many is a whole number of pieces, one or more.',
    'raiseOrder.costInvalid': 'Cost each is an amount, for example 1250.00.',
    'raiseOrder.queueLabel': 'A new purchase order',
    'nav.raiseOrder': 'Order stock',
    // `SupplierInvoice`, `INV-006` and Appendix W.5, section 6.10 and section 15.6 step 4. The
    // three-way match, which is three independent statements compared: what was ordered, what
    // arrived, and what the supplier billed. Four of these sentences are the ones that matter.
    // `nothingPrefilled` says why no box is filled in, because a helpful default here would make a
    // price or quantity variance unreachable. `tolerancesHint` says the three allowances are asked
    // every invoice because nothing stores them, and that the match cannot be worked out again
    // later. `willBePending` says a part-delivered order is not judged yet, which is a result and
    // not a refusal. And `costWithheld` says an invoice cannot be entered by somebody who cannot see
    // the order's costs, which is about the reader rather than about the order.
    'supplierInvoice.heading': 'Supplier invoices',
    'supplierInvoice.loadFailed': 'Could not load what is on order.',
    'supplierInvoice.noneEntered': 'No invoice entered',
    'supplierInvoice.entered': {
        one: 'One invoice entered',
        other: '{count} invoices entered',
    },
    'supplierInvoice.open': 'Open it',
    'supplierInvoice.orderLoadFailed': 'Could not load the order.',
    'supplierInvoice.costWithheld': 'You cannot see the costs on this order, and a supplier invoice is a cost document, so it cannot be entered here. Ask the owner for cost visibility at this branch.',
    'supplierInvoice.forOrder': 'Invoices against {number}',
    'supplierInvoice.orderTotal': 'The order was placed at {amount}',
    'supplierInvoice.queryReason': 'Queried: {reason}',
    'supplierInvoice.matchAgain': 'Match it again',
    'supplierInvoice.cannotMatchYet': 'The order is not in a state that can be matched, so this invoice cannot be judged again now.',
    'supplierInvoice.resolve': 'Close the query',
    'supplierInvoice.resolveNeedsManager': 'A query is closed by the owner or a manager, not from this desk.',
    'supplierInvoice.notMatchable': 'This order is {status}, so there is nothing for an invoice to be matched against.',
    'supplierInvoice.willBePending': 'Part of this order has still to arrive, so nothing is judged yet: the invoice is recorded and waits, and you match it again once the rest comes in.',
    'supplierInvoice.numberLabel': 'Invoice number on the paper',
    'supplierInvoice.invoicedOnLabel': 'Invoice date',
    'supplierInvoice.dueOnLabel': 'Payment due',
    'supplierInvoice.noReceiptLines': 'Nothing has been booked in against this order yet, so there is no line to bill.',
    'supplierInvoice.lineNotOnOrder': 'A line no longer on the order',
    'supplierInvoice.orderedAndReceived': 'Ordered {ordered}, arrived {received}',
    'supplierInvoice.agreedCost': 'Agreed at {amount} each',
    'supplierInvoice.wasOverReceipt': 'More arrived than was ordered',
    'supplierInvoice.billedQuantityLabel': 'How many billed, {what}',
    'supplierInvoice.billedCostLabel': 'Billed at each ({currency}), {what}',
    'supplierInvoice.nothingPrefilled': 'Nothing above is filled in for you. The point of the match is that what the supplier billed is read off their paper rather than copied from what you ordered.',
    'supplierInvoice.foreignMeans': 'This order is in {invoiceCurrency} and your books are in {baseCurrency}, so the rate, its date and what the invoice was booked at are all required.',
    'supplierInvoice.baseTotalLabel': 'Booked at, in total ({currency})',
    'supplierInvoice.rateLabel': 'Exchange rate used',
    'supplierInvoice.rateOnLabel': 'Rate date',
    'supplierInvoice.overReceiptLabel': 'Allowed over-delivery, in basis points',
    'supplierInvoice.invoiceQuantityLabel': 'Allowed over-billing, in basis points',
    'supplierInvoice.priceToleranceLabel': 'Allowed price difference, in basis points',
    'supplierInvoice.tolerancesHint': 'One hundred basis points is one per cent. Lensmira cannot yet keep these as your shop policy, so they are asked every invoice: the numbers you type are the ones this invoice is judged by, and the match cannot be worked out again later from the order alone.',
    'supplierInvoice.enter': 'Enter the invoice',
    'supplierInvoice.numberRequired': 'Type the invoice number printed on the paper.',
    'supplierInvoice.invoicedOnRequired': 'Say the date on the invoice.',
    'supplierInvoice.tolerancesRequired': 'All three allowances are whole basis points, zero or more. There is nothing to assume on your behalf.',
    'supplierInvoice.quantityWhole': 'How many billed is a whole number of pieces, one or more. Leave a line empty if it was not billed.',
    'supplierInvoice.costInvalid': 'Billed at each is an amount, for example 1250.00.',
    'supplierInvoice.onePricePerLine': 'Two deliveries of the same ordered line are billed at two different prices. The match compares one price per line, so bill them at one.',
    'supplierInvoice.nothingBilled': 'Fill in at least one line the supplier billed.',
    'supplierInvoice.rateRequired': 'The rate, its date and what the invoice was booked at are all required for an order in another currency.',
    'supplierInvoice.rateInvalid': 'The rate is a decimal number with at most eight places, for example 280.15.',
    'supplierInvoice.baseTotalInvalid': 'What the invoice was booked at is an amount, for example 141075.00.',
    'supplierInvoice.baseTotalPositive': 'What the invoice was booked at is more than nothing.',
    'supplierInvoice.verdict': 'The match says: {verdict}',
    'supplierInvoice.noFindings': 'Nothing disagreed.',
    'supplierInvoice.findingPieces': {
        one: '{what}, one piece',
        other: '{what}, {count} pieces',
    },
    'supplierInvoice.findingAmount': '{what}, {amount}',
    'supplierInvoice.findingBlocks': 'Opens a query',
    'supplierInvoice.findingWithin': 'Within what you allowed',
    'supplierInvoice.findingNoted': 'Recorded only',
    'supplierInvoice.queueEnterLabel': 'A supplier invoice against {number}',
    'supplierInvoice.queueMatchLabel': 'A second look at invoice {number}',
    'supplierInvoice.queueResolveLabel': 'A closed query on {number}',
    'queue.subject.supplierInvoice': 'The supplier invoice',
    'nav.supplierInvoices': 'Supplier invoices',
    // Appendix W.5's verdicts. "Not judged yet" rather than "pending", because a part-delivered
    // order waiting for the rest of a delivery is not a problem and a shopkeeper should not read it
    // as one.
    'matchVerdict.matched': 'Everything agrees',
    'matchVerdict.pending': 'Not judged yet',
    'matchVerdict.query': 'A query is open',
    // `INV-006`'s six disagreements. Each one names which two of the three statements disagree,
    // because what to do about it differs: a short shipment is a claim on the supplier, over-billing
    // is never paid, under-billing means goods are not invoiced yet.
    'matchFinding.shortShipment': 'Less arrived than was ordered',
    'matchFinding.overShipment': 'More arrived than was ordered',
    'matchFinding.invoiceQuantityOver': 'Billed for more than arrived',
    'matchFinding.invoiceQuantityUnder': 'Billed for less than arrived',
    'matchFinding.priceVariance': 'Billed at a different price than agreed',
    'matchFinding.unknownInvoiceLine': 'Billed for something never ordered',
    // What the invoice row itself holds. Kept apart from the verdict above: "not judged yet" on an
    // invoice is a standing state somebody has to come back to, where a verdict is one answer.
    'invoiceMatchStatus.unmatched': 'Not judged yet',
    'invoiceMatchStatus.matched': 'Agrees with the order',
    'invoiceMatchStatus.queried': 'Queried',
    // `LoanFrames`, `REP-005`, section 6.27, with section 15.13 steps 5 and 6 and its failure
    // branch. A loan frame is the shop's own frame, out of the building on a customer's face while
    // their own is mended, against a deposit and a date.
    //
    // Four decisions live in these words rather than in the code.
    //
    // The list is only the late ones, because `GET /loan-frames/overdue` is the only collection the
    // server offers and it filters to overdue before answering. A shop whose three loans are all
    // still in date would read an empty screen as nothing on loan, so the screen says what the list
    // is instead of letting somebody infer it.
    //
    // The deposit is a figure that is recorded and never cash into a drawer. The service is explicit:
    // `payment` is keyed to an order, so a loan deposit is not yet a drawer or a ledger entry. Words
    // that read like taking a payment would have a shopkeeper reaching for the till and a cash count
    // that never balances.
    //
    // The stock does not move, although section 15.13 step 5 says "It moves to `on loan`, not
    // `sold`". Nothing in this vertical moves it, so the shelf keeps counting a frame that is on a
    // customer's face, and the issue panel says so rather than implying the `INV-003` state change.
    //
    // And a write-off keeps somebody's money. Its words are about a forfeit and a reason, not about
    // tidying a record away, because that is the decision being taken.
    'loanFrames.heading': 'Loan frames past their date',
    'loanFrames.onlyOverdue': 'This is only the loans whose date has passed. A frame lent out and still in date is not in any list the counter can ask for.',
    'loanFrames.loadFailed': 'Could not load the loan frames.',
    'loanFrames.noneOverdue': 'No loan frame is past its date.',
    'loanFrames.openFailed': 'Could not open that loan frame.',
    'loanFrames.answerUnreadable': 'The loan was recorded, but the answer could not be read back.',
    'loanFrames.openIt': 'Open it',
    // Neutral, because the standalone panel holds any loan not in the list above: the one just
    // issued, which is not overdue, and one that dropped off the list on being returned.
    'loanFrames.thisLoan': 'This loan frame',
    'loanFrames.wasDue': 'Was due {date}',
    'loanFrames.issuedOn': 'Went out {date}',
    'loanFrames.dueOn': 'Expected back {date}',
    'loanFrames.cameBack': 'Came back {date}',
    'loanFrames.deposit': 'Deposit {amount}',
    'loanFrames.frameNotInCatalogue': 'This frame is no longer in the catalogue',
    'loanFrames.chasesSoFar': {
        one: 'Chased once',
        other: 'Chased {count} times',
    },
    'loanFrames.noChases': 'Nothing has been chased yet.',
    'loanFrames.openCustomer': 'Open the customer',
    'loanFrames.condition': 'How it came back: {note}',
    'loanFrames.wasWrittenOff': 'Written off: {reason}',
    // Both halves, because what the shop kept and what it owes back are separate questions to the
    // person holding the receipt. The server did the subtraction.
    'loanFrames.depositSplit': 'Kept {kept}, and {back} goes back.',
    'loanFrames.alreadyClosed': 'This loan is already back or written off, so there is nothing left to record against it.',
    'loanFrames.channelLabel': 'How they were chased',
    'loanFrames.channelUnset': 'Say how',
    'loanFrames.channelRequired': 'Say how they were chased.',
    'loanFrames.chaseNoteLabel': 'What happened',
    'loanFrames.recordChase': 'Record the chase',
    'loanFrames.conditionLabel': 'How it came back',
    'loanFrames.bookBack': 'Book it back in',
    // The order, not the length of the ladder: `validateLoanFrame` enforces chase before write-off
    // and deliberately invents no number for how many chases are enough.
    'loanFrames.chaseFirst': "Section 15.13's failure branch is a chase ladder, then a write-off. Record a chase before writing this one off.",
    'loanFrames.reasonLabel': 'Why it is being written off',
    'loanFrames.reasonRequired': 'A write-off needs a reason.',
    'loanFrames.retainedLabel': 'Deposit the shop keeps ({currency})',
    'loanFrames.retainedRequired': 'Say how much of the deposit the shop keeps. Zero returns all of it.',
    'loanFrames.retainedMeans': 'There is no shop policy for this, so the figure is yours. Zero returns the whole deposit.',
    'loanFrames.retainedOverDeposit': 'The shop cannot keep more than the deposit it took.',
    'loanFrames.writeOff': 'Write it off',
    'loanFrames.queueIssueLabel': 'A loan frame going out',
    'loanFrames.queueChaseLabel': 'A chase for a loan frame',
    'loanFrames.queueReturnLabel': 'A loan frame coming back',
    'loanFrames.queueWriteOffLabel': 'A loan frame write-off',
    'loanFrames.issueHeading': 'Lend a frame',
    'loanFrames.stockUnmoved': 'A frame on loan still counts as on the shelf, so take it off the count by hand.',
    'loanFrames.repairLabel': 'Which repair it is for',
    'loanFrames.repairUnset': 'Choose a repair',
    // The condition and the promised date, because that is what a repair is known by at the counter:
    // neither the repair nor the loan carries a customer name.
    'loanFrames.repairOption': '{condition}, promised {date}',
    'loanFrames.repairRequired': 'Choose the repair this loan is for.',
    'loanFrames.noRepairs': 'Nothing is in for repair, so there is nothing to lend a frame against.',
    'loanFrames.remakesElsewhere': 'A loan against a remake cannot be raised here yet.',
    'loanFrames.shelfLabel': 'Which shelf it comes off',
    'loanFrames.shelfUnset': 'Choose a shelf',
    'loanFrames.shelfWarehouse': '{name} (central warehouse)',
    'loanFrames.shelfNotSelling': '{name} (not sold from)',
    'loanFrames.frameLabel': 'Which frame goes out',
    'loanFrames.frameUnset': 'Choose a frame',
    'loanFrames.frameOption': '{frame}, lot {lot}, {ownership}',
    'loanFrames.frameRequired': 'Choose the frame going out.',
    'loanFrames.noFrames': 'No frame on this shelf can be lent.',
    'loanFrames.depositLabel': 'Deposit taken ({currency})',
    'loanFrames.depositRequired': 'Say what deposit was taken. Zero is an answer; blank is not.',
    'loanFrames.depositMeans': 'Recorded against the loan, not taken into the drawer. Zero records a loan with no deposit.',
    'loanFrames.returnByLabel': 'Expected back on',
    'loanFrames.returnByRequired': 'Say when the frame is expected back.',
    'loanFrames.returnByTooSoon': 'Pick a later day than today: a frame is expected back after it goes out.',
    'loanFrames.returnByMeans': 'The frame counts as late from the start of that day.',
    'loanFrames.amountBad': 'That is not an amount.',
    'loanFrames.amountNegative': 'An amount cannot be less than nothing.',
    'loanFrames.issue': 'Record the loan',
    'queue.subject.loanFrame': 'The loan frame',
    // A chase records what a person did to reach a customer, so the channel is not bounded by
    // `MSG-001`'s two v1 send channels. These three complete the family for the chase table.
    'messageChannel.whatsapp': 'WhatsApp',
    'messageChannel.sms': 'SMS',
    'messageChannel.push': 'Push notification',
    'nav.loanFrames': 'Loan frames',
    // `Shipments`, `IMP-001` to `IMP-006`, section 6.28, release gate 8. A shopkeeper's words for
    // landed cost: "what it cost besides the goods" rather than "non-goods cost", "bill" rather than
    // "shipment cost", and "spread it" rather than "allocate". Three of these are decisions rather
    // than labels, and each is on screen because a person cannot work it out by looking.
    // `noList` says there is no read for a list of shipments, so the only ways in are an order that
    // names one and the one you just started. `soldHint` says why the sold figure is typed every
    // time: the product cannot link a sale back to the delivery it came out of, and what is typed
    // decides how much duty lands on this period's cost of sales rather than on the shelf.
    // `basisHint` says what the screen cannot check for you, so a refusal is expected rather than
    // mysterious. The basis words are lower case because they are read inside "Spread by {basis}"
    // as well as on their own in the select.
    'shipments.heading': 'Imports and what they cost',
    'shipments.loadFailed': 'Could not load the shipment.',
    'shipments.ordersLoadFailed': 'Could not load the orders to suppliers.',
    'shipments.loadingOrders': 'Loading the orders to suppliers.',
    'shipments.noList': 'Lensmira cannot list shipments yet: there is no read for one. A shipment is reached through an order that travels in it, or through the one you start below.',
    'shipments.openHeading': 'Shipments your orders travel in',
    'shipments.noneToOpen': 'No order at this branch names a shipment, so there is none to open.',
    'shipments.open': 'Open it',
    'shipments.newHeading': 'Start a shipment',
    'shipments.referenceLabel': 'What to call it',
    'shipments.carrierLabel': 'Who is carrying it',
    'shipments.arrivalLabel': 'Expected to arrive',
    'shipments.referenceRequired': 'Give the shipment a name you will recognise it by.',
    'shipments.attachFirst': 'Add an order to it straight away. A shipment with no order on it cannot be found again once you leave this screen.',
    'shipments.create': 'Start it',
    'shipments.createdWithoutId': 'The shipment was made and its id did not come back, so this screen cannot open it. Add an order to it from the suppliers screen, then open it from here.',
    'shipments.orderingElsewhere': 'Starting a shipment, adding an order to it and moving its tracking belong to whoever raises orders to suppliers, which is not you. The bills and spreading them are yours.',
    'shipments.leave': 'Leave this shipment',
    'shipments.carriedBy': 'Carried by {carrier}',
    'shipments.due': 'Expected {date}',
    'shipments.noArrivalDate': 'No arrival date given',
    'shipments.firstSpread': 'Costs were first spread {when}',
    'shipments.moveTo': 'Move it to {state}',
    'shipments.trackingDone': 'It has arrived, so there is no further state to move it to.',
    'shipments.trackingUnknown': 'This counter has no word for the state {state}, so it cannot offer the next one. Update the app.',
    'shipments.ordersHeading': 'Orders in this shipment',
    'shipments.noOrders': 'No order travels in this shipment yet, so it has nothing to spread across.',
    'shipments.attachLabel': 'Order to add',
    'shipments.attachChoose': 'Choose an order',
    'shipments.attach': 'Add it to the shipment',
    'shipments.attachNone': 'Every order at this branch already travels in a shipment.',
    'shipments.attachRequired': 'Choose which order to add.',
    'shipments.costsHeading': 'What it cost besides the goods',
    'shipments.costsWithheld': "Cost is not yours to see at this branch, so this shipment's bills and how they were spread are not shown.",
    'shipments.noCosts': 'No bill has been entered against this shipment.',
    'shipments.spreadBy': 'Spread by {basis}',
    'shipments.paidTo': 'Paid to {payee}',
    'shipments.bookedAt': 'Booked at {amount}, at {rate} on {date}',
    'shipments.revise': 'Change the amount',
    'shipments.basisFixed': 'It stays spread by {basis}. Every working already written is held to that, so only the amount can change.',
    'shipments.amountLabel': 'Amount, in {currency}',
    'shipments.baseAmountLabel': 'What it was booked at, in {currency}',
    'shipments.rateLabel': 'Rate used',
    'shipments.rateOnLabel': 'Day of that rate',
    'shipments.rateHint': 'Lensmira does not convert it for you. The figure your bank or your clearing agent used is what the books are kept on, so type what it was booked at in {currency}, the rate, and the day that rate was taken.',
    'shipments.sameCurrency': "This bill is in {currency}, the shop's own money, so it carries no rate.",
    'shipments.saveRevision': 'Save the new amount',
    'shipments.newCostHeading': 'Add a bill',
    'shipments.kindLabel': 'What the bill is for',
    'shipments.payeeLabel': 'Who it was paid to',
    'shipments.basisLabel': 'Spread it by',
    'shipments.basisChoose': 'Choose how to spread it',
    'shipments.basisRequired': 'Choose how to spread this bill. There is nothing to assume on your behalf: how a bill was spread is recorded with the figures so they can be checked later.',
    'shipments.basisHint': 'Weight and volume are only on a delivery that was booked in with them, and this screen cannot tell whether yours were, so a bill spread by either may come back refused. Value is refused as well if anything on the shipment was bought in another currency. Number of pieces always works.',
    'shipments.currencyLabel': 'Currency of the bill',
    'shipments.currencyInvalid': 'A currency is a three letter code, like PKR or USD.',
    'shipments.amountInvalid': 'That is not an amount.',
    'shipments.amountPositive': 'An amount is a figure above zero.',
    'shipments.rateRequired': 'A bill in another currency needs the rate, the day it was taken, and what it was booked at in {currency}.',
    'shipments.rateInvalid': 'A rate is a decimal figure with at most eight places, like 280.15.',
    'shipments.addCost': 'Record the bill',
    'shipments.allocationHeading': 'How the bills are spread now',
    'shipments.allocationWithheld': 'How the bills were spread is a cost figure and is not shown to you.',
    'shipments.noAllocation': 'Nothing has been spread yet.',
    'shipments.split': '{onStock} onto stock still here, {onSold} onto what had already sold',
    'shipments.basisShare': 'By {basis}: {quantity} of {total}',
    'shipments.soldOf': {
        one: 'One piece had sold',
        other: '{count} pieces had sold',
    },
    'shipments.inPeriod': 'In {period}',
    'shipments.workingNumber': 'Working {revision}',
    'shipments.runNothing': 'Nothing moved: what is recorded already matches what the figures say.',
    'shipments.runDone': 'The spread was recorded.',
    'shipments.runWrote': {
        one: 'One working written, in {period}.',
        other: '{count} workings written, in {period}.',
    },
    'shipments.runPosted': 'Of it, {cogs} to cost of sales, {inventory} onto stock, and {payable} to what is owed.',
    'shipments.runPostedWithheld': 'What it posted to the ledger is a cost figure and is not shown to you.',
    'shipments.allocateHeading': 'Spread the bills now',
    'shipments.allocateNoCosts': 'There is no bill to spread yet.',
    'shipments.allocateNoLines': 'Nothing has been booked in against these orders yet, so there is nothing to spread a bill across.',
    'shipments.soldHint': "Lensmira cannot tell what has sold out of a delivery: nothing links a sale back to the box it came out of. So it is asked here, line by line, and what you type decides how much of the freight and the duty lands on this period's cost of sales instead of on the stock still on the shelf.",
    'shipments.soldLabel': 'How many of {description} have sold',
    'shipments.soldRequired': 'Say how many have sold on every line. A line left blank would be refused, and a refused write stops everything else waiting to be sent.',
    'shipments.soldInvalid': 'How many have sold is a whole number, zero or more.',
    'shipments.soldTooMany': 'More cannot have sold than arrived on a line.',
    'shipments.valueBasisImpossible': 'A bill spread by value cannot be spread here: a line was bought in {bought} and the books are kept in {base}, and what that line is worth in {base} is not recorded. Spread it by weight, volume or number of pieces instead.',
    'shipments.allocate': 'Spread them',
    'shipments.linesCaption': 'What arrived against {reference}',
    'shipments.col.item': 'Item',
    'shipments.col.arrived': 'Arrived',
    'shipments.col.sold': 'Sold since',
    'shipments.overReceipt': 'More arrived than was ordered',
    'shipments.consignmentLeftOut': "Stock on consignment is left out: the shop has not bought it, so none of the shop's import cost lands on it.",
    'shipments.queueCreateLabel': 'A new shipment',
    'shipments.queueAttachLabel': 'An order added to a shipment',
    'shipments.queueTrackingLabel': "A shipment's tracking",
    'shipments.queueCostLabel': 'A bill on a shipment',
    'shipments.queueReviseLabel': 'A changed bill on a shipment',
    'shipments.queueAllocateLabel': 'Import costs spread across a shipment',
    'queue.subject.shipment': 'The shipment',
    'nav.shipments': 'Imports',
    // `IMP-006`'s six tracking states, read by `apps/counter/src/status-labels.ts`. State nouns, not
    // verbs: the same word is the state on the row and the destination in "Move it to {state}".
    'shipmentTracking.ordered': 'Ordered',
    'shipmentTracking.shipped': 'Shipped',
    'shipmentTracking.inTransit': 'In transit',
    'shipmentTracking.atCustoms': 'At customs',
    'shipmentTracking.cleared': 'Cleared customs',
    'shipmentTracking.received': 'Arrived',
    // `IMP-001`'s seven kinds of non-goods cost, in the words a clearing agent's paperwork uses.
    'shipmentCost.freight': 'Freight',
    'shipmentCost.insurance': 'Insurance',
    'shipmentCost.customsDuty': 'Customs duty',
    'shipmentCost.clearingAgent': 'Clearing agent',
    'shipmentCost.portCharges': 'Port charges',
    'shipmentCost.inlandTransport': 'Inland transport',
    'shipmentCost.bankCharges': 'Bank charges',
    // `IMP-002`'s four bases. Lower case, because each is read inside "Spread by {basis}" and
    // "It stays spread by {basis}" as well as standing alone in the select. `weight` and `volume`
    // carry "of the goods" like `value` does: `status-labels.test.ts` refuses a word that is the
    // enum value it labels, because that is what a missing label looks like.
    'allocationBasis.value': 'value of the goods',
    'allocationBasis.weight': 'weight of the goods',
    'allocationBasis.volume': 'volume of the goods',
    'allocationBasis.unitCount': 'number of pieces',
    // `SendToLab`, section 6.8 `JOB-001` and `JOB-002`. The one act the shop exists to perform had
    // no screen: `POST /jobs` was complete, `job_create_advance_status` is held by four roles, and
    // nothing in the product made a job.
    //
    // `frozenMeans` is why the figures are already filled in, which is the opposite of the invoice
    // desk two screens away. A job is a frozen copy of the Rx at order time, so copying it in is the
    // operation rather than a convenience, and `fromVersion` names the version it was taken from so
    // an accepted snapshot can be traced. `emptyEyeMeans` says an empty eye is one lens and not a
    // plano lens, because a substituted zero is a lens nobody ordered. And `noSpec` says what the
    // ticket will not carry, since `later_todo.md` 17 is a product call and a shopkeeper should hear
    // it from the screen rather than from the lab.
    'sendToLab.heading': 'Send this to the lab',
    'sendToLab.frozenMeans': 'A job is a copy of the prescription as it is today, and the lab makes what the copy says. A later change to the prescription never changes what was made.',
    'sendToLab.fromVersion': 'The figures below are version {version} of the prescription. Check them.',
    'sendToLab.noRx': 'This sale has no prescription on it, so nothing is filled in. Type what the paper says.',
    'sendToLab.noSpec': 'The ticket carries these measurements and not the lens itself. Tell the lab which lens and coating separately until Lensmira can print it.',
    'sendToLab.alreadyAtLab': 'Already at the lab for this sale: {numbers}.',
    'sendToLab.promisedLabel': 'Date promised to the customer',
    'sendToLab.promisedMeans': 'This is the date the work queue ages against, so a job with no date promised is a job nobody chases.',
    'sendToLab.supplierLabel': 'Lab or supplier id',
    'sendToLab.supplierMeans': 'Lensmira cannot yet list your labs, so this is the id from your records. Leave it empty for a job you are keeping in the shop.',
    'sendToLab.eyeLegend': 'The {side} eye',
    'sendToLab.fieldLabel': '{field}, {side} eye',
    'sendToLab.right': 'right',
    'sendToLab.left': 'left',
    'sendToLab.sphere': 'Sphere',
    'sendToLab.cylinder': 'Cylinder',
    'sendToLab.axis': 'Axis',
    'sendToLab.add': 'Add',
    'sendToLab.pd': 'PD in mm',
    'sendToLab.seg': 'Segment height in mm',
    'sendToLab.emptyEyeMeans': 'Leave an eye completely empty to make one lens rather than a pair. An empty eye is not sent, and it is not sent as zero either.',
    'sendToLab.submit': 'Send it to the lab',
    'sendToLab.queueLabel': 'A job for {number}',
    'sendToLab.queueLabelNoNumber': 'A job for a sale with no invoice number yet',
    'queue.subject.labJob': 'The job',
    'sendToLab.oneEyeRequired': 'Fill in at least one eye. A job with neither eye is nothing for a lab to make.',
    'sendToLab.eyeNumber': 'The {side} eye needs {field} as a number.',
    'sendToLab.eyeQuarterStep': 'The {side} eye {field} has to be a 0.25 step, like -2.25 or -2.50. A lab cannot make anything in between.',
    'sendToLab.axisRange': 'The {side} eye axis is a whole number of degrees from 0 to 180.',
    'sendToLab.notManufacturable': 'This order is {status}, so there is no sale for a lab to make. Confirm the sale first.',
    'sendToLab.cannotSend': 'Check the figures above before sending this to the lab.',
    // `CAT-007`, PRD section 15 step 3's `[gate]`. The acknowledgement is a box nobody has ticked
    // for them, and the version is typed because the product holds no policy record to read it
    // from. The state is carried by the words and by the box, never by colour (`CL-02`).
    'sendToLab.ownFrameOptIn': "This job is made into the customer's own frame",
    'sendToLab.ownFrameHeading': "The customer's own frame",
    'sendToLab.ownFrameRequired': 'This sale is lenses only, for a frame the shop did not sell. It cannot go to a lab until the frame is described and the customer has acknowledged the breakage-liability policy.',
    'sendToLab.ownFrameDescriptionLabel': 'Describe the frame',
    'sendToLab.ownFrameDescriptionMeans': 'Enough to pick it out on a lab ticket and to know it when it comes back: colour, shape, any marks.',
    'sendToLab.ownFramePolicyVersionLabel': 'Version of the breakage-liability policy read to the customer',
    'sendToLab.ownFramePolicyVersionMeans': 'Write the version printed on the policy the customer was shown. It is recorded with their acknowledgement, so a later change to the policy does not change what they agreed to. Lensmira does not hold the policy text yet.',
    'sendToLab.ownFrameAcknowledge': 'The customer has been shown version {version} of the breakage-liability policy and agrees to it.',
    'sendToLab.ownFrameAcknowledgeWaiting': 'The customer has been shown the breakage-liability policy and agrees to it. Enter its version above first.',
    'sendToLab.ownFrameNeedsDescription': "Describe the customer's own frame before sending it.",
    'sendToLab.ownFrameNeedsVersion': 'Enter the version of the breakage-liability policy the customer was shown.',
    'sendToLab.ownFrameNeedsAcknowledgement': 'The customer has to agree to the breakage-liability policy before their frame goes to a lab. Tick the box once they have.',
    // `StockWrites`, section 6.10 `INV-001` and `INV-005`. The half `Stock.tsx` named and did not
    // build. Two sentences here are the ones that matter. `moreOfThisOnly` says what the screen
    // cannot do and why, because there is no frames catalogue read in the product and a box for an
    // id nobody can look up is worse than a sentence. And `adjustMeans` says what an adjustment is
    // for in a shopkeeper's words, because `INV-005` is the route that makes stock disappear with a
    // reason attached, and the reason is what somebody reads months later.
    'stockWrites.heading': 'Book stock in, or correct it',
    'stockWrites.moreOfThisOnly': 'This books in more of the item you are looking at. A frame or lens the shop has never carried cannot be added here yet, because nothing in Lensmira lists your suppliers catalogue to pick it from.',
    'stockWrites.receiveLegend': 'A delivery of this item',
    'stockWrites.receivedLabel': 'How many arrived',
    'stockWrites.unitCostLabel': 'Cost each ({currency})',
    'stockWrites.consignmentMeans': 'This stock is on consignment, so it is not yours and carries no cost. Nothing is asked for here.',
    'stockWrites.consignmentHasNoCost': 'Consignment stock has no cost to the shop, so leave the cost empty. If this delivery is yours, it is a different position.',
    'stockWrites.costRequired': 'Say what each one cost, so the shelf is worth what it is worth.',
    'stockWrites.costInvalid': 'Cost each is an amount, for example 1250.00.',
    'stockWrites.receive': 'Book it in',
    'stockWrites.adjustLegend': 'Correct what is on the shelf',
    'stockWrites.adjustMeans': 'Use this for a breakage, a loss, or something found that the record does not have. Write how many pieces to add or take away, not the new total.',
    'stockWrites.deltaLabel': 'Pieces to add, or minus to take away',
    'stockWrites.reasonLabel': 'Why',
    'stockWrites.reasonRequired': 'Say why the shelf and the record disagree. It is what somebody reads months later when the figures are questioned.',
    'stockWrites.deltaWhole': 'Write a whole number of pieces, like 2 or -1.',
    'stockWrites.deltaNotZero': 'Nothing to correct: a change of zero pieces is a reason with no movement behind it.',
    'stockWrites.foundCostLabel': 'Cost each of what was found ({currency})',
    'stockWrites.foundCostRequired': 'There is none of this on the shelf, so there is no cost to carry forward. Say what each found piece is worth.',
    'stockWrites.quantityWhole': 'How many is a whole number of pieces, one or more.',
    'stockWrites.adjust': 'Correct the shelf',
    'stockWrites.adjustNeedsManager': 'Correcting the shelf is the owner or a manager, not from this desk. Book the delivery in here and ask them for the correction.',
    'stockWrites.queueReceiveLabel': 'A delivery onto the shelf',
    'stockWrites.queueAdjustLabel': 'A correction to the shelf',
    'queue.subject.stockWrite': 'The stock change',
    // `DataFiles`, section 6.33 `MIG-001` and `MIG-005`. The screen an optician needs on their first
    // day, which the product had none of: `migration.controller.ts` has had five routes and no
    // caller, so the only way a shop's spreadsheet got in was a developer with the database
    // password.
    //
    // Three of these sentences carry decisions. `dryRunMeans` says the validation writes nothing,
    // because a person about to hand over their whole customer list needs to hear that before they
    // press anything. `validatesOnly` plus the seven `notCommittable` sentences are the server's own
    // reasons, kept in the server's own terms: each names the column that has nowhere to land, which
    // is what a shop needs in order to decide whether to wait or to retype. And `fileMeans` says the
    // file is pasted rather than picked, which is the honest description of what this screen takes.
    'dataFiles.heading': 'Bringing records in, and taking them out',
    'dataFiles.importHeading': 'Bring a file in',
    'dataFiles.dryRunMeans': 'Checking a file changes nothing in your shop. Lensmira reads every row, tells you what it would do and what it cannot read, and waits. Nothing is written until you say so on the second button.',
    'dataFiles.templateLabel': 'What is in the file',
    'dataFiles.columnsAre': 'The columns this needs: {required}.',
    'dataFiles.fileLabel': 'The file',
    'dataFiles.fileMeans': 'Paste the file here, including its first line of column names. Save it out of your spreadsheet as CSV first.',
    'dataFiles.fileRequired': 'Paste the file before checking it.',
    'dataFiles.currencyLabel': 'Currency of the amounts in the file',
    'dataFiles.currencyInvalid': 'A currency is a three letter code, like PKR or USD.',
    'dataFiles.digitsLabel': 'Decimal places in those amounts',
    'dataFiles.digitsMeans': 'Two for rupees. It decides how 1500.50 is read, so a wrong answer here is every amount in the file wrong by a hundred.',
    'dataFiles.validate': 'Check the file',
    'dataFiles.reportHeading': 'What the file would do',
    'dataFiles.noPlan': 'The columns could not be matched up, so there is nothing to report yet. Fix the column names below and check it again.',
    'dataFiles.totalRead': 'Rows read:',
    'dataFiles.totalCreate': 'Would be added:',
    'dataFiles.totalSupersede': 'Would replace an existing record:',
    'dataFiles.totalUnchanged': 'Already the same, nothing to do:',
    'dataFiles.totalQuarantined': 'Set aside as unreadable:',
    'dataFiles.totalWarnings': 'Readable, with something worth looking at:',
    'dataFiles.mappingProblem': 'Column names: {code}. Expected {column}, file has {header}.',
    'dataFiles.nothingWrong': 'Nothing in the file disagreed.',
    'dataFiles.severityError': 'Set aside',
    'dataFiles.severityWarning': 'Worth a look',
    'dataFiles.issueGroup': {
        one: '{code} {column}, one row. It is:',
        other: '{code} {column}, {count} rows. The first of them:',
    },
    'dataFiles.quarantineRow': 'Row {row}',
    'dataFiles.validatesOnly': 'Lensmira can check this kind of file and cannot bring it in yet.',
    'dataFiles.notCommittable.prescriptions': 'A prescription row gives one cylinder, axis and add for both eyes, so nothing in it says which eye the cylinder belongs to, and a guess is a wrong pair of glasses.',
    'dataFiles.notCommittable.frames': 'A frame row carries a selling price and a frame record has none: a price lives in a price book, so the price would be dropped without a word.',
    'dataFiles.notCommittable.lenses': 'A lens record needs its surfacing and the file has no column for it, and the availability range columns do not match how the sign is stored.',
    'dataFiles.notCommittable.lensPrices': 'A price list comes in as a new price book version, which is published and then frozen. That is the price book screen and not this one.',
    'dataFiles.notCommittable.treatments': 'A treatment row carries a price and a name, and a treatment is keyed by its kind, brand, grade and colour with the price in a price book.',
    'dataFiles.notCommittable.suppliers': 'A supplier record needs a code and a country the file does not give, and its terms and contact have nowhere to land.',
    'dataFiles.notCommittable.openJobs': 'A job is made from a sale, carrying the prescription, frame and lens as they were on the day. An open-jobs row has none of that, so the job would have nothing to manufacture from.',
    'dataFiles.needsCustomerRow': 'Bringing customers in writes customer records, so it needs the same permission as adding one by hand. Ask the owner.',
    'dataFiles.fixFirst': 'Fix what is set aside above and check the file again. Rows that are set aside are not brought in, and the rest would be.',
    'dataFiles.commitMeans': 'This writes the rows above into your shop. Rows set aside are left out, and bringing the same file in twice does not duplicate anything.',
    'dataFiles.commit': 'Bring it in',
    'dataFiles.imported': 'The file has been brought in.',
    'dataFiles.importNeedsOwner': 'Bringing a file in is the owner or a manager. You can still take a copy of your data out below.',
    'dataFiles.exportHeading': 'Take a copy out',
    'dataFiles.exportMeans': 'A zip of your shop records as they are now. It is your data and taking it is recorded, so there is a line in the log saying who took it and when.',
    'dataFiles.exportNow': 'Make the copy',
    'dataFiles.exportNoFile': 'The copy came back with no file in it. Nothing was lost; try again.',
    'dataFiles.download': 'Save {filename}',
    'dataFiles.withheld': 'Left out of the copy: {entity}, because {reason}',
    'dataFiles.notExported': 'Not in the copy yet: {entity}, because {reason}',
    'dataFiles.queueValidateLabel': 'A check of a file',
    'dataFiles.queueImportLabel': 'A file coming in',
    'dataFiles.queueExportLabel': 'A copy of the shop records',
    'queue.subject.dryRun': 'The check',
    'queue.subject.dataImport': 'The file',
    'queue.subject.dataExport': 'The copy',
    'nav.dataFiles': 'Records in and out',
    // Appendix E's `stock_adjustment_reason`, as a shopkeeper would say it. The code is what the
    // server takes and these are what somebody picks from, which is the whole point: the reason was
    // a free-text box for an hour and every correction it sent would have been refused with
    // `UNKNOWN_REASON` and stopped the till.
    //
    // `revaluation` and `return_to_vendor` are in the enum and neither adjustment branch allows
    // them, so they are worded here and never offered: a table that is exhaustive over the enum is
    // what makes that a decision rather than an omission.
    'stockReason.countVariance': 'Found in a count',
    'stockReason.damage': 'Damaged',
    'stockReason.shrinkage': 'Missing, nobody knows where',
    'stockReason.expiry': 'Past its expiry date',
    'stockReason.writeOff': 'Written off',
    'stockReason.revaluation': 'Revalued',
    'stockReason.returnToVendor': 'Sent back to the supplier',
    'stockReason.sample': 'Given away as a sample',
    'stockReason.inHouseBreakage': 'Broken in the shop',
    'stockWrites.reasonPick': 'Pick a reason',
    'stockWrites.reasonOneWay': 'Adding pieces back is only ever a count: anything else is a loss, and a loss takes pieces away.',
    // Setting up an authenticator, `TEN-005`. The owner and the manager cannot sign in without one
    // and nothing in the product could create one, so these words exist for the first minute of a
    // shop's life and are read by somebody who has just been told their password does not work.
    //
    // `signIn.enrolDone` gives the secret and says to write it down, because nothing can show it
    // again: `credential` is in no read and there is no route that returns it. That is said plainly
    // rather than softened, since the consequence of closing the page is a locked-out owner.
    'signIn.enrolOpen': 'Setting up for the first time?',
    'signIn.enrolHeading': 'Set up your authenticator',
    'signIn.enrolMeans': 'An owner or a manager signs in with a password and a six digit code. Type your email and password and Lensmira will give you the code to add to your authenticator app, once.',
    'signIn.enrol': 'Give me the code to add',
    'signIn.enrolDone': 'Add this to your authenticator app now, then sign in with the six digits it shows. Lensmira cannot show it to you again.',
    'signIn.enrolSecret': 'The code to add',
    'signIn.enrolLink': 'Or open it in your authenticator app',
    'signIn.enrolBack': 'Back to signing in',
    'signIn.enrolNeedsBoth': 'Type your email and your password first.',
    // PIN sign-in on an enrolled counter terminal, `TEN-006`. The refusal sentence is the server's and
    // names none of the halves: a wrong PIN, an unknown short name, a locked account and a revoked
    // terminal all read the same, so this file has no sentence for any of them.
    'signIn.pin.heading': 'Sign in with your PIN',
    'signIn.pin.username': 'Your short name',
    'signIn.pin.pin': 'PIN',
    'signIn.pin.backspace': 'Delete',
    'signIn.pin.clear': 'Clear',
    'signIn.pin.needsBoth': 'Type your short name and your PIN.',
    'signIn.pin.usePassword': 'Use my email and password instead',
    'signIn.pin.usePin': 'Use my PIN on this till',
    'signIn.pin.unreachable': "We can't reach your account right now. This isn't your PIN, try again shortly.",
    // The owner's terminal panel on the setup screen, `TEN-006`, `decisions/0021`. `tokenMeans` says
    // before anything is pressed that the code is shown once, because the only way back from losing
    // it is to revoke the terminal and enrol another.
    'terminals.heading': 'Counter terminals',
    'terminals.means': 'A terminal is a till you have enrolled, so staff can sign in on it with a short name and a PIN. Only enrolled tills offer that. Revoking one stops it at once.',
    'terminals.loadFailed': 'The terminals could not be loaded. Try again in a moment.',
    'terminals.nameLabel': 'A name for this till, such as Front till',
    'terminals.nameRequired': 'Give the terminal a name, such as Front till.',
    'terminals.enrol': 'Enrol a terminal',
    'terminals.enrolling': 'Enrolling',
    'terminals.enrolled': '{name} is enrolled',
    'terminals.tokenMeans': 'This code is shown once and nothing can show it again. Use it on the till now, or write it down. If it is lost, revoke the terminal and enrol it again.',
    'terminals.tokenLabel': 'The terminal code',
    'terminals.useHere': 'Use this device as that terminal',
    'terminals.tokenDone': 'I have taken it down',
    'terminals.usingHere': 'This device is now a terminal. Its sign-in screen offers the PIN pad.',
    'terminals.pasteLabel': 'Or paste a terminal code to use on this device',
    'terminals.pasteUse': 'Use the code on this device',
    'terminals.thisDevice': 'This device holds a terminal code.',
    'terminals.forgetHere': 'Stop using this device as a terminal',
    'terminals.forgotten': 'This device no longer offers the PIN pad.',
    'terminals.enrolledOn': 'Enrolled {date}',
    'terminals.revokedOn': 'Revoked {date}: {reason}',
    'terminals.revoke': 'Revoke {name}',
    'terminals.revokeMeans': 'This stops {name} for good and signs out everybody who signed in on it with a PIN. To use that till again it has to be enrolled as a new terminal.',
    'terminals.revokeReason': 'Why it is being revoked',
    'terminals.revokeConfirm': 'Revoke it',
    'terminals.revokeCancel': 'Leave it alone',
    'terminals.reasonRequired': 'Say why, so the record shows it later.',
    'terminals.queueRevokeLabel': 'Revoking the terminal {name}',
    'terminals.revokeQueued': 'Revoking {name}.',
    // Setting a PIN, in the people list of the setup screen, `TEN-006`.
    'shopSetup.pin.set': 'Give {name} a PIN',
    'shopSetup.pin.change': 'Change the PIN for {name}',
    'shopSetup.pin.current': 'Signs in as {username}',
    'shopSetup.pin.username': 'Short name for signing in',
    'shopSetup.pin.pin': {
        one: 'PIN, {count} digit',
        other: 'PIN, {count} digits',
    },
    'shopSetup.pin.means': 'They type this short name and PIN on a till that has been enrolled. A new PIN also unlocks the account if wrong guesses locked it. Tell {name} the PIN yourself, because nothing here shows it again.',
    'shopSetup.pin.confirm': 'Save the PIN',
    'shopSetup.pin.cancel': 'Leave it alone',
    'shopSetup.pin.usernameInvalid': 'A short name is 3 to 20 letters or digits, and may have a dot, underscore or hyphen inside.',
    'shopSetup.pin.pinInvalid': {
        one: 'The PIN is exactly {count} digit, 0 to 9.',
        other: 'The PIN is exactly {count} digits, 0 to 9.',
    },
    'shopSetup.pin.queueLabel': 'A PIN for {name}',
    'shopSetup.pin.queued': 'Setting the PIN for {name}.',
    // `Transfers`, section 6.10 `INV-004` and section 12 case 46. Moving stock between branches,
    // which nothing in the product could do and nothing could have until a transfer recorded where
    // it was going.
    //
    // Three sentences carry decisions. `inTransitMeans` says the list is what has left and not
    // arrived, in both directions, because a shopkeeper's question is "what is moving that concerns
    // me" and the two ends are one answer. `shortMeans` says what happens when fewer arrive, before
    // the figure is typed, because section 15.6 makes a short receipt a discrepancy to decide rather
    // than a mistake to prevent and the truth has to be easier to enter than a round number. And
    // `theirsToReceive` says who books it in, so nobody presses a button the server would refuse.
    'transfers.heading': 'Stock on the move',
    'transfers.inTransitMeans': 'What has left one branch and not arrived at the other, both ways. It is still your stock while it is on the road.',
    'transfers.loadFailed': 'Could not load what is on the move.',
    'transfers.shelfFailed': 'Could not load what is on that shelf.',
    'transfers.coming': 'Coming here',
    'transfers.going': 'Going out',
    'transfers.fromBranch': 'from {branch}',
    'transfers.toBranch': 'to {branch}',
    'transfers.unknownBranch': 'a branch nobody recorded',
    'transfers.theirsToReceive': 'The branch it is going to books this in.',
    'transfers.startReceiving': 'Book it in',
    'transfers.shelfLabel': 'Onto which shelf',
    'transfers.pickAShelfOption': 'Choose a shelf',
    'transfers.pickAShelf': 'Say which shelf it is going on.',
    'transfers.receivedLabel': 'How many arrived',
    'transfers.shortMeans': 'If fewer arrived than were sent, write what actually arrived. Lensmira records the difference and holds its value rather than writing it off, so somebody can decide what happened.',
    'transfers.bookIn': 'Book in what arrived',
    'transfers.moreThanSent': 'Only {sent} were sent, so no more than that can arrive.',
    'transfers.sendLegend': 'Send stock to another branch',
    'transfers.fromShelfLabel': 'From which shelf',
    'transfers.itemLabel': 'What to send',
    'transfers.pickAnItemOption': 'Choose what to send',
    'transfers.pickAnItem': 'Say what you are sending.',
    // `stock_item.lot` defaults to the empty string, which is the schema saying "received without a
    // lot number", so a shop that does not track lots read `Lot , 10 on hand`. And the option named
    // no item at all: two positions of two different frames on one shelf were indistinguishable.
    'transfers.itemOption': '{item}, lot {lot}, {quantity} on hand',
    'transfers.itemOptionNoLot': '{item}, {quantity} on hand',
    'transfers.toBranchLabel': 'To which branch',
    'transfers.pickABranchOption': 'Choose a branch',
    'transfers.pickABranch': 'Say which branch it is going to.',
    'transfers.quantityLabel': 'How many pieces',
    'transfers.quantityWhole': 'How many is a whole number of pieces, one or more.',
    'transfers.moreThanShelf': 'That is more than the shelf holds.',
    'transfers.sendMeans': 'The stock leaves this branch now and stays yours until the other branch books it in. Only that branch can.',
    'transfers.send': 'Send it',
    'transfers.needsTheRow': 'Moving stock between branches is the owner, a manager or a dispenser. You can see what is on the move.',
    'transfers.queueSendLabel': 'Stock going to another branch',
    'transfers.queueReceiveLabel': 'Stock arriving from another branch',
    'queue.subject.transfer': 'The transfer',
    'nav.transfers': 'On the move',
    // Approving a cash variance, section 6.9 `POS-004` and `ACC-001`. The route existed and nothing
    // in the product posted to it, so a variance of any size left the session `variance_pending` for
    // ever: the next shift could not open a drawer at that terminal and every cash tender there
    // failed. One rupee out and the till took no cash again.
    //
    // `approveMeans` says what approving does, because it is not a dismissal: `ACC-001` posts the
    // difference to `cash_variance`, so the money is recognised as gone or found and the drawer is
    // released. A control that read as "make this go away" would be used that way.
    'cashSession.approveHeading': 'Approve the difference',
    'cashSession.approveMeans': 'The difference is recorded against the shop books as money gone or money found, and the drawer is released for the next shift. Nothing is written off quietly: it appears on the variance report with your name and your note.',
    'cashSession.approveNoteLabel': 'What was found',
    'cashSession.approveNoteMeans': 'Say what you checked and what you concluded. Without it the report records that a difference existed and nothing about it.',
    'cashSession.approveNoteRequired': 'Write what was found before approving. It is the whole of what the variance report can tell somebody later.',
    'cashSession.approve': 'Approve it and release the drawer',
    'cashSession.queue.approveLabel': 'An approved difference on {terminalId}',
    'queue.subject.varianceApproval': 'The approval',
    // Raising a remake from the job that failed, section 6.8 `RMK-001` to `RMK-004`, release gate 5.
    // Three routes existed and no screen called any of them, and this screen told an optician to go
    // to a remakes screen that does not exist, so a failed quality check was the end of the job and
    // the customer's glasses could never be made.
    //
    // `means` says what raising one does, because `RMK-004` leaves the original job closed: this is a
    // second pair made to the same frozen instruction, not a reopening. `causeMeans` says that the
    // cause decides who is expected to pay, which ten words in a list do not convey.
    'jobDetail.remake.heading': 'Make this pair again',
    'jobDetail.remake.means': 'A new job is raised carrying this one-s prescription, frame and measurements exactly as they were. This job stays closed and failed: nothing is rewritten, and the pair that was made wrong stays on the record.',
    'jobDetail.remake.causeLabel': 'Why it has to be made again',
    'jobDetail.remake.causePick': 'Choose what went wrong',
    'jobDetail.remake.causeMeans': 'This decides who is expected to bear the cost, which somebody can change afterwards. It is the one thing about a remake that cannot be worked out later from the job itself.',
    'jobDetail.remake.causeRequired': 'Say why it has to be made again before raising the remake.',
    'jobDetail.remake.raise': 'Raise the remake',
    'jobDetail.remake.writeLabel': 'A remake of job {jobNumber}',
    'jobDetail.deadEnd.qcFailedNoRow': 'This job failed its quality check. Raising the remake is the optician or a manager, so ask one of them to make the pair again.',
    // Section 8 `ST-02`: the six states every data surface has. The copy for twenty-five screens
    // converted to `DataSurface` in one pass. `what` is the noun a shopkeeper would use, the empty
    // sentence invites the first action and names the screen it happens on, and the filtered
    // sentence reads differently because a shop with nothing yet and a filter that matched nothing
    // are different situations. `later_todo.md` 92.
    'commission.empty': 'No pay rules yet, so no commission can be worked out. Write the first one in the form below: who is paid, on what, and at what rate.',
    'commission.what': 'the pay rules',
    'dispensing.empty': 'Nothing is half-done at this branch. Start one above and it will wait here until someone carries on.',
    'dispensing.what': 'the unfinished dispenses',
    'erasure.what': 'what can and cannot be erased',
    'frameChoice.what': 'frames on the shelf',
    'frameImages.framesFailed': 'The list of frames could not be read.',
    'frameImages.framesWhat': 'frames in the catalogue',
    'frameImages.listFailed': 'The pictures for this frame could not be read.',
    'frameImages.noFrames': 'No frames are on file yet. Add one under Catalogue first, then come back to add its pictures.',
    'frameImages.noneYetCanEdit': 'No pictures of this frame yet. Add one below, from the front, and one from the side.',
    'frameImages.what': 'pictures of this frame',
    'holds.emptyCanHold': 'No frame is on hold for this customer. Pick one below to set it aside for them.',
    'holds.what': 'frames on hold',
    'lensChoice.noneOnFile': 'No lenses are on file yet. Add one under Catalogue and it will be offered here.',
    'lensChoice.what': 'lenses for this prescription',
    'loanFrames.framesFailed': 'The frames on this shelf could not be read.',
    'loanFrames.framesWhat': 'frames on this shelf',
    'loanFrames.repairsFailed': 'The repairs could not be read, so a loan cannot be raised yet.',
    'loanFrames.repairsWhat': 'repairs on the bench',
    'loanFrames.what': 'loan frames past their date',
    'notifications.noneCleared': 'Nothing has been cleared yet.',
    'notifications.noneSeen': 'Nothing has been marked as seen yet.',
    'notifications.what': 'things that need attention',
    'outbox.empty': "Nothing is waiting and nothing has failed. Messages you send from a customer's record show up here.",
    'outbox.filtered': 'No message is in that state. Pick another, or go back to the ones still to deal with.',
    'outbox.what': 'messages',
    'periodClose.noneClosedInvite': 'No month has been closed yet. Once a month has ended, close it below to lock its figures.',
    'periodClose.what': 'the closed months',
    'priceImpact.what': 'the price preview',
    'promotions.empty': 'No offers yet. Write the first one in the form below: say what it gives, the dates it runs and who it is for.',
    'promotions.what': 'the offers',
    'purchasing.empty': 'No order is open with a supplier. Raise one under Order stock and it will show here.',
    'purchasing.filtered': 'No order is marked {status}. Try another status, or Still open.',
    'purchasing.missingCosts': 'what the orders cost',
    'purchasing.what': 'orders to suppliers',
    'recalls.empty': 'Nobody is due to be asked back right now. Customers appear here as their sight tests and contact lens supplies come due.',
    'recalls.filtered': 'Nobody is in that group. Pick another, or go back to the list still to do.',
    'recalls.what': 'customers to bring back',
    'repairs.empty': 'No repair is on the bench. A repair appears here once it is taken in.',
    'repairs.filtered': 'No repairs at that stage. Choose another stage above.',
    'repairs.what': 'the repairs',
    'reports.listEmpty': 'No reports are open to your role yet.',
    'reports.runEmpty': 'Nothing to show yet. This report fills in as the shop records sales, stock and jobs.',
    'reports.what': 'the reports',
    'reports.whatRun': 'this report',
    'reports.withheldField.cogs': 'the cost of goods sold',
    'reports.withheldField.cost_value': 'the cost value',
    'reports.withheldField.due': 'what is due',
    'reports.withheldField.margin': 'the margin',
    'reports.withheldField.margin_pct': 'the margin percentage',
    'reports.withheldField.net': 'the net figure',
    'reports.withheldField.shop_cost': 'what the shop paid',
    'reports.withheldField.supplier_credit': 'supplier credits',
    'returnLine.what': 'the lines on this order',
    'sendMessage.noneActive': 'The shop has wording written, but none of it is turned on. An owner turns it on under Wording.',
    'sendMessage.what': 'messages you can send',
    'supplierInvoice.empty': 'No order is open with a supplier, so there is nothing to enter an invoice against. Orders are raised under Order stock.',
    'supplierInvoice.orderWhat': 'this order and its deliveries',
    'suppliers.noneReadOnly': 'No supplier is set up yet. Ask the owner, a manager or the accountant to add one.',
    'suppliers.what': 'your suppliers',
    'supportAccess.what': 'support access',
    'templates.what': 'message wording',
    'terminals.emptyInvite': 'No terminal has been enrolled yet. Name this till below and enrol it to get a code.',
    'terminals.what': 'the terminals',
    'transfers.empty': 'Nothing is on the move right now. Stock sent between branches shows here until it is booked in.',
    'transfers.missingValue': 'what the stock is worth',
    'transfers.what': 'stock on the move',
    'frameImages.missingCount': {
        one: 'one picture could not be shown',
        other: '{count} pictures could not be shown',
    },
    // The seven counter screens for the config areas an API could reach and no screen could:
    // the contact lens catalogue, recall rules, supplier price lists, customer sharing,
    // appointment setup and the diary. The detector's third pass reported 13 tables an API
    // wrote that no screen could reach, and `../../../../later_todo.md` 93 is why.
    //
    // Written in one pass because `t()` takes a `MessageKey` generated from this file and the
    // package is imported from its `dist`, so a key is a type error until it is here and the
    // package is rebuilt. Seven writers would lose each other's work.
    'appointmentStatus.booked': 'Booked',
    'appointmentStatus.cancelled': 'Cancelled',
    'appointmentStatus.completed': 'Came in',
    'appointmentStatus.moved': 'Moved',
    'appointmentStatus.noShow': 'Did not come',
    'apptCalendar.closureAdd': 'Save the day',
    'apptCalendar.closureAddHeading': 'Mark a day',
    'apptCalendar.closureDate': 'Day',
    'apptCalendar.closureDateRequired': 'Pick the day.',
    'apptCalendar.closureDateTaken': '{date} already has an entry. Change that one instead.',
    'apptCalendar.closureEditLabel': 'Change the note for {date}',
    'apptCalendar.closureKind': 'What happens that day',
    'apptCalendar.closureKindShut': 'The branch is shut',
    'apptCalendar.closureKindTrades': 'The branch is open although it is a public holiday',
    'apptCalendar.closureLabel': 'What it is for (optional)',
    'apptCalendar.closureLabelLong': 'The note is at most 200 characters.',
    'apptCalendar.closureNoDelete': 'A shut day is never deleted. If plans change, open it again and it stays in this list as a record.',
    'apptCalendar.closureOpenAgain': 'Open {date} after all',
    'apptCalendar.closureShut': 'Shut',
    'apptCalendar.closureShutAgain': 'Shut {date} again',
    'apptCalendar.closureTrades': 'Open as usual',
    'apptCalendar.closureTradesMeans': 'Use this when the country lists the day as a holiday and you trade anyway. On any other day it changes nothing.',
    'apptCalendar.closuresEmpty': 'No shut days added for {branch}. Add the first one with the form on this page, for example Eid.',
    'apptCalendar.closuresFiltered': 'No shut days coming up at {branch}. Choose Earlier days to see past ones.',
    'apptCalendar.closuresHeading': 'Days {branch} is shut',
    'apptCalendar.closuresLoadFailed': 'Could not read the shut days for {branch}.',
    'apptCalendar.closuresMeans': "Public holidays on your country's list already count as shut. Add a day here for your own closure, such as Eid or a family wedding. Nothing can be booked on a shut day.",
    'apptCalendar.heading': 'Appointments: days off and seasonal hours',
    'apptCalendar.overrideAdd': 'Add the hours',
    'apptCalendar.overrideAddHeading': 'Add seasonal hours',
    'apptCalendar.overrideChange': 'Change the hours for {from} to {to}',
    'apptCalendar.overrideCloses': 'Closes',
    'apptCalendar.overrideDatesBackwards': 'The last day has to be on or after the first day.',
    'apptCalendar.overrideDatesRequired': 'Pick the first day and the last day.',
    'apptCalendar.overrideFrom': 'First day',
    'apptCalendar.overrideMidnight': 'Closing at midnight cannot be set here. Use 23:59.',
    'apptCalendar.overrideOpens': 'Opens',
    'apptCalendar.overrideOverlap': 'Those days overlap the hours already set for {from} to {to}. Change that set, or pick other days.',
    'apptCalendar.overrideRange': '{from} to {to}',
    'apptCalendar.overrideResume': 'Use these hours again',
    'apptCalendar.overrideResumeOverlap': 'Another set, {from} to {to}, now covers some of these days. Stop that one first, then use these again.',
    'apptCalendar.overrideStop': 'Season over: stop using these hours',
    'apptCalendar.overrideStopMeans': 'Stopping a set takes it out of use and keeps it here. For those days the branch goes back to its usual hours.',
    'apptCalendar.overrideStoppedEdit': 'Use these hours again before changing them.',
    'apptCalendar.overrideTimesBackwards': 'It has to close after it opens.',
    'apptCalendar.overrideTimesRequired': 'Say when it opens and when it closes.',
    'apptCalendar.overrideTo': 'Last day',
    'apptCalendar.overrideWindow': '{opens} to {closes}',
    'apptCalendar.overridesEmpty': 'No seasonal hours at {branch}. Add the first set with the form on this page, for example Ramadan hours.',
    'apptCalendar.overridesFiltered': 'Every set of seasonal hours at {branch} has been stopped. Tick Show the ones you have stopped using to see them.',
    'apptCalendar.overridesHeading': 'Seasonal hours at {branch}',
    'apptCalendar.overridesLoadFailed': 'Could not read the seasonal hours for {branch}.',
    'apptCalendar.overridesMeans': 'Hours that replace the usual opening hours between two dates, such as Ramadan. They change the hours only: a day the branch is shut stays shut, and nothing here opens a closed day.',
    'apptCalendar.queueAddClosure': 'Marking {date}',
    'apptCalendar.queueAddOverride': 'Adding hours for {from} to {to}',
    'apptCalendar.queueChangeClosure': 'A change to {date}',
    'apptCalendar.queueChangeOverride': 'A change to the hours for {from} to {to}',
    'apptCalendar.queueOpenClosure': 'Opening {date} after all',
    'apptCalendar.queueResumeOverride': 'Using the hours for {from} to {to} again',
    'apptCalendar.queueShutClosure': 'Shutting {date} again',
    'apptCalendar.queueStopOverride': 'Stopping the hours for {from} to {to}',
    'apptCalendar.showComing': 'Coming up',
    'apptCalendar.showEarlier': 'Earlier days',
    'apptOffers.heading': 'Appointments: what you offer',
    'apptOffers.kindMeans.staff': 'A calendar for one of your own people.',
    'apptOffers.kindMeans.testingRoom': 'A room that appointments can be booked into.',
    'apptOffers.kindMeans.visitingOptometrist': 'Someone who is not on your staff and is in only on the days you set.',
    'apptOffers.minutes': {
        one: '{count} minute',
        other: '{count} minutes',
    },
    'apptOffers.queueAddResource': 'Adding {name}',
    'apptOffers.queueAddType': 'Adding {name}',
    'apptOffers.queueChangeType': 'A change to {name}',
    'apptOffers.queueRenameResource': 'Renaming {name}',
    'apptOffers.queueResumeResource': 'Using {name} again',
    'apptOffers.queueResumeType': 'Offering {name} again',
    'apptOffers.queueStopResource': 'Stopping {name}',
    'apptOffers.queueStopType': 'Stopping {name}',
    'apptOffers.resourceAdd': 'Add it',
    'apptOffers.resourceAddHeading': 'Add a room or a person',
    'apptOffers.resourceKind': 'What it is',
    'apptOffers.resourceKindRequired': 'Choose what it is.',
    'apptOffers.resourceKindUnchosen': 'Not chosen',
    'apptOffers.resourceName': 'Name',
    'apptOffers.resourceNameRequired': 'Give it a name, up to 120 characters.',
    'apptOffers.resourceNameTaken': 'There is already one called {name} at {branch}.',
    'apptOffers.resourceRename': 'Rename {name}',
    'apptOffers.resourceRenameMeans': 'Only the name can change. The kind and the person are what past appointments were booked against.',
    'apptOffers.resourceResume': 'Use {name} again',
    'apptOffers.resourceResumeStaffTaken': '{person} already has a calendar in use at {branch}. Stop using that one first, then use this one again.',
    'apptOffers.resourceStaff': 'Which staff member',
    'apptOffers.resourceStaffTaken': '{person} already has a calendar in use at {branch}. Rename that one instead.',
    'apptOffers.resourceStop': 'Stop using {name}',
    'apptOffers.resourcesEmpty': 'Nothing is set up at {branch} yet. Add the first room or person with the form on this page.',
    'apptOffers.resourcesFiltered': 'Everything at {branch} has been stopped. Tick Show the ones you have stopped using to see them, or add a new one.',
    'apptOffers.resourcesHeading': 'Rooms and people at {branch}',
    'apptOffers.resourcesLoadFailed': 'Could not read the rooms and people at {branch}.',
    'apptOffers.resourcesMeans': 'What an appointment books besides a time: one of your people, a testing room, or a visiting optometrist. These are kept for each branch.',
    'apptOffers.staffNamesMissing': 'the staff names',
    'apptOffers.staffUnnamed': 'a staff member',
    'apptOffers.typeAdd': 'Add the type',
    'apptOffers.typeAddHeading': 'Add an appointment type',
    'apptOffers.typeChange': 'Change {name}',
    'apptOffers.typeMinutes': 'Minutes it takes',
    'apptOffers.typeMinutesShape': 'Minutes is a whole number from 1 to 1440.',
    'apptOffers.typeName': 'Name',
    'apptOffers.typeNameRequired': 'Give the type a name, up to 120 characters.',
    'apptOffers.typeNameTaken': 'There is already a type called {name}. Two with one name cannot be told apart at the counter.',
    'apptOffers.typeNeeds': 'What it needs',
    'apptOffers.typeNeedsMeans': 'Tick what has to be free for this appointment. Leave all clear if it needs only a time.',
    'apptOffers.typeNeedsMissing': 'Needs a {kind}, and {branch} has none in use, so it cannot be booked there.',
    'apptOffers.typeNeedsNothing': 'Only a time',
    'apptOffers.typeResume': 'Offer {name} again',
    'apptOffers.typeStop': 'Stop offering {name}',
    'apptOffers.typeStoppedMeans': 'A stopped type stays on file for the appointments already booked. It cannot be booked again until you offer it again.',
    'apptOffers.typesEmpty': 'No appointment types yet. Add the first one with the form on this page, for example a sight test or a collection.',
    'apptOffers.typesFiltered': 'Every appointment type has been stopped. Tick Show the ones you have stopped using to see them, or add a new one.',
    'apptOffers.typesHeading': 'Appointment types',
    'apptOffers.typesLoadFailed': 'Could not read your appointment types.',
    'apptOffers.typesMeans': 'What a customer can book, and how long it takes. The length is copied onto each appointment when it is booked, so changing it here moves nothing already in the diary.',
    'apptOffers.visitDatesCount': {
        one: '{count} day set',
        other: '{count} days set',
    },
    'apptOffers.visitDatesMissing': 'the visit dates of {names}',
    'apptOffers.visitDatesOpen': 'Set the days {name} is in',
    'apptOffers.visitNoDates': 'No dates are set, so nobody can book {name} on any day. Set the days they are in.',
    'apptSetup.branchScope': 'These settings are for {branch}. Switch branch at the top to set another.',
    'apptSetup.leave': 'Leave it as it is',
    'apptSetup.nothingChanged': 'Nothing has changed yet.',
    'apptSetup.ownerOnly': 'Only the owner can change what the shop offers for appointments, the days it is shut and its seasonal hours. A manager who needs one changed has to ask the owner.',
    'apptSetup.save': 'Save the change',
    'apptSetup.showStopped': 'Show the ones you have stopped using',
    'apptSetup.stoppedNote': 'not in use',
    'clCatalogue.addMax': 'Highest add',
    'clCatalogue.addMin': 'Lowest add',
    'clCatalogue.addNegative': 'An add is a plus power, so it cannot be below zero.',
    'clCatalogue.addOne': 'Add a contact lens',
    'clCatalogue.allowSubstitution': 'Allow another brand for {name}',
    'clCatalogue.alreadyOnFile': 'You already have {name} in this replacement schedule on file. Change that one instead of adding another.',
    'clCatalogue.alreadyStopped': 'You already have {name} on file, marked as stopped. Sell it again from the list instead of adding another.',
    'clCatalogue.axesEvery10': 'Every 10 degrees, 10 to 180',
    'clCatalogue.axesEvery5': 'Every 5 degrees, 5 to 180',
    'clCatalogue.axesList': 'Axes, in degrees',
    'clCatalogue.axesMode': 'Axes it is made in',
    'clCatalogue.axesTyped': 'Only the ones I type',
    'clCatalogue.axisShape': 'Axes are whole degrees from 1 to 180, with commas between them, like 90, 180.',
    'clCatalogue.baseCurves': 'Base curves, in mm',
    'clCatalogue.brand': 'Brand',
    'clCatalogue.brandAndNameRequired': 'A contact lens needs a brand and a name.',
    'clCatalogue.cancel': 'Leave it as it is',
    'clCatalogue.change': 'Change {name}',
    'clCatalogue.changed': '{name} was changed.',
    'clCatalogue.cylinderMax': 'Highest cylinder',
    'clCatalogue.cylinderMeans': 'Cylinders on contact lenses are minus. The lowest is the strongest, for example -2.25, and the highest is the weakest, for example -0.75.',
    'clCatalogue.cylinderMin': 'Lowest cylinder',
    'clCatalogue.denied': 'You are not allowed to see the contact lens list. Ask the owner or a manager.',
    'clCatalogue.diameters': 'Diameters, in mm',
    'clCatalogue.filtered': 'No contact lens matches that. Try part of the brand or the name, or the device identifier.',
    'clCatalogue.filteredRetired': 'Every contact lens that matches has been stopped. Untick the box above to see them.',
    'clCatalogue.forbidSubstitution': 'This brand only for {name}',
    'clCatalogue.heading': 'Contact lenses',
    'clCatalogue.hideStopped': 'Hide the ones we have stopped selling',
    'clCatalogue.isMultifocal': 'This lens is multifocal (it has an add)',
    'clCatalogue.isToric': 'This lens is toric (it corrects astigmatism)',
    'clCatalogue.loadFailed': 'Could not read your contact lenses.',
    'clCatalogue.manufacturer': 'Manufacturer',
    'clCatalogue.material': 'Material',
    'clCatalogue.materialHint': 'As it is printed on the box, for example silicone hydrogel.',
    'clCatalogue.means': 'Each contact lens you stock, with the sizes and powers it comes in. Stock is counted against these, so a lens has to be on file here before a box of it can be received.',
    'clCatalogue.modality': 'How often it is replaced',
    'clCatalogue.modalityChoose': 'Choose one',
    'clCatalogue.modalityRequired': 'Say how often it is replaced.',
    'clCatalogue.name': 'Lens name',
    'clCatalogue.none': 'No contact lens is on file yet. Add the first one with Add a contact lens, below this list.',
    'clCatalogue.noneReadOnly': 'No contact lens is on file yet. Ask the owner or a manager to add them under Catalogue.',
    'clCatalogue.powerShape': '{field} is a number, like -2.25.',
    'clCatalogue.powersHeading': 'Powers it comes in',
    'clCatalogue.powersMeans': 'Give the lowest and the highest, not every power in between. Powers go up in steps of 0.25.',
    'clCatalogue.queueAddLabel': 'Adding {name}',
    'clCatalogue.queueChangeLabel': 'A change to {name}',
    'clCatalogue.queueResumeLabel': 'Selling {name} again',
    'clCatalogue.queueStopLabel': 'Stopping sales of {name}',
    'clCatalogue.queueSubstitutionLabel': 'Setting whether another brand may replace {name}',
    'clCatalogue.rangeBoth': 'Give both the lowest and the highest {what}, or leave both empty.',
    'clCatalogue.rangeReversed': 'The lowest {what} is higher than the highest. Check the signs.',
    'clCatalogue.renameClash': 'Another lens already has that brand, name and replacement schedule. Change one of the three.',
    'clCatalogue.resume': 'Sell {name} again',
    'clCatalogue.row.add': 'Add {min} to {max}',
    'clCatalogue.row.axes': 'Axes {values}',
    'clCatalogue.row.baseCurve': 'Base curve {values} mm',
    'clCatalogue.row.box': {
        one: 'Box of {count}',
        other: 'Box of {count}',
    },
    'clCatalogue.row.brandOnly': 'This brand only',
    'clCatalogue.row.cylinder': 'Cylinder {min} to {max}',
    'clCatalogue.row.diameter': 'Diameter {values} mm',
    'clCatalogue.row.noPowers': 'No powers given',
    'clCatalogue.row.sphere': 'Sphere {min} to {max}',
    'clCatalogue.row.stopped': 'Stopped',
    'clCatalogue.row.substitutionAllowed': 'Another brand may be offered',
    'clCatalogue.save': 'Put this lens on file',
    'clCatalogue.saveChange': 'Save the change',
    'clCatalogue.saved': '{name} is on file.',
    'clCatalogue.savedAlready': '{name} was already on file, so nothing was added.',
    'clCatalogue.savedAlreadyDiffers': '{name} was already on file, so nothing was added. It is saved differently there: {fields}. Use Change on it to update it.',
    'clCatalogue.searchHint': 'Brand, name or device identifier',
    'clCatalogue.searchLabel': 'Find a contact lens',
    'clCatalogue.sizeShape': 'Sizes are numbers in mm with at most two decimals and commas between them, like 8.4, 8.7. None can be zero.',
    'clCatalogue.sizesHeading': 'Sizes it comes in',
    'clCatalogue.sizesMeans': 'Type every size the maker offers, with commas between them.',
    'clCatalogue.sphereMax': 'Highest sphere',
    'clCatalogue.sphereMin': 'Lowest sphere',
    'clCatalogue.stepShape': '{field} goes up in steps of 0.25, and {value} is not one. The nearest are {below} and {above}.',
    'clCatalogue.stop': 'Stop selling {name}',
    'clCatalogue.substitution': 'Another brand may be offered in its place',
    'clCatalogue.substitutionMeans': 'Turn this off for a lens whose brand is named on the prescription and may not be swapped. It can be changed later from the list.',
    'clCatalogue.toricNeedsAxes': 'A toric lens needs the axes it is made in.',
    'clCatalogue.truncated': {
        one: 'only the first {count} is listed. Search by brand or name to find the rest',
        other: 'only the first {count} are listed. Search by brand or name to find the rest',
    },
    'clCatalogue.udi': 'Device identifier (UDI)',
    'clCatalogue.unchanged': '{name} already says that, so nothing was changed.',
    'clCatalogue.unitsPerBox': 'Lenses in one box',
    'clCatalogue.unitsPerBoxMeans': 'Boxes are what you count and eye-months are what you sell, and this ties the two together. A box of thirty dailies is 30.',
    'clCatalogue.unitsPerBoxRequired': 'Say how many lenses are in a box, as a whole number from 1 to 10,000.',
    'clCatalogue.waterContent': 'Water content, as a percentage',
    'clCatalogue.waterShape': 'Water content is a percentage from 0 to 100, with at most two decimals.',
    'clCatalogue.what': 'your contact lenses',
    'customerSharing.alreadySet': 'That is already how the shop is set.',
    'customerSharing.confirmAll': 'Share every customer with every branch',
    'customerSharing.confirmGroup': 'Share customers within groups',
    'customerSharing.confirmHint': 'Nothing changes until you press the button. It takes effect straight away.',
    'customerSharing.confirmOwn': "Keep each branch's customers separate",
    'customerSharing.currentAll': 'Right now every branch sees every customer.',
    'customerSharing.currentGroup': 'Right now a branch sees its own customers and those of the other branches in its group.',
    'customerSharing.currentOwn': 'Right now each branch sees only the customers filed at it.',
    'customerSharing.currentUnknown': "This shop's setting is not one the counter recognises, so each branch is seeing only its own customers. Choose one below to fix it.",
    'customerSharing.effectAll': 'Every customer in the shop, and their prescriptions, becomes visible to staff at every branch, including customers filed at branches they have never worked at. Nobody can take that back for a customer once another branch has seen them. If your branches are separately owned, think twice.',
    'customerSharing.effectGroup': "Staff see customers filed at their own branch and at the other branches in the same group, and nobody else's. A branch that is in no group sees no customers at all, not even its own, until you put it in one. Customers with no home branch are seen by nobody.",
    'customerSharing.effectOwn': "Staff see only customers filed at their own branch. Customers that other branches could see a moment ago disappear from their lists and searches, and so do those customers' prescriptions, so a repeat customer who walks into a different branch will look like a new one. Customers with no home branch are seen by nobody.",
    'customerSharing.groupAdd': 'Add group',
    'customerSharing.groupBranches': 'Branches in this group',
    'customerSharing.groupEdit': 'Change',
    'customerSharing.groupLiveWarning': 'Sharing within groups is switched on, so saving this changes who sees which customers straight away.',
    'customerSharing.groupLocked': 'This group includes a branch you cannot see here (closed, or not yours), so it cannot be changed from this screen.',
    'customerSharing.groupMembers': {
        one: '{count} branch: {names}',
        other: '{count} branches: {names}',
    },
    'customerSharing.groupName': 'Group name',
    'customerSharing.groupNameTaken': 'There is already a group called {name}.',
    'customerSharing.groupNeedsName': 'Give the group a name.',
    'customerSharing.groupNeedsTwo': 'A group shares customers between branches, so it needs at least two.',
    'customerSharing.groupQueueLabel': 'Saving the group {name}',
    'customerSharing.groupSave': 'Save group',
    'customerSharing.groupSaved': '{name} is saved.',
    'customerSharing.groupsDenied': 'Only the owner can see how branches are grouped. Ask the owner.',
    'customerSharing.groupsEmpty': 'No branch groups yet. Make the first one below. Groups only matter if you choose to share within groups.',
    'customerSharing.groupsHeading': 'Branch groups',
    'customerSharing.groupsMeans': 'A group is branches that share their customers with each other. Only used when you choose to share within groups.',
    'customerSharing.groupsNotLoaded': 'The groups could not be read, so they cannot be changed right now.',
    'customerSharing.groupsPartial': {
        one: '{count} branch in this group is closed or not yours',
        other: '{count} branches in this group are closed or not yours',
    },
    'customerSharing.groupsReadFailed': 'Could not read the branch groups. Nothing has been changed. Try again.',
    'customerSharing.heading': 'Customers across branches',
    'customerSharing.intro': 'This decides which branches can see a customer, and their prescriptions with them. It applies to every branch at once, and to staff the moment they next open a customer.',
    'customerSharing.keepAsIs': 'Leave it as it is',
    'customerSharing.needGroupFirst': 'Make a group first, below. Sharing within groups with no group would leave every branch seeing no customers.',
    'customerSharing.needGroupSaved': 'Your group is still being saved. You can choose this once it has gone through.',
    'customerSharing.optionAll': 'Every branch sees every customer',
    'customerSharing.optionGroup': 'Branches in the same group share customers',
    'customerSharing.optionOwn': 'Each branch sees only its own customers',
    'customerSharing.queueLabel': 'Changing which branches share customers',
    'customerSharing.saved': 'Done. Branches now share customers this way: {how}.',
    'customerSharing.staleGroup': 'Somebody changed this group since you opened it. It has been reloaded, so look again before saving.',
    'diary.book': 'Book it',
    'diary.bookHeading': 'Book an appointment',
    'diary.bookQueueLabel': 'An appointment for {name} at {time}',
    'diary.bookablesEmpty': 'No appointment types are set up yet, so nothing can be booked. The owner sets them up.',
    'diary.bookablesEmptyOwner': 'No appointment types are set up yet, so nothing can be booked. Each one needs a name, how long it takes, and whether it needs a room or a person.',
    'diary.bookablesFailed': 'What this shop books could not be read, so a booking cannot be started. The day above is unaffected.',
    'diary.bookablesMissingResources': 'the rooms and people, so only a type that needs neither can be booked',
    'diary.bookablesWhat': 'what this shop books and who books it',
    'diary.cancel': 'Cancel it',
    'diary.cancelConfirm': 'Cancel the appointment',
    'diary.cancelReasonLabel': 'Why, if they said',
    'diary.chosenCustomer': 'Booking for {name}.',
    'diary.customerEmpty': 'Type a name or a number to find the customer.',
    'diary.customerFailed': 'The customer search could not be read, so nobody is offered.',
    'diary.customerLabel': 'Who it is for',
    'diary.customerNone': 'Nobody on file matches that. Put them on file under Customers first, then come back and book them in.',
    'diary.customerSearchLabel': 'Find them by name or number',
    'diary.customerUnnamed': "The customer's name is not on this list.",
    'diary.customerWhat': 'customers',
    'diary.dayLabel': 'Day',
    'diary.deniedBranch': 'You are not assigned to this branch, so its diary is not shown. Switch branches, or ask the owner to assign you to this one.',
    'diary.empty': 'Nothing is booked at this branch on {date}.',
    'diary.emptyCanBook': 'Nothing is booked at this branch on {date}. Book the first one below.',
    'diary.filtered': 'Nothing is booked for {who} on {date}. Choose everyone to see the whole day: an appointment with no room or person set is only on that list.',
    'diary.heading': 'Appointments',
    'diary.keep': 'Keep it',
    'diary.loadFailed': 'The day could not be read, so what is booked is not shown. Nothing has been cancelled.',
    'diary.markAttended': 'They came in',
    'diary.markMissed': 'They did not come',
    'diary.missedHint': 'They did not come. Their number is on their record.',
    'diary.missingNames': "the customers' names",
    'diary.movedFrom': 'Moved from an earlier time.',
    'diary.movedTo': 'Moved to another time.',
    'diary.nextDay': 'Day after',
    'diary.noteHeading': 'Note',
    'diary.noteLabel': 'Note, if there is one',
    'diary.noteTooLong': 'The note is too long. Keep it under 2,000 characters.',
    'diary.pendingHold': 'The time is not held until this reaches the server. If somebody takes it first you will be told, and can pick another.',
    'diary.pickATime': 'Pick a time.',
    'diary.previousDay': 'Day before',
    'diary.reschedule': 'Move it',
    'diary.rescheduleHint': 'Pick the new day and time below. The old time is freed and the two are linked, so the day still shows that somebody was told {time}.',
    'diary.rescheduleNoNearby': 'The time it is booked at now is not offered, and neither is the quarter hour either side of it.',
    'diary.resourceNeeded': '{type} needs a {kind}. Choose one before picking a time.',
    'diary.resourcesLabel': 'Room or person',
    'diary.scheduleMissing': 'This branch has no appointment hours or slot length set, so nothing can be booked here yet. The owner sets them.',
    'diary.scheduleMissingOwner': 'This branch has no appointment hours or slot length set, so nothing can be booked here yet.',
    'diary.slotsFailed': 'The free times could not be read, so no time is offered.',
    'diary.slotsLabel': 'Free times on {date}',
    'diary.slotsNone': 'No free time left on {date} for what you chose. The branch may be shut that day, or the room or person may be full, or they may not be in.',
    'diary.slotsWhat': 'the free times',
    'diary.timeTaken': 'Somebody took that time while you were booking. Pick another below: the booking that was refused is thrown away when you do.',
    'diary.today': 'Today',
    'diary.typeDuration': '{minutes} minutes',
    'diary.typeLabel': 'What it is for',
    'diary.typeUnset': 'Choose one',
    'diary.what': "the day's appointments",
    'diary.when': '{from} to {till}',
    'diary.whoEveryone': 'Everyone at this branch',
    'diary.whoLabel': 'Whose day',
    'diary.withNobody': 'No room or person set',
    'queue.subject.appointment': 'The appointment',
    'queue.subject.appointmentClosure': 'The shut day',
    'queue.subject.appointmentResource': 'The room or person',
    'queue.subject.appointmentType': 'The appointment type',
    'queue.subject.clProduct': 'The contact lens',
    'queue.subject.hoursOverride': 'The seasonal hours',
    'queue.subject.recallRule': 'The reminder setting',
    'queue.subject.supplierPrice': 'The price',
    'queue.subject.visitDates': 'The visit dates',
    'recallRules.changedElsewhere': 'Someone changed this setting while you were looking. Reload it and make your change again.',
    'recallRules.contactLens.explain': 'Remind a customer to reorder contact lenses before their supply runs out. How long a pack lasts depends on the kind of lens, so tell us for each.',
    'recallRules.daysPerUnit': 'How many days one pack lasts',
    'recallRules.daysPerUnitHint': 'Fill in only the kinds of lens you sell. A customer whose lens kind is left blank is not reminded.',
    'recallRules.denied': 'Only an owner can set when customers are brought back. Ask the owner to set this up.',
    'recallRules.frameRefresh.explain': 'Remind a customer that it may be time for a new frame, counted from their last frame sale.',
    'recallRules.heading': 'When to bring customers back',
    'recallRules.incomplete': 'To switch this on, fill in {fields}.',
    'recallRules.intervalMonths': 'Remind after (months)',
    'recallRules.intervalMonthsHint': 'Whole months, 1 to 1200.',
    'recallRules.isActive': 'Remind customers about this',
    'recallRules.loadFailed': 'Could not load the reminder settings.',
    'recallRules.maxNudges': 'Ask at most this many times',
    'recallRules.means': 'Pick how long after a visit the shop wants to remind a customer. Nobody is reminded about a kind until you switch it on. The numbers below are yours to set: the software does not guess them.',
    'recallRules.modalityBlank': 'Customers wearing these are not being reminded: {modalities}.',
    'recallRules.notANumber': '{field} must be a whole number from {min} to {max}.',
    'recallRules.openRecalls': 'Go to Bring back',
    'recallRules.queueLabel': 'A reminder setting',
    'recallRules.reorderLeadDays': 'Remind this many days before the supply runs out',
    'recallRules.repeat': 'Remind again if they do not come in',
    'recallRules.repeatEveryDays': 'Ask again every (days)',
    'recallRules.repeatHint': 'Set both or neither, so a customer is never chased without end.',
    'recallRules.runHint': 'After switching a reminder on, ask for the list under Bring back.',
    'recallRules.save': 'Save',
    'recallRules.saved': 'Saved.',
    'recallRules.savedUnchanged': 'Nothing changed, it was already set this way.',
    'recallRules.sightTest.explain': 'Remind a customer that a sight test is due, counted from their last test.',
    'recallRules.statusNotSet': 'Not set up yet',
    'recallRules.statusOff': 'Off',
    'recallRules.statusOn': 'On',
    'recallRules.warrantyExpiry.explain': 'Warranty-ending reminders are not working yet. The shop does not record when a warranty ends, so these rules can be saved and nobody will be reminded.',
    'recallRules.what': 'reminder settings',
    'refusal.appointmentAlreadyStarted': 'That appointment has already started, so it cannot be cancelled or moved. Record whether they came instead.',
    'refusal.appointmentClosed': 'That appointment is already closed, so it cannot be changed again.',
    'refusal.appointmentInThePast': 'That time has already passed at this branch. Pick another one.',
    'refusal.appointmentNotStarted': 'That appointment has not started yet, so it cannot be recorded as missed.',
    'refusal.appointmentScheduleMissing': 'This branch has no appointment hours set, so nothing can be booked here.',
    'refusal.appointmentTypeArchived': 'That kind of appointment is no longer offered. Pick another one.',
    'refusal.resourceArchived': 'That room or person is no longer in use. Pick another one.',
    'refusal.resourceDoubleBooked': 'Somebody took that time first. Pick another one.',
    'refusal.resourceNotVisiting': 'That optometrist is not in on the day you chose.',
    'resourceKind.staff': 'Staff member',
    'resourceKind.testingRoom': 'Testing room',
    'resourceKind.visitingOptometrist': 'Visiting optometrist',
    'supplierPrices.add': 'Add this price',
    'supplierPrices.addOne': 'Add a price',
    'supplierPrices.alreadyListed': '{sku} already has a price starting {date} on this list. Change that one, or start this one on a different day.',
    // Three the build asked for after the copy was written, which is the ordinary way of it: the
    // screen met a shape the survey had not. `supplierPrices.zeroConfirm` follows the figure, so the
    // amount is rendered separately inside `<Ltr>` and this is the sentence after it.
    'supplierPrices.zeroConfirm': 'is what you typed. If one really costs that, a free sample for instance, press Add this price again. Otherwise change the cost.',
    'supplierPrices.leadTimeShape': 'Days to deliver is a whole number of days, from 0 to {max}.',
    'supplierPrices.showingFirst': 'Showing the first {shown} of {total}. Search to narrow the list.',
    'supplierPrices.back': 'Back to your suppliers',
    'supplierPrices.cancel': 'Leave it as it is',
    'supplierPrices.change': 'Change the price of {sku}',
    'supplierPrices.colCode': 'Item code',
    'supplierPrices.colCost': 'Cost',
    'supplierPrices.colDates': 'Dates',
    'supplierPrices.colLead': 'Days to deliver',
    'supplierPrices.colWhat': 'What it is',
    'supplierPrices.cost': 'What one costs you, in {currency}',
    'supplierPrices.costRequired': 'Type what one costs you.',
    'supplierPrices.costShape': 'The cost is a number like 1500 or 1500.50, with no more decimal places than {currency} has.',
    'supplierPrices.costTooLarge': 'That cost is too large to record.',
    'supplierPrices.currencyHint': 'Prices are kept in {currency}, the currency {name} bills in.',
    'supplierPrices.dates': 'From {from}',
    'supplierPrices.datesTo': 'From {from} to {to}',
    'supplierPrices.denied': "A supplier's price is a cost price, and you have not been given cost visibility at every branch you work in. Ask the owner to give it to you under Setup.",
    'supplierPrices.description': 'What it is',
    'supplierPrices.descriptionRequired': 'Say what the item is.',
    'supplierPrices.endIt': 'End this price',
    'supplierPrices.ended': 'ended',
    'supplierPrices.filtered': "No price on this list matches that. Clear the search, or untick 'Only prices in force today'.",
    'supplierPrices.heading': 'Prices from {name}',
    'supplierPrices.inForce': 'in force today',
    'supplierPrices.leadTime': 'Days to deliver',
    'supplierPrices.loadFailed': "Could not read this supplier's prices.",
    'supplierPrices.markAvailable': 'Mark {sku} as available again',
    'supplierPrices.markUnavailable': 'Mark {sku} as not available',
    'supplierPrices.newDayHint': 'A new price for the same item, starting on a later day, takes over from that day. You do not need to end the old one.',
    'supplierPrices.none': 'No price is on this list yet. Add the first one with Add a price, below.',
    'supplierPrices.noneReadOnly': 'No price is on this list yet. Ask the owner, a manager or the accountant to add one.',
    'supplierPrices.notAvailable': 'not available from them',
    'supplierPrices.notStarted': 'starts {date}',
    'supplierPrices.nothingChanged': 'Nothing has changed yet.',
    'supplierPrices.onlyCurrent': 'Only prices in force today',
    'supplierPrices.queueAddLabel': 'Adding a price for {sku}',
    'supplierPrices.queueAvailableLabel': 'Marking {sku} as available again',
    'supplierPrices.queueChangeLabel': 'A change to the price of {sku}',
    'supplierPrices.queueEndLabel': 'Ending the price of {sku}',
    'supplierPrices.queueUnavailableLabel': 'Marking {sku} as not available',
    'supplierPrices.rangeInvalid': 'A price cannot end before the day it starts.',
    'supplierPrices.save': 'Save the change',
    'supplierPrices.search': 'Find an item code or description',
    'supplierPrices.sku': 'Their item code',
    'supplierPrices.skuRequired': 'Type their item code.',
    'supplierPrices.validFrom': 'Price starts on',
    'supplierPrices.validFromRequired': 'Say which day the price starts.',
    'supplierPrices.validTo': 'Price ends on (leave empty if it has no end)',
    'supplierPrices.what': "this supplier's prices",
    'visitDates.add': 'Add the day',
    'visitDates.addLabel': 'A day they are in',
    'visitDates.already': '{date} is already in the list.',
    'visitDates.back': 'Back to the rooms and people',
    'visitDates.empty': 'No days yet, so {name} cannot be booked on any day. Pick a day below and add it.',
    'visitDates.heading': 'Days {name} is in',
    'visitDates.loadFailed': 'Could not read the days for {name}.',
    'visitDates.means': 'A visiting optometrist can be booked only on these days. With no days listed, they cannot be booked at all.',
    'visitDates.pickFirst': 'Pick a day first.',
    'visitDates.queueSave': 'Saving the days for {name}',
    'visitDates.remove': 'Take off {date}',
    'visitDates.removeWarning': 'Saving replaces the whole list. If an appointment is already booked with {name} on a day you take off, the save is refused and nothing is changed, so move or cancel those appointments first.',
    'visitDates.save': 'Save the days',
    'visitDates.tooMany': 'That is more than two years of days. Take some off before saving.',
    'visitDates.unsaved': 'Not saved yet. Nothing changes until you save.',
    // The eighth appointment screen and the recall run trigger. `appointment_schedule` is the
    // branch base weekly hours, which `isWorkingDay` reads before any closure or season, so
    // without it `loadBranchSchedule` refuses every booking on every branch: the other seven
    // appointment screens edit exceptions to hours that did not exist. And nothing in the counter
    // called `POST /recalls/runs`, so a shop could set recall rules and the queue stayed empty.
    'apptHours.advance': 'Days ahead this branch will book',
    'apptHours.advanceMeans': 'How far into the future a customer booking themselves in may go.',
    'apptHours.buffer': 'Minutes kept free after each appointment',
    'apptHours.bufferMeans': 'The gap for clearing the room or writing up the test. Nothing else can be booked on the same room or person inside it.',
    'apptHours.changedElsewhere': 'Somebody else changed these hours while this screen was open. Clear the change that was refused, then make yours on top of theirs.',
    'apptHours.closesAt': 'Closes at',
    'apptHours.closesMidnight': 'For a branch open until the end of the day, set the closing time to 23:59.',
    'apptHours.daysHeading': 'Days this branch opens',
    'apptHours.daysMeans': 'Tick every day this branch takes appointments. Nothing is ticked to start with, because the days a shop closes are not the same two days everywhere.',
    'apptHours.daysOrder': 'The week is listed the way these records number it, from Sunday. That is a numbering, not a claim about which day your week starts on.',
    'apptHours.denied': "Only the owner can set a branch's opening hours and slot rules. Ask them to set this branch up.",
    'apptHours.heading': 'Appointment hours',
    'apptHours.hoursHeading': 'Opening hours',
    'apptHours.hoursMeans': 'The first appointment of the day can start at the opening time, and the last one has to finish by the closing time.',
    'apptHours.loadFailed': 'Could not read the appointment hours for {branch}.',
    'apptHours.means': 'The hours {branch} takes appointments in, and how its slots are worked out. Every booking, every free slot the diary offers and every day off is measured against this.',
    'apptHours.midnightMeans': 'A branch open until the end of the day is held as midnight, which this box cannot show: it reads 23:59 and stays at midnight until you change the closing time.',
    'apptHours.missing': '{fields} still need a number. Nothing here has a default, so each one has to be chosen.',
    'apptHours.noDayTicked': 'Tick at least one day. A branch that opens on no day cannot be saved, because nothing could ever be booked at it.',
    'apptHours.notANumber': '{field} must be a whole number from {min} to {max}.',
    'apptHours.notSetYet': 'Nothing is set for {branch} yet, so nothing can be booked there. None of it is filled in for you: a shop chooses its own days and hours.',
    'apptHours.notice': 'Shortest notice a booking may give, in minutes',
    'apptHours.noticeMeans': 'How little warning a customer booking themselves in may give.',
    'apptHours.opensAt': 'Opens at',
    'apptHours.policyInert': 'Those last two are read by booking from the shop website, which comes in a later release. Neither one stops an appointment taken at the counter today, however it is set.',
    'apptHours.queueLabel': 'Appointment hours for {branch}',
    'apptHours.rulesHeading': 'Slot rules',
    'apptHours.sameEveryDay': 'These hours apply to every day ticked above. One day trading shorter hours than the rest cannot be set here yet; a dated stretch of different hours can, under seasonal hours.',
    'apptHours.save': 'Save these hours',
    'apptHours.saved': 'The appointment hours are saved.',
    'apptHours.slotStep': 'Minutes between start times',
    'apptHours.slotStepMeans': 'Start times are counted from the opening time, so 15 offers 9:00, 9:15 and 9:30 at a branch that opens at 9.',
    'apptHours.stillWaiting': 'The last change to these hours has not reached the server yet. It will go on its own, and the fields come back as soon as it has.',
    'apptHours.timesBackwards': 'The closing time has to be after the opening time.',
    'apptHours.timesRequired': 'An opening time and a closing time are both needed.',
    'apptHours.weekday.friday': 'Friday',
    'apptHours.weekday.monday': 'Monday',
    'apptHours.weekday.saturday': 'Saturday',
    'apptHours.weekday.sunday': 'Sunday',
    'apptHours.weekday.thursday': 'Thursday',
    'apptHours.weekday.tuesday': 'Tuesday',
    'apptHours.weekday.wednesday': 'Wednesday',
    'apptHours.what': 'the appointment hours',
    'queue.subject.appointmentHours': 'The appointment hours',
    'recalls.queueRunLabel': 'A search for who is due',
    'recalls.run': 'Find who is due',
    'recalls.runNone': 'Nobody is due today. Reminders skipped: {skipped}. If you expected someone, check the reminder settings are switched on.',
    'recalls.runResult': 'Found: {found}. Already on the list: {alreadyQueued}. Reminders skipped: {skipped}.',
    // Four v1 slices the detector found: a table or column the product read, or a requirement
    // the schema carried, with nothing anywhere that could write it. `BR-11` expenses,
    // `RMK-008` remake lab credit, `POS-008a` and `CAT-015` tenant policy numbers, and
    // `IMP-004`"s second half, paying a supplier invoice.
    'expenses.allocation.by_revenue': 'Shared by all branches, in proportion to their sales',
    'expenses.allocation.direct': 'This branch only',
    'expenses.allocation.equal': 'Shared equally by all branches',
    'expenses.allocationLabel': 'Whose cost it is',
    'expenses.amountLabel': 'Amount',
    'expenses.amountRequired': 'Enter an amount above zero, such as 1500 or 1500.50.',
    'expenses.cashMeans': 'This also comes out of the cash the drawer should hold when it is counted.',
    'expenses.category.bank_charges': 'Bank charges',
    'expenses.category.equipment': 'Equipment',
    'expenses.category.maintenance': 'Maintenance and repairs',
    'expenses.category.marketing': 'Marketing',
    'expenses.category.other': 'Other',
    'expenses.category.professional_fees': 'Professional fees',
    'expenses.category.rent': 'Rent',
    'expenses.category.salaries': 'Salaries',
    'expenses.category.transport': 'Transport and courier',
    'expenses.category.utilities': 'Utilities',
    'expenses.categoryLabel': 'What it was for',
    'expenses.dateInvalid': 'Write the date as year-month-day, for example 2026-09-30.',
    'expenses.dateLabel': 'Date it was spent (leave blank for today)',
    'expenses.empty': 'No expenses recorded yet.',
    'expenses.listHeading': 'Recorded expenses',
    'expenses.noDrawer': 'The cash drawer is not open at this till. Open it from the cash drawer screen, or choose how it was really paid.',
    'expenses.noteLabel': 'Note (optional)',
    'expenses.noteTooLong': 'Keep the note under {max} characters.',
    'expenses.paidFrom.accounts_payable': 'Owed on account, to be paid later',
    'expenses.paidFrom.bank': 'The bank',
    'expenses.paidFrom.cash': 'The cash drawer',
    'expenses.paidFromLabel': 'Paid from',
    'expenses.queueLabel': 'Expense, {category}',
    'expenses.recordHeading': 'Record an expense',
    'expenses.shared': 'Shared',
    'expenses.submit': 'Record expense',
    'jobDetail.remakeLife.credit': 'Supplier credit received',
    'jobDetail.remakeLife.creditAt': 'Supplier credit received on',
    'jobDetail.remakeLife.creditWriteLabel': 'Supplier credit received for the remake of job {jobNumber}',
    'jobDetail.remakeLife.heading': 'Remakes on this job',
    'jobDetail.remakeLife.resolve': 'Mark resolved',
    'jobDetail.remakeLife.resolveWriteLabel': 'Remake of job {jobNumber} resolved',
    'jobDetail.remakeLife.resolvedAt': 'Resolved on',
    'jobDetail.remakeLife.start': 'The lab has started it',
    'jobDetail.remakeLife.startWriteLabel': 'The lab has started the remake of job {jobNumber}',
    'policies.allowance': 'Edging allowance in tenths of a millimetre',
    'policies.allowanceInvalid': 'The edging allowance must be a whole number from 0 to {max}.',
    'policies.allowanceMeans': 'How much extra glass your edger needs beyond the frame shape. 20 means 2.0 mm. Empty means lenses are not checked for cut-out.',
    'policies.changedElsewhere': 'Someone else changed the policies first. The screen has read them again. Check your numbers and save again.',
    'policies.cutoutHeading': 'Cut-out check',
    'policies.denied': 'Only the owner can change the shop policies.',
    'policies.discountHeading': 'Discount limit',
    'policies.discountMeans': 'The most discount each role can give, as a percentage. A role over its limit has to fetch the owner or a manager. Empty means no limit.',
    'policies.heading': 'Policies',
    'policies.limitHint': 'A percentage from 0 to 100, for example 10 or 7.5.',
    'policies.limitInvalid': 'The discount limit for {role} must be a percentage from 0 to 100, with at most two decimals.',
    'policies.loadFailed': 'The shop policies could not be read. Try again.',
    'policies.means': 'These are the numbers your shop works to. Leave a box empty and the rule is not applied. A 0 is a number you chose, not an empty box.',
    'policies.queueLabel': 'Shop policies',
    'policies.returnsHeading': 'Returns',
    'policies.role.accountant': 'Accountant',
    'policies.role.cashier': 'Cashier',
    'policies.role.dispenser': 'Dispenser',
    'policies.role.manager': 'Manager',
    'policies.role.optician': 'Optician',
    'policies.role.owner': 'Owner',
    'policies.role.read_only': 'Read-only account',
    'policies.save': 'Save policies',
    'policies.saved': 'Policies saved.',
    'policies.stillWaiting': 'An earlier change to the policies is still waiting to be sent. Wait for it before saving again.',
    'policies.what': 'The shop policies',
    'policies.window': 'Return window in days',
    'policies.windowInvalid': 'The return window must be a whole number of days from 0 to {max}.',
    'policies.windowMeans': 'How many days after the sale a stock item can be returned. Empty means no window is set, so every stock return needs a goodwill decision. 0 means the same day only.',
    'queue.subject.expense': 'The expense',
    'queue.subject.policies': 'The shop policies',
    'supplierInvoice.paidAndLeft': 'Paid {paid}, still to pay {left}',
    'supplierInvoice.queueSettleLabel': 'A payment against invoice {number}',
    'supplierInvoice.settle': 'Record the payment',
    'supplierInvoice.settleAmountInvalid': 'Amount paid is an amount, for example 1250.00.',
    'supplierInvoice.settleAmountLabel': 'Amount paid ({currency})',
    'supplierInvoice.settleAmountPositive': 'A payment is more than nothing.',
    'supplierInvoice.settleBaseInvalid': 'What left the bank is an amount, for example 56600.00.',
    'supplierInvoice.settleBaseLabel': 'What left the bank, in total ({currency})',
    'supplierInvoice.settleBasePositive': 'What left the bank is more than nothing.',
    'supplierInvoice.settleDifferenceGoesSeparately': 'Any difference between this and what the invoice was booked at is recorded on its own, never added to what the goods cost.',
    'supplierInvoice.settleFullyPaid': 'Paid in full',
    'supplierInvoice.settleHeading': 'What the supplier was paid',
    'supplierInvoice.settleOpen': 'Record a payment',
    'supplierInvoice.settlePaidFromBank': 'The bank',
    'supplierInvoice.settlePaidFromCash': 'The till',
    'supplierInvoice.settlePaidFromLabel': 'Paid from',
    'supplierInvoice.settleRateInvalid': 'The rate is a decimal with up to eight places, for example 283.00.',
    'supplierInvoice.settleRateLabel': 'Exchange rate on the day you paid',
    'supplierInvoice.settleRateOnLabel': 'Rate date',
    'supplierInvoice.settleRateRequired': 'An invoice in another currency needs what left the bank, the rate you used and the date of that rate.',
    'supplierInvoice.settleTooMuch': 'That is more than is left to pay on this invoice, which is {amount}.',
    'supplierInvoice.settleWholeInvoice': 'This pays the invoice in full, {amount}. An invoice in another currency is paid in one payment.',
    'supplierInvoice.settledOnLabel': 'Date the supplier was paid',
    'supplierInvoice.settledOnRequired': 'Say the date the supplier was paid.',
    // `PanelStatements`, section 6.13 `CLM-001` to `CLM-006`, section 15.20 step 4. The monthly
    // statement a panel is sent. `V1-SPEC.md` section 6.13 is capture only, so these are the words
    // around a document the shop prints or reads down the phone, never around sending one.
    //
    // `colDenied` is "Refused" rather than "Denied": a payer refusing a line is what a shopkeeper
    // says, and `claimStatus.denied` matches it so the column and the cell under it read the same.
    'panelStatements.cancel': 'Cancel',
    'panelStatements.chooseAPanel': 'Choose a panel to see what it has been sent and which months are left to issue.',
    'panelStatements.colCaptured': 'Captured',
    'panelStatements.colDenied': 'Refused',
    'panelStatements.colOrder': 'Order',
    'panelStatements.colOutstanding': 'Outstanding',
    'panelStatements.colPaid': 'Paid',
    'panelStatements.colPanelShare': 'Panel share',
    'panelStatements.colStatus': 'Status',
    'panelStatements.colTransferred': 'Moved to the customer',
    'panelStatements.colWrittenOff': 'Written off',
    'panelStatements.confirmButton': 'Yes, issue it',
    'panelStatements.confirmIssue': 'This issues {panel} the statement for {month}. An issued statement cannot be edited, only replaced by a corrected revision.',
    'panelStatements.confirmReissue': 'This replaces revision {revision} of {panel}\u2019s {month} statement with a corrected one. The copy the panel is holding stays readable.',
    'panelStatements.heading': 'Panel statements',
    'panelStatements.hideLines': 'Hide the orders',
    'panelStatements.issue': 'Issue it',
    'panelStatements.issueHeading': 'Issue a statement',
    'panelStatements.issuedHeading': 'Already issued',
    'panelStatements.issuedOn': 'Issued {date}',
    'panelStatements.linesCaption': 'Every order on this statement, as it stood on the day it was issued.',
    'panelStatements.loadFailed': 'Could not load this panel\u2019s statements.',
    'panelStatements.monthLabel': 'Month',
    'panelStatements.monthSummary': {
        one: '{count} claim was captured in this month.',
        other: '{count} claims were captured in this month.',
    },
    'panelStatements.monthUnchosen': 'Choose a month',
    'panelStatements.noneIssuedInvite': 'Nothing has been issued to this panel yet. Issue a month below and the copy appears here.',
    'panelStatements.nothingToIssue': 'This panel has no claim in any month, so there is nothing to send them.',
    'panelStatements.panelDeactivated': '{panel} (no longer in use)',
    'panelStatements.panelLabel': 'Panel',
    'panelStatements.panelUnchosen': 'Choose a panel',
    'panelStatements.panelsFailed': 'Could not load the panels. Try again shortly.',
    'panelStatements.period': '{from} to {to}',
    'panelStatements.queueLabel': 'Statement for {month}',
    'panelStatements.reissue': 'Issue a corrected revision',
    'panelStatements.revision': 'Revision {revision}',
    'panelStatements.showLines': {
        one: 'Show the {count} order on it',
        other: 'Show the {count} orders on it',
    },
    'panelStatements.stillTrading': 'This month is not over, so these figures can still change.',
    'panelStatements.superseded': 'Replaced by a later revision',
    'panelStatements.totalsRow': 'Total',
    'panelStatements.what': 'the statements this panel has been issued',
    'panelStatements.whatItIs': 'The monthly statement a panel is sent, listing every order it covers. Print it or read the figures to their accounts clerk. Lensmira does not send it for you.',
    'panelStatements.withheld': {
        one: '{count} statement covers a branch you are not assigned to, so it is not shown.',
        other: '{count} statements cover branches you are not assigned to, so they are not shown.',
    },
    'panelStatements.withheldHeading': 'Months that cannot be issued from here',
    'queue.subject.panelStatement': 'The statement',
    // The one statement refusal with no parameter in it, so the one a translator can reach.
    // The other seven name a month, a revision, a period or a branch count: `refusals.ts` keeps
    // those on the server until the payload carries a `details` object (`../later_todo.md` 48).
    'refusal.statementDoesNotReconcile': 'The claims in this month do not add up, so no statement was issued. A claim\u2019s panel share has to equal what is paid, outstanding, refused, moved to the customer and written off.',
    // `CLM-004`'s eight claim states as words, for the statement table. Not the enum: a payer is
    // shown this table, and `written_off` is not a word anybody writes. `PanelStatements.tsx`
    // `CLAIM_STATUS` holds the map and `CLAIM_STATUS_KEYS` ties it to the domain registry.
    'claimStatus.captured': 'Captured',
    'claimStatus.denied': 'Refused',
    'claimStatus.paid': 'Paid',
    'claimStatus.partiallyPaid': 'Part paid',
    'claimStatus.reversed': 'Payment reversed',
    'claimStatus.submitted': 'Sent to the panel',
    'claimStatus.transferred': 'Moved to the customer',
    'claimStatus.writtenOff': 'Written off',
    // §6.13 `CLM-001` to `CLM-003`: setting a panel up. `PanelAccounts.tsx` on the `setup`
    // destination. The rate a plan pays is withheld rather than zeroed for a caller without
    // the cost grant, so there is a word for that and not a blank.
    'panelAccounts.addMember': 'Add this member',
    'panelAccounts.addPanel': 'Add a panel',
    'panelAccounts.addPlan': 'Add this plan',
    'panelAccounts.addRule': 'Add this category',
    'panelAccounts.amountLabel': 'Amount',
    'panelAccounts.authLabel': 'Authorisation needed first',
    'panelAccounts.badAmount': 'A figure, digits only.',
    'panelAccounts.badCurrency': 'Three capital letters, such as PKR.',
    'panelAccounts.badDate': 'A date as YYYY-MM-DD.',
    'panelAccounts.categoryLabel': 'Category',
    'panelAccounts.checkedOnLabel': 'Checked on',
    'panelAccounts.coverageLine': '{category}: {rule}',
    'panelAccounts.currencyLabel': 'Currency',
    'panelAccounts.customerLabel': 'Customer',
    'panelAccounts.duplicate': 'That name is already in use here.',
    'panelAccounts.heading': 'Panel accounts',
    'panelAccounts.kindCorporate': 'Company panel',
    'panelAccounts.kindInsurer': 'Insurer',
    'panelAccounts.kindLabel': 'Kind',
    'panelAccounts.membersHeading': 'Members',
    'panelAccounts.membersHint': 'Re-check only in the sitting a member was added: nothing lists them.',
    'panelAccounts.nameLabel': 'Panel name',
    'panelAccounts.needed': 'Fill this in first.',
    'panelAccounts.no': 'No',
    'panelAccounts.noPlans': 'No plan on this panel yet.',
    'panelAccounts.noneYet': 'No panel on file yet. Add the first below.',
    'panelAccounts.panelLine': '{name}, paid in {currency}',
    'panelAccounts.percentLabel': 'Percent',
    'panelAccounts.planLabel': 'Plan',
    'panelAccounts.planLine': '{name}, {policy}',
    'panelAccounts.planNameLabel': 'Plan name',
    'panelAccounts.plansHeading': 'Plans at {name}',
    'panelAccounts.policyLabel': 'Who may be served',
    'panelAccounts.policyUnverified': 'Unverified members too',
    'panelAccounts.policyVerified': 'Verified members only',
    'panelAccounts.queueLabel': 'Panel account, {name}',
    'panelAccounts.readFailed': 'The panels could not be read.',
    'panelAccounts.recheck': 'Record a new check',
    'panelAccounts.recheckStatusLabel': 'New eligibility',
    'panelAccounts.roundDown': 'Down',
    'panelAccounts.roundUp': 'Up',
    'panelAccounts.roundingLabel': 'Rounding',
    'panelAccounts.ruleAllowance': 'Up to',
    'panelAccounts.ruleCopay': 'All but',
    'panelAccounts.ruleExcluded': 'Nothing',
    'panelAccounts.ruleLabel': 'The panel pays',
    'panelAccounts.rulePercent': 'Percentage',
    'panelAccounts.ruleSchedule': 'Fixed price',
    'panelAccounts.ruleWithheld': 'Rate not shown to you',
    'panelAccounts.statusIneligible': 'Not eligible',
    'panelAccounts.statusLabel': 'Eligibility',
    'panelAccounts.statusUnverified': 'Not checked',
    'panelAccounts.statusVerified': 'Verified',
    'panelAccounts.yes': 'Yes',
    // §6.13 `CLM-002`, `CLM-003` and §15.20 steps 2 and 3: the panel share on a sale.
    // `receivableNotPayment` is the sentence that stops a cashier reading a capture as a
    // payment taken: §15.20 step 3 is explicit that a panel share is a receivable.
    'claimCapture.action.reduce': 'Reduce the claim',
    'claimCapture.action.resubmit': 'Bill it again',
    'claimCapture.action.submit': 'Send to the panel',
    'claimCapture.action.transferToCustomer': 'Move the refusal to the customer',
    'claimCapture.action.writeOff': 'Write it off',
    'claimCapture.amountLabel': 'Amount',
    'claimCapture.amountLabelOptional': 'Amount, blank for all of it',
    'claimCapture.amountMost': 'At most {amount}',
    'claimCapture.amountNotANumber': 'Enter the amount in figures.',
    'claimCapture.amountOutOfRange': 'That is more than this claim allows.',
    'claimCapture.amountRequired': 'Enter an amount.',
    'claimCapture.authorisationNeeded': 'This plan needs an authorisation first.',
    'claimCapture.cancel': 'Cancel',
    'claimCapture.capture': 'Capture the panel share',
    'claimCapture.captureButton': 'Yes, capture it',
    'claimCapture.capturedOn': 'Captured on',
    'claimCapture.colCustomer': 'Customer pays',
    'claimCapture.colLine': 'Item',
    'claimCapture.colPanel': 'Panel pays',
    'claimCapture.colPrice': 'Price',
    'claimCapture.confirmCapture': 'Capture {panel} {plan} against this sale?',
    'claimCapture.customerOwes': 'Customer owes',
    'claimCapture.denied': 'Refused',
    'claimCapture.grantedOnLabel': 'Granted on',
    'claimCapture.heading': 'Panel share',
    'claimCapture.loadFailed': 'The panel share could not be loaded.',
    'claimCapture.loading': 'Loading the panel share',
    'claimCapture.noPlans': 'This customer is on no plan that can take this sale.',
    'claimCapture.noneYet': 'Nothing has been captured on this sale.',
    'claimCapture.notOffered': 'This sale cannot carry a panel claim.',
    'claimCapture.nothingToDo': 'Nothing is left to do on this claim.',
    'claimCapture.paid': 'Panel has paid',
    'claimCapture.panelOwes': 'Panel owes',
    'claimCapture.planLabel': 'Plan',
    'claimCapture.planOption': '{panel} {plan}',
    'claimCapture.planUnchosen': 'Choose a plan',
    'claimCapture.queue.reduce': 'Claim reduced',
    'claimCapture.queue.resubmit': 'Claim billed again',
    'claimCapture.queue.submit': 'Claim sent to the panel',
    'claimCapture.queue.transferToCustomer': 'Refusal moved to the customer',
    'claimCapture.queue.writeOff': 'Claim written off',
    'claimCapture.queueLabel': 'Panel share for {panel}',
    'claimCapture.reasonLabel': 'Reason',
    'claimCapture.reasonRequired': 'Write the reason.',
    'claimCapture.receivableNotPayment': 'This records what the panel owes. No money is taken here and the customer still owes their part.',
    'claimCapture.referenceLabel': 'Authorisation reference',
    'claimCapture.retry': 'Try again',
    'claimCapture.send': 'Record it',
    'claimCapture.splitCaption': 'What each side pays, line by line',
    'claimCapture.status': 'Claim',
    'claimCapture.transferred': 'Moved to the customer',
    'claimCapture.withheldHeading': 'Plans that cannot take this sale',
    'claimCapture.writeOffMeans': 'A write-off gives up this money. It stays on the record in your name.',
    'claimCapture.writtenOff': 'Written off',
    // §6.13 `CLM-004`: the cheque a panel sends, against its open claims, oldest first.
    // `openTotal` and `notYetSubmitted` are plural pairs because they carry `{count}` and
    // `translate` has no ICU: a bare string reads "1 claims" for one.
    'panelRemittance.agedFailed': 'Could not load the open claims.',
    'panelRemittance.agedHeading': 'What this panel owes',
    'panelRemittance.amountLabel': 'Amount received ({currency})',
    'panelRemittance.amountNotAmount': 'Write the amount in figures, not in words.',
    'panelRemittance.amountNotPositive': 'A payment is more than zero.',
    'panelRemittance.cancel': 'Cancel',
    'panelRemittance.chooseAPanel': 'Choose a panel to see what it owes and record what it has paid.',
    'panelRemittance.confirm': 'This records {amount} received from {panel}. A payment is reversed rather than edited, so check the figure before recording it.',
    'panelRemittance.confirmButton': 'Yes, record it',
    'panelRemittance.dateNeeded': 'Give the day the money arrived.',
    'panelRemittance.heading': 'Panel remittance',
    'panelRemittance.moreThanOpen': '{amount} of this is more than the panel owes. The remainder stays unapplied, against no claim.',
    'panelRemittance.nothingOpen': 'This panel owes nothing. A payment recorded now would sit unapplied, against no claim.',
    'panelRemittance.oldestFirst': 'The money goes onto the oldest open claim first, then the next, until it runs out.',
    'panelRemittance.panelDeactivated': '{panel} (no longer in use)',
    'panelRemittance.panelLabel': 'Panel',
    'panelRemittance.panelUnchosen': 'Choose a panel',
    'panelRemittance.panelsFailed': 'Could not load the panels. Try again shortly.',
    'panelRemittance.queueLabel': 'Payment from {panel}',
    'panelRemittance.receivedOnLabel': 'Received on',
    'panelRemittance.record': 'Record it',
    'panelRemittance.recordHeading': 'Record a payment',
    'panelRemittance.referenceHint': 'The cheque or transfer number, so the shop and the panel can name the same payment.',
    'panelRemittance.referenceLabel': 'Reference (optional)',
    'panelRemittance.what': 'the open claims against this panel',
    'panelRemittance.whatItIs': 'Money a panel has paid, recorded against its open claims. The oldest claim is settled first.',
    'panelRemittance.notYetSubmitted': {
        one: 'One claim has not been sent to its panel yet, so it has no age.',
        other: '{count} claims have not been sent to their panel yet, so they have no age.',
    },
    'panelRemittance.openTotal': {
        one: '{panel} owes {amount} on one open claim.',
        other: '{panel} owes {amount} across {count} open claims.',
    },
    'queue.subject.claim': 'The panel share',
    'queue.subject.panelAccount': 'The panel',
    'queue.subject.panelRemittance': 'The payment',
    // The claims refusals a shopkeeper can act on, and the whole set that needs no
    // parameter. Every throw site was read: all of them state the refusal with nothing
    // interpolated, so the catalogue loses none of what the server knew. The ones naming a
    // branch, a role or a record stay on the server, for the reason `refusals.ts` gives.
    'refusal.claimExists': 'This sale already has a panel claim on it. Open the claim rather than capturing a second one.',
    'refusal.eventNotDirect': 'A payment from a panel is recorded against the panel, not against one claim, so the two cannot disagree about what was paid.',
    'refusal.notAMember': 'This customer is not on that plan, so the panel will not pay for them.',
    'refusal.notEligible': 'This plan serves verified members only, and this member has not been verified. Record an eligibility check first.',
    'refusal.orderHasNoCustomer': 'This sale has no customer on it, and a panel pays for a named member. Add the customer first.',
    'refusal.panelNotFound': 'No such panel.',
    'refusal.paymentAlreadyReversed': 'That payment has already been reversed.',
    'refusal.paymentNotFound': 'No such payment.',
    'refusal.paymentNotThisPanel': 'That payment belongs to a different panel.',
    'refusal.planDeactivated': 'That plan is no longer in use, so nothing can be claimed against it.',
    'refusal.planRequired': 'Say how the money is to be spread before recording it. Nothing is assumed about which claims a payment settles.',
    'refusal.reasonRequired': 'Write the reason. It stays on the record.',
    'refusal.statementNotFound': 'No such statement.',
    'refusal.reversalNotReversible': 'That payment cannot be reversed.',
};
//# sourceMappingURL=en.js.map